import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Container,
  Chip,
  Avatar,
  Grid,
  Paper,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Skeleton,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  SportsScore as ScoreIcon,
  Event as EventIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Celebration as CelebrationIcon,
  Close as CloseIcon,
  Restaurant as EatIcon,
  Hotel as SleepIcon,
  SportsHandball as PadelIcon,
  Refresh as RepeatIcon,
  ExpandMore as ExpandMoreIcon,
  RestartAlt as RestartAltIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  CompareArrows as CompareIcon,
  Person as PersonIcon,
  MilitaryTech as MastersIcon,
  InfoOutlined as InfoIcon,
  MenuBook as RulesBookIcon,
  Phone as PhoneIcon,
  Campaign as CampaignIcon,
  Checklist as ResultsIcon,
  EditNote as ScoreMatchesIcon,
  People as PlayersIcon,
  Bolt as MatchesBoltIcon,
} from "@mui/icons-material";
import {
  PlayerStats,
  LeagueDay,
  Match,
  Division,
  PlayerHistory,
  PartnershipStats,
  OpponentStats,
  DivisionComparison,
  DivisionRecords,
  RecordItem,
  PopupStyle,
  getTournamentLevels,
  getLevelRangeLabel,
} from "../types";
import { apiRequest } from "../config/api";
import {
  getPromotionTarget,
  getEffectivePromotionSpots,
  getPromotionSpots,
  getRelegationSpots,
} from "../utils/seasonTransition";
import { PopupHeader, openPopupLink } from "../utils/popupStyles";
import { PE } from "../utils/padelEagleBrand";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { trackEvent } from "../utils/analytics";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";
import SafeAvatar from "../components/SafeAvatar";
import ScorekeeperRegistrationDialog from "../components/ScorekeeperRegistrationDialog";
import HeadToHeadDialog from "../components/HeadToHeadDialog";
import BadgeShowcase from "../components/BadgeShowcase";
import CountdownTimer from "../components/CountdownTimer";
import OfflineIndicator from "../components/OfflineIndicator";
import SeasonRegistrationBanner from "../components/SeasonRegistrationBanner";
import FormattedTextBlock from "../components/FormattedTextBlock";
import VenueBadge from "../components/VenueBadge";
import splLogo from "../img/SPL_Logo.png";
import padelEagleLogo from "../img/padelEagle.png";
import spcLogo from "../img/SPC.png";
import nationsBanner from "../img/nationsBanner.png";
import NationsLeagueFaqDialog from "../components/NationsLeagueFaqDialog";
import { useOrganization } from "../contexts/OrganizationContext";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  useTheme as useMuiTheme,
} from "@mui/material/styles";

// SPT logo — replace splLogo with the real SPT asset when available
const sptLogo = splLogo;

// Home page uses Montserrat across the board instead of the app-wide Inter/Poppins theme fonts.
const MONTSERRAT = "'Montserrat', sans-serif";
// Padel Eagle uses its CI fonts (Varela Round headings / Bai Jamjuree body) from the global theme.
const PE_BODY_FONT = PE.bodyFont;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function PublicHome() {
  const { currentDivision, setPlayerDivision } = useDivision();
  const { selectedSeason, allSeasons, isLoading: seasonLoading } = useSeason();
  const navigate = useNavigate();
  const { isScorekeeper, isAdmin, playerUser, playerToken } = useAuth();
  const { currentOrg } = useOrganization();
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const montserratTheme = useMemo(
    () =>
      currentOrg?.slug === "padel-eagle" ? muiTheme : createTheme(muiTheme, {
        typography: {
          fontFamily: MONTSERRAT,
          h1: { fontFamily: MONTSERRAT },
          h2: { fontFamily: MONTSERRAT },
          h3: { fontFamily: MONTSERRAT },
          h4: { fontFamily: MONTSERRAT },
          h5: { fontFamily: MONTSERRAT },
          h6: { fontFamily: MONTSERRAT },
          subtitle1: { fontFamily: MONTSERRAT },
          subtitle2: { fontFamily: MONTSERRAT },
          body1: { fontFamily: MONTSERRAT },
          body2: { fontFamily: MONTSERRAT },
          button: { fontFamily: MONTSERRAT },
          caption: { fontFamily: MONTSERRAT },
          overline: { fontFamily: MONTSERRAT },
        },
      }),
    [muiTheme, currentOrg?.slug],
  );
  const [tabValue, setTabValue] = useState(0);
  const [rankings, setRankings] = useState<PlayerStats[]>([]);
  // Rankings for the division above currentDivision — used to detect extra
  // low-win-rate relegations up there that should open extra promotion spots
  // here (e.g. 3 relegated from Master instead of 2 means 3 promoted from
  // Division 1, not 2).
  const [aboveDivisionRankings, setAboveDivisionRankings] = useState<
    PlayerStats[]
  >([]);
  const [hideGuests, setHideGuests] = useState(true);
  const [rankingInfoOpen, setRankingInfoOpen] = useState(false);
  const [roadToMastersRankings, setRoadToMastersRankings] = useState<
    PlayerStats[]
  >([]);
  const [roadToMastersLoading, setRoadToMastersLoading] = useState(false);
  const [leagueDays, setLeagueDays] = useState<LeagueDay[]>([]);
  const [loading, setLoading] = useState(true);

  // Determine organization-specific settings
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const PE_BLUE = isPadelEagle ? "#743444" : "#1976d2";
  const PE_ORANGE = isPadelEagle ? "#E49421" : "#42a5f5";
  // Orgs with their own custom branding (e.g. RPL) get the pill-grid nav instead of the
  // SPL/Padel-Eagle icon tab bar. SPL itself is always excluded, even if its org doc has
  // a branding.primaryColor set (e.g. left over from a tournament-only color tweak).
  const hasCustomBranding =
    !isPadelEagle && !!currentOrg && currentOrg.slug !== "spl" && !!currentOrg.branding?.primaryColor;
  const orgPrimary = currentOrg?.branding?.primaryColor || PE_BLUE;
  const orgSecondary = currentOrg?.branding?.secondaryColor || PE_ORANGE;
  const orgAccentColor = currentOrg?.branding?.accentColor || orgPrimary;
  const customTabItems = [
    { label: "Standings", icon: TrophyIcon, value: 0 },
    { label: "Upcoming", icon: CalendarIcon, value: 2 },
    { label: "Results", icon: ResultsIcon, value: 1 },
    { label: "Records", icon: MastersIcon, value: 3 },
    { label: "Rules", icon: RulesBookIcon, value: 4 },
    { label: "Info", icon: InfoIcon, value: 5 },
    ...(isScorekeeper || isAdmin
      ? [{ label: "Score Matches", icon: ScoreMatchesIcon, value: 6 }]
      : []),
  ];

  // Helper: safely convert a Firestore Timestamp object or any date-like value to a JS Date
  const toJsDate = (d: any): Date => {
    if (!d) return new Date(NaN);
    if (d instanceof Date) return d;
    if (typeof d === "string" || typeof d === "number") return new Date(d);
    // Firestore Timestamp serialized as { _seconds, _nanoseconds } or { seconds, nanoseconds }
    const secs = d._seconds ?? d.seconds;
    if (typeof secs === "number") return new Date(secs * 1000);
    return new Date(NaN);
  };

  // View toggle for organizations with both types
  const [publicView, setPublicView] = useState<"league" | "tournament" | "spt">(() => {
    const saved = localStorage.getItem("publicView");
    return (saved as "league" | "tournament" | "spt") || "league";
  });
  const [publicTournaments, setPublicTournaments] = useState<any[]>([]);
  const [nationsLeagueEvents, setNationsLeagueEvents] = useState<any[]>([]);
  const [nationsFaqEvent, setNationsFaqEvent] = useState<any | null>(null);
  const [sptTournaments, setSptTournaments] = useState<any[]>([]);
  const [sptRegistrations, setSptRegistrations] = useState<any[]>([]);
  const [sptPendingInvitations, setSptPendingInvitations] = useState<any[]>([]);
  const [sptReceivedInvitations, setSptReceivedInvitations] = useState<any[]>([]);
  const [sptProcessingInviteId, setSptProcessingInviteId] = useState<string | null>(null);
  const [sptInfoTournament, setSptInfoTournament] = useState<any | null>(null);
  // Padel Eagle tab order is Tournaments, Ranking, Road to Masters (Tournaments and Road to Masters are disabled: coming soon). Panel indexes stay as they
  // were (Ranking 0, Road to Masters 1); Tournaments has its own value and renders the SPT cards.
  const PE_TOURNAMENTS_TAB = 7;
  const sptActive = publicView === "spt" || (isPadelEagle && tabValue === PE_TOURNAMENTS_TAB);
  const [peDefaultTabSet, setPeDefaultTabSet] = useState(false);
  // Only show the FAQ button when the org has defined FAQ items
  const [hasFaqItems, setHasFaqItems] = useState(false);
  useEffect(() => {
    if (!currentOrg?.id) return;
    apiRequest("/api/faq")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setHasFaqItems(Array.isArray(data) && data.length > 0))
      .catch(() => setHasFaqItems(false));
  }, [currentOrg?.id]);
  useEffect(() => {
    if (isPadelEagle && !peDefaultTabSet) {
      setPeDefaultTabSet(true);
      setTabValue(PE_TOURNAMENTS_TAB);
    }
  }, [isPadelEagle, peDefaultTabSet]);
  const [now, setNow] = useState(Date.now());

  const formatCountdown = (targetDate: Date) => {
    const diff = Math.max(0, targetDate.getTime() - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const openAnnouncementLink = (url?: string) => {
    if (!url) return;
    if (url.startsWith('/')) {
      window.location.href = url;
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Fetch tournaments for SPC tab
  useEffect(() => {
    if (publicView !== "tournament") return;
    const params = new URLSearchParams();
    if (playerUser?.playerId) {
      params.set("playerId", playerUser.playerId);
    } else {
      params.set("playerView", "true");
    }
    apiRequest(`/api/tournaments?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setPublicTournaments(Array.isArray(data) ? data.filter((t: any) => t.status !== "cancelled") : []))
      .catch(() => setPublicTournaments([]));
  }, [publicView, currentOrg?.id, playerUser?.playerId]);

  // Fetch Nations League editions for SPC tab
  useEffect(() => {
    if (publicView !== "tournament") return;
    apiRequest("/api/nations-league/")
      .then(r => r.ok ? r.json() : [])
      .then(data => setNationsLeagueEvents(Array.isArray(data) ? data : []))
      .catch(() => setNationsLeagueEvents([]));
  }, [publicView, currentOrg?.id]);

  // Fetch tournaments for SPT tab
  useEffect(() => {
    if (!sptActive) return;
    const params = new URLSearchParams();
    if (playerUser?.playerId) {
      params.set("playerId", playerUser.playerId);
    } else {
      params.set("playerView", "true");
    }
    apiRequest(`/api/tournaments?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSptTournaments(Array.isArray(data) ? data.filter((t: any) => t.status !== "cancelled") : []))
      .catch(() => setSptTournaments([]));
  }, [sptActive, currentOrg?.id, playerUser?.playerId]);

  // Fetch player registration/invitation state for SPT cards
  useEffect(() => {
    if (!sptActive || !playerUser || !playerToken) {
      setSptRegistrations([]);
      setSptPendingInvitations([]);
      setSptReceivedInvitations([]);
      return;
    }

    const fetchSptPlayerState = async () => {
      try {
        const [registrationsRes, sentRes, receivedRes] = await Promise.all([
          apiRequest("/api/tournament-registrations/my-registrations", {
            headers: { Authorization: `Bearer ${playerToken}` },
          }),
          apiRequest("/api/tournament-invitations/sent", {
            headers: { Authorization: `Bearer ${playerToken}` },
          }),
          apiRequest("/api/tournament-invitations/received", {
            headers: { Authorization: `Bearer ${playerToken}` },
          }),
        ]);

        if (registrationsRes.ok) {
          const regs = await registrationsRes.json();
          setSptRegistrations(Array.isArray(regs) ? regs : []);
        }

        if (sentRes.ok) {
          const sent = await sentRes.json();
          setSptPendingInvitations(
            Array.isArray(sent)
              ? sent.filter((inv: any) => inv.status === "pending")
              : [],
          );
        }

        if (receivedRes.ok) {
          const received = await receivedRes.json();
          setSptReceivedInvitations(
            Array.isArray(received)
              ? received.filter((inv: any) => inv.status === "pending")
              : [],
          );
        }
      } catch (error) {
        console.error("Error fetching SPT player state:", error);
      }
    };

    fetchSptPlayerState();
  }, [sptActive, playerUser?.playerId, playerToken]);

  const refreshSptState = async () => {
    if (!sptActive) return;

    const params = new URLSearchParams();
    if (playerUser?.playerId) {
      params.set("playerId", playerUser.playerId);
    } else {
      params.set("playerView", "true");
    }

    try {
      const [tournamentsRes, registrationsRes, sentRes, receivedRes] = await Promise.all([
        apiRequest(`/api/tournaments?${params}`),
        playerUser && playerToken
          ? apiRequest("/api/tournament-registrations/my-registrations", {
              headers: { Authorization: `Bearer ${playerToken}` },
            })
          : Promise.resolve(null as any),
        playerUser && playerToken
          ? apiRequest("/api/tournament-invitations/sent", {
              headers: { Authorization: `Bearer ${playerToken}` },
            })
          : Promise.resolve(null as any),
        playerUser && playerToken
          ? apiRequest("/api/tournament-invitations/received", {
              headers: { Authorization: `Bearer ${playerToken}` },
            })
          : Promise.resolve(null as any),
      ]);

      if (tournamentsRes?.ok) {
        const data = await tournamentsRes.json();
        setSptTournaments(
          Array.isArray(data) ? data.filter((t: any) => t.status !== "cancelled") : [],
        );
      }

      if (registrationsRes?.ok) {
        const regs = await registrationsRes.json();
        setSptRegistrations(Array.isArray(regs) ? regs : []);
      }

      if (sentRes?.ok) {
        const sent = await sentRes.json();
        setSptPendingInvitations(
          Array.isArray(sent)
            ? sent.filter((inv: any) => inv.status === "pending")
            : [],
        );
      }

      if (receivedRes?.ok) {
        const received = await receivedRes.json();
        setSptReceivedInvitations(
          Array.isArray(received)
            ? received.filter((inv: any) => inv.status === "pending")
            : [],
        );
      }
    } catch (error) {
      console.error("Error refreshing SPT player state:", error);
    }
  };

  const handleSptAcceptInvitation = async (invitationId: string) => {
    if (!playerToken) return;
    setSptProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await refreshSptState();
    } catch (error) {
      console.error("Error accepting invitation:", error);
    } finally {
      setSptProcessingInviteId(null);
    }
  };

  const handleSptDeclineInvitation = async (invitationId: string) => {
    if (!playerToken) return;
    setSptProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await refreshSptState();
    } catch (error) {
      console.error("Error declining invitation:", error);
    } finally {
      setSptProcessingInviteId(null);
    }
  };

  const handleSptCancelInvitation = async (invitationId: string) => {
    if (!playerToken) return;
    setSptProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await refreshSptState();
    } catch (error) {
      console.error("Error canceling invitation:", error);
    } finally {
      setSptProcessingInviteId(null);
    }
  };

  const handleSptWithdrawRegistration = async (
    registrationId: string,
    tournamentName: string,
  ) => {
    if (!playerToken) return;
    if (!window.confirm(`Are you sure you want to withdraw from ${tournamentName}?`)) {
      return;
    }
    try {
      const response = await apiRequest(`/api/tournament-registrations/${registrationId}/withdraw`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Failed to withdraw registration");
      }
      await refreshSptState();
    } catch (error: any) {
      console.error("Error withdrawing registration:", error);
      alert(error?.message || "Failed to withdraw registration");
    }
  };

  // Auto-toggle dark mode when switching views
  useEffect(() => {
    if (currentOrg?.competitionType === "both") {
      const shouldBeDark = publicView === "tournament" || publicView === "spt";
      if (shouldBeDark && mode === "light") {
        toggleTheme();
      } else if (!shouldBeDark && mode === "dark") {
        toggleTheme();
      }
      // Store view in localStorage for PublicLayout to detect
      localStorage.setItem("publicView", publicView);
    }
  }, [publicView, currentOrg?.competitionType]);

  // Dynamic logo based on view for both organizations
  const orgLogo =
    currentOrg?.branding?.logo ||
    (isPadelEagle
      ? padelEagleLogo
      : currentOrg?.competitionType === "both" && publicView === "tournament"
        ? spcLogo
        : currentOrg?.competitionType === "both" && publicView === "spt"
          ? sptLogo
          : splLogo);
  const orgName = isPadelEagle
    ? "Padel Eagle"
    : currentOrg?.competitionType === "both"
      ? (publicView === "tournament" ? "SPC" : publicView === "spt" ? "SPT" : "SPL")
      : currentOrg?.name || "SPL";
  // A custom pre-designed banner (club name/logo already baked into the image)
  // fully replaces the gradient hero's background, logo, and title.
  const hasHeroImage = !isPadelEagle && !!currentOrg?.branding?.heroImage;
  const isViewingAll = String(currentDivision) === "All";

  // Tournament cards: Padel Eagle hides completed ones, and the level selector filters by tournament level ("All" shows everything)
  const visibleSptTournaments = useMemo(() => {
    const base = isPadelEagle ? sptTournaments.filter((t: any) => t.status !== "completed") : sptTournaments;
    if (!isPadelEagle || isViewingAll) return base;
    return base.filter((t: any) => getTournamentLevels(t).includes(currentDivision as any));
  }, [sptTournaments, isPadelEagle, isViewingAll, currentDivision]);

  const displayedRankings = useMemo(
    () => hideGuests ? rankings.filter((p) => !p.isGuest && !p.isGuestPlayer) : rankings,
    [rankings, hideGuests]
  );

  // Organization-specific terminology
  const isTournamentOrg = currentOrg?.competitionType === "tournament";
  const eventLabel = isTournamentOrg ? "Tournament" : "League Day";
  const eventsLabel = isTournamentOrg ? "Tournaments" : "League Days";

  // Live Now: any league day scheduled for today that is not yet completed
  const isLiveNow = leagueDays.some((ld) => {
    if (ld.isCompleted || ld.isDraft) return false;
    const ldDate = toJsDate(ld.date);
    const today = new Date();
    return (
      ldDate.getFullYear() === today.getFullYear() &&
      ldDate.getMonth() === today.getMonth() &&
      ldDate.getDate() === today.getDate()
    );
  });

  // Update division context when player logs in
  useEffect(() => {
    if (playerUser?.division) {
      setPlayerDivision(playerUser.division as Division);
    }
  }, [playerUser?.division, setPlayerDivision]);

  const [selectedLeagueDay, setSelectedLeagueDay] = useState<LeagueDay | null>(
    null,
  );
  const [leagueDayMatches, setLeagueDayMatches] = useState<Match[]>([]);
  const [topPerformers, setTopPerformers] = useState<any[]>([]);
  const [matchStats, setMatchStats] = useState({
    totalMatches: 0,
    completedMatches: 0,
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState<Set<string>>(new Set());
  const [upcomingMatches, setUpcomingMatches] = useState<{
    [leagueDayId: string]: { leagueDay: LeagueDay; matches: any[] };
  }>({});

  // Next match for logged-in player: find earliest upcoming league day where player appears
  type NextMatch = {
    leagueDay: { date: any; time?: string; id: string; venueName?: string; venueAddress?: string };
    courtNumber: number;
    roundNumber: number;
    partner: string;
    opponents: string;
  };
  const nextPlayerMatch = (() => {
    if (!playerUser?.playerId) return null;
    const pid = playerUser.playerId;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let earliest: NextMatch | null = null;
    let earliestDate: Date | null = null;
    Object.values(upcomingMatches).forEach(({ leagueDay, matches }) => {
      const ldDate = toJsDate(leagueDay.date);
      ldDate.setHours(0, 0, 0, 0);
      if (ldDate < today) return;
      matches.forEach((match: any) => {
        const inPair1 =
          match.pair1?.player1Id === pid || match.pair1?.player2Id === pid;
        const inPair2 =
          match.pair2?.player1Id === pid || match.pair2?.player2Id === pid;
        if (!inPair1 && !inPair2) return;
        if (!earliestDate || ldDate < earliestDate) {
          earliestDate = ldDate;
          const myPair = inPair1 ? match.pair1 : match.pair2;
          const oppPair = inPair1 ? match.pair2 : match.pair1;
          const partner =
            myPair.player1Id === pid
              ? myPair.player2Name || "Partner"
              : myPair.player1Name || "Partner";
          const opponents = `${oppPair.player1Name || "?"} & ${oppPair.player2Name || "?"}`;
          earliest = { leagueDay, courtNumber: match.courtNumber, roundNumber: match.roundNumber, partner, opponents };
        }
      });
    });
    return earliest;
  })() as NextMatch | null;

  const [draftLeagueDays, setDraftLeagueDays] = useState<any[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  // Per-leagueDay registration state for the Upcoming tab
  const [registeringLeagueDayId, setRegisteringLeagueDayId] = useState<string | null>(null);
  const [registerSuccessId, setRegisterSuccessId] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<{ leagueDayId: string; message: string; isBalance?: boolean } | null>(null);
  // IDs of league days the logged-in player is actually eligible to register for
  const [eligibleLeagueDayIds, setEligibleLeagueDayIds] = useState<Set<string>>(new Set());
  const [scorekeeperDialogOpen, setScorekeeperDialogOpen] = useState(false);

  // Division Comparison State
  const [divisionComparison, setDivisionComparison] = useState<
    DivisionComparison[]
  >([]);

  // Cross-division hero stats (fetched once per org/season, not division-specific)
  const [orgStats, setOrgStats] = useState<{ totalPlayers: number; totalLeagueDays: number; totalMatches: number } | null>(null);

  // Records State
  const [records, setRecords] = useState<DivisionRecords | null>(null);

  // Head-to-Head State
  const [h2hOpen, setH2hOpen] = useState(false);
  const [selectedPlayer1, setSelectedPlayer1] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [comparingPlayerId, setComparingPlayerId] = useState<string | null>(
    null,
  );

  // Scorekeeper State
  const [scorekeeperMatches, setScorekeeperMatches] = useState<Match[]>([]);
  const [scorekeeperLoading, setScorekeeperLoading] = useState(false);
  const [scoringMatch, setScoringMatch] = useState<Match | null>(null);
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false);
  const [scorekeeperError, setScorekeeperError] = useState<string | null>(null);
  const [scorekeeperNames, setScorekeeperNames] = useState<{
    [key: string]: string;
  }>({});
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [leagueDayToReset, setLeagueDayToReset] = useState<{
    id: string;
    name: string;
    matchCount: number;
  } | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  // Announcement State
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const [announcementData, setAnnouncementData] = useState<any>(null);

  // Public Homepage Pop-ups (anonymous visitors — dismissal tracked in localStorage)
  const [pendingPublicPopups, setPendingPublicPopups] = useState<{
    id: string;
    title: string;
    message: string;
    imageUrl?: string;
    linkUrl?: string;
    linkLabel?: string;
    style?: PopupStyle;
  }[]>([]);
  const [currentPublicPopupIndex, setCurrentPublicPopupIndex] = useState(0);

  const dismissPublicPopup = (id: string) => {
    try {
      const dismissed: string[] = JSON.parse(localStorage.getItem('dismissedPublicPopups') || '[]');
      if (!dismissed.includes(id)) {
        dismissed.push(id);
        localStorage.setItem('dismissedPublicPopups', JSON.stringify(dismissed));
      }
    } catch {}
    setCurrentPublicPopupIndex((i) => i + 1);
  };

  // Cached data state
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Player Profile Modal State
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerHistory, setPlayerHistory] = useState<PlayerHistory | null>(
    null,
  );
  const [playerModalLoading, setPlayerModalLoading] = useState(false);
  const [playerHistoryError, setPlayerHistoryError] = useState<string | null>(null);
  const [badgePointsConfig, setBadgePointsConfig] = useState<Record<string, number> | null>(null);
  const [playerProfileOpen, setPlayerProfileOpen] = useState(false);
  const [avatarPreviewSrc, setAvatarPreviewSrc] = useState<string | null>(null);
  const [playerProfileSeasonId, setPlayerProfileSeasonId] = useState<
    string | null
  >(null);
  const [matchHistoryPage, setMatchHistoryPage] = useState(1);
  const matchesPerPage = 10;

  // Fetch scorekeeper names when matches change
  useEffect(() => {
    const fetchScorekeeperNames = async () => {
      const newNames: { [key: string]: string } = {};
      const uniqueScorekeeperIds = Array.from(
        new Set(
          scorekeeperMatches
            .filter((match) => match.scoredBy)
            .map((match) => match.scoredBy),
        ),
      ).filter((id): id is string => id !== undefined);

      for (const id of uniqueScorekeeperIds) {
        if (!scorekeeperNames[id]) {
          try {
            // Try scorekeeper first
            let response = await apiRequest(`/api/scorekeepers/${id}`);
            if (response.ok) {
              const data = await response.json();
              newNames[id] = `${data.name || id} (Scorekeeper)`;
            } else {
              // If scorekeeper not found, try admin
              const token = localStorage.getItem("authToken");
              if (token) {
                response = await fetch(
                  `${
                    process.env.REACT_APP_API_URL || "http://localhost:3001"
                  }/api/admins/${id}`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                  },
                );
                if (response.ok) {
                  const data = await response.json();
                  newNames[id] = `${data.username || id} (Admin)`;
                } else {
                  newNames[id] = id;
                }
              } else {
                newNames[id] = id;
              }
            }
          } catch (error) {
            console.error(`Error fetching user name for ID ${id}:`, error);
            newNames[id] = id;
          }
        }
      }

      if (Object.keys(newNames).length > 0) {
        setScorekeeperNames((prev) => ({ ...prev, ...newNames }));
      }
    };

    if (scorekeeperMatches.length > 0) {
      fetchScorekeeperNames();
    }
  }, [scorekeeperMatches]);

  // Fetch announcement
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await apiRequest("/api/announcements/active");
        const data = await response.json();
        console.log("Announcement data:", data);
        if (data && data.isEnabled && data.message) {
          setAnnouncement(data.message);
          setAnnouncementData(data);
          console.log("Announcement set:", data.message);
        } else {
          console.log("Announcement not enabled or no message");
        }
      } catch (error) {
        console.error("Error fetching announcement:", error);
      }
    };

    fetchAnnouncement();
  }, [currentOrg]);

  // Fetch active public homepage pop-ups (anonymous visitors)
  useEffect(() => {
    const fetchPublicPopups = async () => {
      try {
        const response = await apiRequest("/api/announcements/public-popups/active");
        if (!response.ok) return;
        const data = await response.json();
        let dismissed: string[] = [];
        try {
          dismissed = JSON.parse(localStorage.getItem('dismissedPublicPopups') || '[]');
        } catch {}
        const popups = Array.isArray(data) ? data.filter((p: any) => !dismissed.includes(p.id)) : [];
        setPendingPublicPopups(popups);
        setCurrentPublicPopupIndex(0);
      } catch (error) {
        console.error("Error fetching public pop-ups:", error);
      }
    };

    fetchPublicPopups();
  }, [currentOrg]);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Refresh data when coming back online
      fetchPublicData();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [currentDivision]);

  useEffect(() => {
    if (seasonLoading) return; // Wait for SeasonContext to resolve before fetching
    console.log(
      "[PublicHome] useEffect triggered - currentDivision:",
      currentDivision,
    );
    fetchPublicData();
  }, [currentDivision, currentOrg, selectedSeason?.id, seasonLoading]);

  // Fetch org-wide hero stats (all divisions combined, independent of selected division)
  useEffect(() => {
    if (!currentOrg || isTournamentOrg) return;
    const fetchOrgStats = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedSeason?.id) params.set('seasonId', selectedSeason.id);
        const response = await apiRequest(`/api/public/divisions/comparison?${params}`);
        if (response.ok) {
          const data: DivisionComparison[] = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const totals = data.reduce(
              (acc, div) => ({
                totalPlayers: acc.totalPlayers + (div.totalPlayers || 0),
                totalLeagueDays: acc.totalLeagueDays + (div.totalLeagueDays || 0),
                totalMatches: acc.totalMatches + (div.totalMatches || 0),
              }),
              { totalPlayers: 0, totalLeagueDays: 0, totalMatches: 0 },
            );
            setOrgStats(totals);
          }
        }
      } catch {
        // silently fail — hero stats just won't show totals
      }
    };
    fetchOrgStats();
  }, [currentOrg?.id, selectedSeason?.id]);

  // Fetch scorekeeper matches when switching to scorekeeper tab
  useEffect(() => {
    const scorekeeperTabIndex = hasCustomBranding ? 6 : isPadelEagle ? 5 : 4;
    if (tabValue === scorekeeperTabIndex && (isScorekeeper || isAdmin)) {
      console.log("[PublicHome] Score Matches tab activated, fetching matches");
      fetchScorekeeperMatches();
    }
  }, [tabValue, isScorekeeper, isAdmin, isPadelEagle, hasCustomBranding]);

  // Fetch Road to Masters rankings when switching to that tab (Padel Eagle only)
  useEffect(() => {
    if (isPadelEagle && tabValue === 1) {
      console.log(
        "[PublicHome] Road to Masters tab activated, fetching combined rankings",
      );
      fetchRoadToMastersData();
    }
  }, [tabValue, isPadelEagle]);

  const fetchRoadToMastersData = async () => {
    try {
      setRoadToMastersLoading(true);
      console.log("[PublicHome] Fetching Road to Masters combined rankings");

      // Fetch all active players without division filter
      const params = new URLSearchParams({
        rankBy: 'hybrid',
        participationWeight: '40',
        ...(selectedSeason?.id && { seasonId: selectedSeason.id }),
      });

      const rankingsResponse = await apiRequest(
        `/api/public/rankings?${params.toString()}&_t=${Date.now()}`,
      );

      if (rankingsResponse.ok) {
        const allPlayersRankings = await rankingsResponse.json();
        console.log(
          "[PublicHome] Road to Masters data received:",
          allPlayersRankings,
        );

        if (Array.isArray(allPlayersRankings)) {
          // Filter out test players and player_ placeholder names (case-insensitive)
          const filteredRankings = allPlayersRankings.filter((player) => {
            const nameToCheck = (player.playerName || "").toLowerCase();
            const hasTest = nameToCheck.includes("test");
            const hasPlayerUnderscore = nameToCheck.includes("player_");
            return !hasTest && !hasPlayerUnderscore;
          });

          console.log(
            `[PublicHome] Filtered ${allPlayersRankings.length} players to ${filteredRankings.length} (excluded test and player_ names)`,
          );
          setRoadToMastersRankings(filteredRankings);
        }
      }
    } catch (error) {
      console.error("[PublicHome] Error fetching Road to Masters data:", error);
    } finally {
      setRoadToMastersLoading(false);
    }
  };

  const fetchPublicData = async () => {
    try {
      if (String(currentDivision) === "All" && isPadelEagle) {
        // Fetch data from all divisions and aggregate
        const divisions: Division[] = [
          "Open",
          "Beginner",
          "Advanced",
          "Intermediate",
        ];

        // Fetch rankings from all divisions
        const allRankings: PlayerStats[] = [];
        for (const division of divisions) {
          const params = new URLSearchParams({
            division: division,
            rankBy: 'hybrid',
            participationWeight: '40',
            ...(selectedSeason?.id && { seasonId: selectedSeason.id }),
          });
          const rankingsResponse = await apiRequest(
            `/api/public/rankings?${params.toString()}&_t=${Date.now()}`,
          );
          if (rankingsResponse.ok) {
            const divisionRankings = await rankingsResponse.json();
            if (Array.isArray(divisionRankings)) {
              allRankings.push(...divisionRankings);
            }
          }
        }

        // Sort all rankings by hybrid score (or tournament points for tournament orgs)
        allRankings.sort((a, b) => {
          if (isTournamentOrg) {
            const aPoints = a.totalTournamentPoints || 0;
            const bPoints = b.totalTournamentPoints || 0;
            if (bPoints !== aPoints) return bPoints - aPoints;
            return b.gameDifference - a.gameDifference;
          }
          // Players with no games always go to the bottom
          if (a.gamesPlayed === 0 && b.gamesPlayed > 0) return 1;
          if (b.gamesPlayed === 0 && a.gamesPlayed > 0) return -1;
          // Hybrid score (already computed per-division by the server)
          const diff = ((b as any).hybridScore || 0) - ((a as any).hybridScore || 0);
          if (Math.abs(diff) > 0.01) return diff;
          return b.gamesPlayed - a.gamesPlayed;
        });

        setRankings(allRankings);
        console.log(
          "[PublicHome] Aggregated rankings from all divisions:",
          allRankings.length,
        );

        // Fetch league days from all divisions
        const allLeagueDays: LeagueDay[] = [];
        for (const division of divisions) {
          const leagueDaysResponse = await apiRequest(
            `/api/public/league-days?division=${division}`,
          );
          if (leagueDaysResponse.ok) {
            const divisionLeagueDays = await leagueDaysResponse.json();
            if (Array.isArray(divisionLeagueDays)) {
              allLeagueDays.push(...divisionLeagueDays);
            }
          }
        }

        // Sort league days by date (most recent first)
        allLeagueDays.sort((a, b) => {
          const getTimestamp = (date: any): number => {
            if (date._seconds) return date._seconds;
            if (date.seconds) return date.seconds;
            return new Date(date).getTime() / 1000;
          };
          return getTimestamp(b.date) - getTimestamp(a.date);
        });

        setLeagueDays(allLeagueDays);
        console.log(
          "[PublicHome] Aggregated league days from all divisions:",
          allLeagueDays.length,
        );

        // Fetch upcoming matches from all divisions
        await fetchUpcomingMatches();

        // Clear division comparison and records for "All"
        setDivisionComparison([]);
        setRecords(null);
      } else if (String(currentDivision) === "All") {
        // For non-Padel Eagle orgs, show division comparison
        const comparisonResponse = await apiRequest(
          `/api/public/divisions/comparison`,
        );
        if (comparisonResponse.ok) {
          const comparisonData = await comparisonResponse.json();
          console.log(
            "[PublicHome] Division comparison data received:",
            comparisonData,
          );
          setDivisionComparison(comparisonData);
        }
        setRecords(null);
        setRankings([]);
        setLeagueDays([]);
      } else {
        // Standard single division fetch
        // Always use the public rankings endpoint — same data, same logic for everyone.
        // Test division is special-cased via its own league-days endpoint below but rankings
        // are fine through the public route (the server already handles Test division filtering).
        const timestamp = Date.now();

        const params = new URLSearchParams({
          division: currentDivision,
          rankBy: 'hybrid',
          participationWeight: '40',
          _t: timestamp.toString(),
          ...(selectedSeason?.id && { seasonId: selectedSeason.id }),
        });

        const rankingsEndpoint = `/api/public/rankings?${params.toString()}`;

        const rankingsResponse = await apiRequest(rankingsEndpoint);
        if (rankingsResponse.ok) {
          const rankingsData = await rankingsResponse.json();
          console.log("[PublicHome] Rankings data received:", rankingsData);

          // Check if data is wrapped (from service worker cache)
          let actualData = rankingsData;
          let cachedTimestamp = null;

          if (rankingsData.data && rankingsData.cachedAt) {
            actualData = rankingsData.data;
            cachedTimestamp = rankingsData.cachedAt;
            console.log(
              "[PublicHome] Viewing cached data from:",
              cachedTimestamp,
            );
          }

          setCachedAt(cachedTimestamp);
          setRankings(Array.isArray(actualData) ? actualData : []);
        }

        // Fetch rankings for the division above, to detect extra low-win-rate
        // relegations there that should open extra promotion spots here.
        const aboveDivision = getPromotionTarget(currentDivision);
        if (aboveDivision) {
          const aboveParams = new URLSearchParams({
            division: aboveDivision,
            rankBy: 'hybrid',
            participationWeight: '40',
            ...(selectedSeason?.id && { seasonId: selectedSeason.id }),
          });
          const aboveResponse = await apiRequest(
            `/api/public/rankings?${aboveParams.toString()}`,
          );
          if (aboveResponse.ok) {
            const aboveData = await aboveResponse.json();
            const actualAboveData = aboveData?.data && aboveData?.cachedAt
              ? aboveData.data
              : aboveData;
            setAboveDivisionRankings(
              Array.isArray(actualAboveData) ? actualAboveData : [],
            );
          }
        } else {
          setAboveDivisionRankings([]);
        }

        // Fetch league days with division filter
        const leagueDaysEndpoint =
          currentDivision === "Test"
            ? `/api/league-days?division=${currentDivision}&isCompleted=true`
            : `/api/public/league-days?division=${currentDivision}${selectedSeason?.id ? `&seasonId=${selectedSeason.id}` : ""}`;

        const leagueDaysResponse = await apiRequest(leagueDaysEndpoint);
        if (leagueDaysResponse.ok) {
          const leagueDaysData = await leagueDaysResponse.json();
          setLeagueDays(leagueDaysData);
        }

        // Fetch upcoming matches
        await fetchUpcomingMatches();

        // Clear division comparison
        setDivisionComparison([]);

        // Fetch records for specific division
        const recordsResponse = await apiRequest(
          `/api/public/records/${currentDivision}${selectedSeason?.id ? `?seasonId=${selectedSeason.id}` : ""}`,
        );
        if (recordsResponse.ok) {
          const recordsData = await recordsResponse.json();
          setRecords(recordsData);
        } else {
          setRecords(null);
        }
      }

      // Fetch scorekeeper matches if user is a scorekeeper
      if (isScorekeeper) {
        await fetchScorekeeperMatches();
      }
    } catch (error) {
      console.error("Error fetching public data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingMatches = async () => {
    try {
      setUpcomingLoading(true);
      console.log(
        "[PublicHome] Fetching upcoming matches for division:",
        currentDivision,
      );

      if (String(currentDivision) === "All" && isPadelEagle) {
        // Fetch upcoming matches from all divisions
        const divisions: Division[] = [
          "Open",
          "Beginner",
          "Advanced",
          "Intermediate",
        ];
        const allUpcomingMatches: any = {};
        const allDraftLeagueDays: any[] = [];

        for (const division of divisions) {
          const matchesResponse = await apiRequest(
            `/api/public/upcoming-matches?division=${division}`,
          );
          if (matchesResponse.ok) {
            const matchesData = await matchesResponse.json();
            Object.assign(allUpcomingMatches, matchesData);
          }

          const draftsResponse = await apiRequest(
            `/api/public/draft-league-days?division=${division}`,
          );
          if (draftsResponse.ok) {
            const draftsData = await draftsResponse.json();
            if (Array.isArray(draftsData)) {
              allDraftLeagueDays.push(...draftsData);
            }
          }
        }

        setUpcomingMatches(allUpcomingMatches);
        setDraftLeagueDays(allDraftLeagueDays);
        console.log(
          "[PublicHome] Aggregated upcoming matches from all divisions",
        );
      } else {
        // Fetch upcoming matches for single division or non-Padel Eagle "All"
        const matchesResponse = await apiRequest(
          `/api/public/upcoming-matches?division=${currentDivision}`,
        );
        if (matchesResponse.ok) {
          const matchesData = await matchesResponse.json();
          console.log(
            "[PublicHome] Upcoming matches data received:",
            matchesData,
          );
          setUpcomingMatches(matchesData);
        } else {
          console.error(
            "[PublicHome] Failed to fetch upcoming matches:",
            matchesResponse.status,
          );
        }

        // Fetch draft league days
        const draftsResponse = await apiRequest(
          `/api/public/draft-league-days?division=${currentDivision}`,
        );
        if (draftsResponse.ok) {
          const draftsData = await draftsResponse.json();
          console.log(
            "[PublicHome] Draft league days data received:",
            draftsData,
          );
          setDraftLeagueDays(draftsData);
        } else {
          console.error(
            "[PublicHome] Failed to fetch draft league days:",
            draftsResponse.status,
          );
        }
      }
    } catch (error) {
      console.error("Error fetching upcoming matches:", error);
    } finally {
      setUpcomingLoading(false);
    }
  };

  // Fetch the league days the logged-in player is eligible to register for.
  // This mirrors the server-side division + allowedDivisions check so the button
  // is only shown when the player would actually be allowed to register.
  useEffect(() => {
    if (!playerToken) {
      setEligibleLeagueDayIds(new Set());
      return;
    }
    const fetchEligible = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/player/league-days/open`,
          { headers: { Authorization: `Bearer ${playerToken}` } }
        );
        if (res.ok) {
          const days: any[] = await res.json();
          setEligibleLeagueDayIds(new Set(days.map((d) => d.id)));
        }
      } catch {
        // non-critical — fall back to showing no register buttons
      }
    };
    fetchEligible();
  }, [playerToken]);

  const fetchScorekeeperMatches = async () => {
    console.log(
      "[DEBUG] fetchScorekeeperMatches called - isScorekeeper:",
      isScorekeeper,
      "isAdmin:",
      isAdmin,
      "currentDivision:",
      currentDivision,
    );

    // Check authentication token
    const token = localStorage.getItem("authToken");
    console.log(
      "[DEBUG] Auth token exists:",
      !!token,
      "Token preview:",
      token ? `${token.substring(0, 20)}...` : "null",
    );

    if (!isScorekeeper && !isAdmin) {
      console.log("[DEBUG] Not a scorekeeper or admin, skipping fetch");
      return;
    }

    if (!token) {
      setScorekeeperError(
        "Authentication required. Please log in as a scorekeeper or admin.",
      );
      console.error("[DEBUG] No auth token found in localStorage");
      return;
    }

    try {
      setScorekeeperLoading(true);
      setScorekeeperError(null);
      console.log(
        "[PublicHome] Fetching scorekeeper matches for division:",
        currentDivision,
      );

      const response = await apiRequest(
        `/api/scorekeepers/matches?division=${currentDivision}`,
      );
      if (response.ok) {
        const matches = await response.json();
        console.log("[PublicHome] Scorekeeper matches received:", matches);
        setScorekeeperMatches(matches);
        setScorekeeperError(null);
      } else if (response.status === 429) {
        setScorekeeperError(
          "Servers are currently under stress, please try again in a few minutes. If the problem persists please contact the admin.",
        );
        console.error(
          "[PublicHome] Rate limited when fetching scorekeeper matches",
        );
      } else {
        setScorekeeperError("Failed to load matches. Please try again later.");
        console.error(
          "[PublicHome] Failed to fetch scorekeeper matches:",
          response.status,
        );
      }
    } catch (error) {
      setScorekeeperError(
        "Network error. Please check your connection and try again.",
      );
      console.error("Error fetching scorekeeper matches:", error);
    } finally {
      setScorekeeperLoading(false);
    }
  };

  const handleScoreMatch = (match: Match) => {
    console.log("handleScoreMatch called with:", match);
    setScoringMatch(match);
    setScoringDialogOpen(true);
  };

  const handleUpdateMatchScore = async (matchId: string, scoreData: any) => {
    try {
      console.log("handleUpdateMatchScore called with:", {
        matchId,
        scoreData,
      });
      const response = await apiRequest(
        `/api/scorekeepers/matches/${matchId}/score`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(scoreData),
        },
      );

      if (response.ok) {
        await fetchScorekeeperMatches();
        setScoringDialogOpen(false);
        setScoringMatch(null);
      } else if (response.status === 429) {
        setScorekeeperError(
          "Servers are currently under stress, please try again in a few minutes. If the problem persists please contact the admin.",
        );
        setScoringDialogOpen(false);
        setScoringMatch(null);
      } else {
        setScorekeeperError(
          "Failed to update match score. Please try again later.",
        );
        console.error("Failed to update match score:", response.status);
        setScoringDialogOpen(false);
        setScoringMatch(null);
      }
    } catch (error) {
      setScorekeeperError(
        "Network error. Please check your connection and try again.",
      );
      console.error("Error updating match score:", error);
      setScoringDialogOpen(false);
      setScoringMatch(null);
    }
  };

  const handleResetLeagueDayScores = (
    leagueDayId: string,
    leagueDayName: string,
    matchCount: number,
  ) => {
    setLeagueDayToReset({ id: leagueDayId, name: leagueDayName, matchCount });
    setResetDialogOpen(true);
  };

  const confirmResetLeagueDayScores = async () => {
    if (!leagueDayToReset) return;

    try {
      setResetLoading(true);
      const response = await apiRequest(
        `/api/scorekeepers/league-days/${leagueDayToReset.id}/reset`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        // Refresh matches
        await fetchScorekeeperMatches();
        setResetDialogOpen(false);
        setLeagueDayToReset(null);
        setScorekeeperError(null);
      } else {
        const errorData = await response.json();
        setScorekeeperError(
          errorData.error || "Failed to reset league day scores",
        );
      }
    } catch (error) {
      setScorekeeperError(
        "Network error. Please check your connection and try again.",
      );
      console.error("Error resetting league day scores:", error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <TrophyIcon sx={{ color: "#FFD700", fontSize: 32 }} />;
      case 2:
        return <TrophyIcon sx={{ color: "#C0C0C0", fontSize: 28 }} />;
      case 3:
        return <TrophyIcon sx={{ color: "#CD7F32", fontSize: 24 }} />;
      default:
        return <StarIcon sx={{ color: PE_BLUE, fontSize: 20 }} />;
    }
  };

  // Helper function to check if a player is eligible for promotion/relegation
  const isEligibleForPromotionRelegation = (player: PlayerStats): boolean => {
    return !player.isGuest && !player.isGuestPlayer;
  };

  // A player is also relegation-flagged (independent of table position) if their
  // win rate is below 40%, once they've played enough league days for that
  // number to be meaningful. Uses the same raw win % shown as "WR" on each
  // player's card, so the tag always matches the visible number.
  const WIN_RATE_RELEGATION_THRESHOLD = 40;
  const MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION = 2;
  const isLowWinRateRelegation = (player: PlayerStats): boolean => {
    return (
      isEligibleForPromotionRelegation(player) &&
      player.leagueDaysPlayed >= MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION &&
      (player.winPercentage ?? 100) < WIN_RATE_RELEGATION_THRESHOLD
    );
  };

  // Get top players for promotion zone display
  // This includes enough players to show N eligible players in promotion zone
  const getTopPlayersForDisplay = (
    players: PlayerStats[],
    promotionSpots: number,
  ): PlayerStats[] => {
    const result: PlayerStats[] = [];
    let eligibleCount = 0;

    for (const player of players) {
      result.push(player);
      if (isEligibleForPromotionRelegation(player)) {
        eligibleCount++;
        if (eligibleCount >= promotionSpots) {
          break;
        }
      }
    }

    return result;
  };

  // Display-only promotion-zone size: Master and Women Division have no real
  // promotion target (getEffectivePromotionSpots correctly returns 0 for them),
  // but we still highlight their top 3 as a "Championship Zone" for recognition.
  // Every other division defers to the real, relegation-aware effective count.
  const getDisplayPromotionSpots = (
    division: Division,
    aboveDivisionRankings: PlayerStats[],
  ): number => {
    if (division === "Master" || division === "Women Division") return 3;
    return getEffectivePromotionSpots(division, aboveDivisionRankings);
  };

  const getZoneInfo = (
    division: Division,
    player: PlayerStats,
    allPlayers: PlayerStats[],
  ) => {
    // If player is a guest, they are not eligible for promotion/relegation
    if (!isEligibleForPromotionRelegation(player)) {
      return {
        zone: "safe",
        color: "#9e9e9e",
        icon: "➖",
        text: "Guest Player",
      };
    }

    // Registered players who haven't played a single game are shown neutrally —
    // only active players (gamesPlayed > 0) are evaluated for promotion/relegation.
    // Custom-branded orgs (e.g. RPL) show provisional zone coloring by registration
    // order instead, matching their pre-season reference design.
    if (player.gamesPlayed === 0 && !hasCustomBranding) {
      return {
        zone: "safe",
        color: "#9e9e9e",
        icon: "➖",
        text: "No games played",
      };
    }

    // Calculate position among eligible players who have actually played (gamesPlayed > 0).
    // Custom-branded orgs pre-season (no games played yet) rank provisionally by
    // registration order instead of excluding everyone.
    const eligiblePlayers = allPlayers.filter(
      (p) =>
        isEligibleForPromotionRelegation(p) &&
        (p.gamesPlayed > 0 || hasCustomBranding),
    );
    const eligibleRank =
      eligiblePlayers.findIndex((p) => p.playerId === player.playerId) + 1;
    const totalEligiblePlayers = eligiblePlayers.length;

    // For Padel Eagle: Top 48 players qualify for Masters
    if (isPadelEagle && eligibleRank <= 48) {
      return {
        zone: "masters",
        color: "#E49421", // Golden color for Masters qualifiers
        icon: "🏅",
        text: "Masters Qualifier",
      };
    }

    // Bumped up if the division above has extra low-win-rate relegations
    // beyond its usual "bottom N", so extra promotion spots open up here.
    // (Master/Women Division always show their top 3 as a Championship Zone.)
    const promotionSpots = getDisplayPromotionSpots(
      division,
      aboveDivisionRankings,
    );
    const relegationSpots = getRelegationSpots(division);

    // For non-Padel Eagle organizations, use standard promotion logic
    if (!isPadelEagle && eligibleRank <= promotionSpots) {
      return {
        zone: "promotion",
        color: "#4caf50",
        icon: "⬆️",
        text:
          division === "Master" || division === "Women Division"
            ? "Championship Zone"
            : "Promotion Zone",
      };
    }

    if (
      (relegationSpots > 0 &&
        eligibleRank > totalEligiblePlayers - relegationSpots) ||
      isLowWinRateRelegation(player)
    ) {
      return {
        zone: "relegation",
        color: isPadelEagle ? "#743444" : PE_BLUE,
        icon: "⬇️",
        text: "Relegation Zone",
      };
    }

    return {
      zone: "safe",
      color: "#9e9e9e",
      icon: "➖",
      text: "Safe Zone",
    };
  };

  // Relegation players (by position OR low win rate) are sunk to the bottom of
  // the displayed list so their row position matches their relegation status —
  // otherwise a low-win-rate player flagged "Relegation" could still appear
  // mid-table, confusing them about why they're marked that way. Zone status
  // itself is always computed against the original (points/hybrid) order —
  // passed in as `players` — so moving players around here never changes who
  // counts as position-relegated.
  const getFinalDisplayOrder = (
    players: PlayerStats[],
    division: Division,
  ): PlayerStats[] => {
    const safe: PlayerStats[] = [];
    const relegated: PlayerStats[] = [];
    players.forEach((player) => {
      const { zone } = getZoneInfo(division, player, players);
      (zone === "relegation" ? relegated : safe).push(player);
    });
    return [...safe, ...relegated];
  };

  const formatDate = (date: any) => {
    // Handle Firebase Timestamp
    let dateObj;
    if (date && typeof date.toDate === "function") {
      dateObj = date.toDate();
    } else if (date && date._seconds) {
      dateObj = new Date(date._seconds * 1000);
    } else if (date) {
      dateObj = new Date(date);
    } else {
      return "Invalid Date";
    }

    return dateObj.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (date: any, time?: string): string => {
    const dateString = formatDate(date);
    if (time) {
      return `${dateString} • ${time}`;
    }
    return dateString;
  };

  const handleLeagueDayClick = async (leagueDay: LeagueDay) => {
    setSelectedLeagueDay(leagueDay);
    setModalLoading(true);
    setExpandedRounds(new Set()); // Reset expanded rounds for new league day

    try {
      // Fetch matches for this league day with division filter
      const response = await apiRequest(
        `/api/public/league-days/${leagueDay.id}/matches?division=${currentDivision}`,
      );
      if (response.ok) {
        const data = await response.json();
        setLeagueDayMatches(data.matches);
        setTopPerformers(data.topPerformers || []);
        setMatchStats({
          totalMatches: data.totalMatches || 0,
          completedMatches: data.completedMatches || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching league day matches:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedLeagueDay(null);
    setLeagueDayMatches([]);
    setTopPerformers([]);
    setMatchStats({ totalMatches: 0, completedMatches: 0 });
  };

  // Register for a league day directly from the Upcoming tab
  const handleRegisterFromUpcoming = async (leagueDayId: string) => {
    if (!playerToken) return;
    setRegisteringLeagueDayId(leagueDayId);
    setRegisterError(null);
    setRegisterSuccessId(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/player/league-days/${leagueDayId}/register`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${playerToken}` },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setRegisterSuccessId(leagueDayId);
        // Refresh upcoming matches so the button state updates
        await fetchUpcomingMatches();
      } else if (data.error === "OUTSTANDING_BALANCE") {
        setRegisterError({
          leagueDayId,
          message: data.message,
          isBalance: true,
        });
      } else {
        setRegisterError({ leagueDayId, message: data.error || "Registration failed" });
      }
    } catch {
      setRegisterError({ leagueDayId, message: "Network error. Please try again." });
    } finally {
      setRegisteringLeagueDayId(null);
    }
  };

  // Player Profile Modal Functions
  const handlePlayerClick = async (playerId: string) => {
    // Track player profile click
    trackEvent('player_profile_click', {
      playerId: playerId,
      source: 'public_home',
    });
    
    setSelectedPlayerId(playerId);
    setPlayerProfileOpen(true);
    setPlayerProfileSeasonId(selectedSeason?.id || null);
    fetchPlayerHistory(playerId, selectedSeason?.id || null);
  };

  const fetchPlayerHistory = async (
    playerId: string,
    seasonId: string | null,
  ) => {
    setPlayerModalLoading(true);
    setPlayerHistory(null);
    setPlayerHistoryError(null);

    const params = new URLSearchParams();
    params.append("division", currentDivision);
    if (seasonId) {
      params.append("seasonId", seasonId);
    }

    // Fetch history and badge config independently: this dialog is shown on the
    // public, unauthenticated home page, so a stale/expired token causing one
    // request to fail (e.g. badge config) must not blank out the other's data.
    const [historyResult, badgeConfigResult] = await Promise.allSettled([
      apiRequest(`/api/public/players/${playerId}/history?${params.toString()}`),
      badgePointsConfig === null ? apiRequest("/api/badge-config") : Promise.resolve(null),
    ]);

    if (historyResult.status === "fulfilled" && historyResult.value.ok) {
      const data = await historyResult.value.json();
      setPlayerHistory(data);
    } else {
      const reason =
        historyResult.status === "rejected" ? historyResult.reason : undefined;
      console.error("Error fetching player history:", reason ?? historyResult);
      setPlayerHistoryError(
        "Couldn't load this player's profile. Please check your connection and try again.",
      );
    }

    if (
      badgeConfigResult.status === "fulfilled" &&
      badgeConfigResult.value?.ok
    ) {
      const config = await badgeConfigResult.value.json();
      setBadgePointsConfig(config);
    }

    setPlayerModalLoading(false);
  };

  const handlePlayerProfileSeasonChange = (seasonId: string) => {
    setPlayerProfileSeasonId(seasonId);
    setMatchHistoryPage(1);
    if (selectedPlayerId) {
      fetchPlayerHistory(selectedPlayerId, seasonId);
    }
  };

  const handleClosePlayerProfile = () => {
    setPlayerProfileOpen(false);
    setSelectedPlayerId(null);
    setPlayerHistory(null);
    setPlayerProfileSeasonId(null);
    setMatchHistoryPage(1);
  };

  // Helper function to calculate partnerships
  const calculatePartnerships = (): PartnershipStats[] => {
    if (!playerHistory || !selectedPlayerId) return [];

    const partnerships: {
      [key: string]: { name: string; won: number; total: number };
    } = {};

    playerHistory.matches.forEach((match) => {
      const isPair1 =
        match.pair1.player1Id === selectedPlayerId ||
        match.pair1.player2Id === selectedPlayerId;
      const partnerId = isPair1
        ? match.pair1.player1Id === selectedPlayerId
          ? match.pair1.player2Id
          : match.pair1.player1Id
        : match.pair2.player1Id === selectedPlayerId
          ? match.pair2.player2Id
          : match.pair2.player1Id;
      const partnerName = isPair1
        ? match.pair1.player1Id === selectedPlayerId
          ? match.pair1Player2Name
          : match.pair1Player1Name
        : match.pair2.player1Id === selectedPlayerId
          ? match.pair2Player2Name
          : match.pair2Player1Name;

      if (!partnerships[partnerId]) {
        partnerships[partnerId] = { name: partnerName, won: 0, total: 0 };
      }

      partnerships[partnerId].total++;

      const didWin = isPair1
        ? match.score.winnerPair === 1
        : match.score.winnerPair === 2;
      if (didWin) {
        partnerships[partnerId].won++;
      }
    });

    return Object.entries(partnerships)
      .map(([partnerId, stats]) => ({
        partnerId,
        partnerName: stats.name,
        gamesPlayed: stats.total,
        gamesWon: stats.won,
        winRate: stats.total > 0 ? (stats.won / stats.total) * 100 : 0,
      }))
      .sort((a, b) => b.gamesWon - a.gamesWon);
  };

  // Helper function to calculate opponents
  const calculateOpponents = (): OpponentStats[] => {
    if (!playerHistory || !selectedPlayerId) return [];

    const opponents: {
      [key: string]: { name: string; lost: number; total: number };
    } = {};

    playerHistory.matches.forEach((match) => {
      const isPair1 =
        match.pair1.player1Id === selectedPlayerId ||
        match.pair1.player2Id === selectedPlayerId;

      // Get both opponents
      const opponent1Id = isPair1
        ? match.pair2.player1Id
        : match.pair1.player1Id;
      const opponent2Id = isPair1
        ? match.pair2.player2Id
        : match.pair1.player2Id;
      const opponent1Name = isPair1
        ? match.pair2Player1Name
        : match.pair1Player1Name;
      const opponent2Name = isPair1
        ? match.pair2Player2Name
        : match.pair1Player2Name;

      const didLose = isPair1
        ? match.score.winnerPair === 2
        : match.score.winnerPair === 1;

      [
        { id: opponent1Id, name: opponent1Name },
        { id: opponent2Id, name: opponent2Name },
      ].forEach(({ id, name }) => {
        if (!opponents[id]) {
          opponents[id] = { name, lost: 0, total: 0 };
        }
        opponents[id].total++;
        if (didLose) {
          opponents[id].lost++;
        }
      });
    });

    return Object.entries(opponents)
      .map(([opponentId, stats]) => ({
        opponentId,
        opponentName: stats.name,
        gamesPlayed: stats.total,
        gamesLost: stats.lost,
        lossRate: stats.total > 0 ? (stats.lost / stats.total) * 100 : 0,
      }))
      .filter((opponent) => opponent.gamesLost > 0) // Only include opponents they've lost to
      .sort((a, b) => b.gamesLost - a.gamesLost);
  };

  const handleCompareClick = (
    e: React.MouseEvent,
    playerId: string,
    playerName: string,
  ) => {
    e.stopPropagation(); // Prevent card click
    if (!comparingPlayerId) {
      // First player selected
      setComparingPlayerId(playerId);
      setSelectedPlayer1({ id: playerId, name: playerName });
    } else if (comparingPlayerId === playerId) {
      // Same player clicked, cancel
      setComparingPlayerId(null);
      setSelectedPlayer1(null);
    } else {
      // Second player selected, open dialog
      setSelectedPlayer2({ id: playerId, name: playerName });
      setH2hOpen(true);
    }
  };

  const handleH2hClose = () => {
    setH2hOpen(false);
    setComparingPlayerId(null);
    setSelectedPlayer1(null);
    setSelectedPlayer2(null);
  };

  return (
    <MuiThemeProvider theme={montserratTheme}>
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <SeasonRegistrationBanner />

      {/* Theme Indicator Bar */}
      {currentOrg?.competitionType === "both" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background:
              publicView === "tournament"
                ? "linear-gradient(90deg, #2d3561 0%, #3d4571 50%, #2d3561 100%)"
                : publicView === "spt"
                  ? "linear-gradient(90deg, #1b5e20 0%, #4caf50 50%, #1b5e20 100%)"
                  : (isPadelEagle ? "#743444" : "linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)"),
            zIndex: 9999,
            transition: "all 0.5s ease",
            boxShadow:
              publicView === "tournament"
                ? "0 2px 10px rgba(80, 90, 140, 0.5)"
                : publicView === "spt"
                  ? "0 2px 10px rgba(27, 94, 32, 0.6)"
                  : "0 2px 10px rgba(25, 118, 210, 0.5)",
          }}
        />
      )}

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Cached Data Warning */}
      {cachedAt && !isOnline && (
        <Box sx={{ mt: 7, mb: 2 }}>
          <Alert severity="info" icon={<WarningIcon />}>
            <Typography variant="body2">
              <strong>Viewing cached data</strong> from{" "}
              {new Date(cachedAt).toLocaleString()}. These rankings may not
              reflect the most recent scores.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Public Homepage Pop-up (anonymous visitors) */}
      {pendingPublicPopups.length > 0 && currentPublicPopupIndex < pendingPublicPopups.length && (() => {
        const popup = pendingPublicPopups[currentPublicPopupIndex];
        return (
          <Dialog
            open
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
              sx: {
                overflow: 'hidden',
                borderRadius: 4,
                boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
              },
            }}
            TransitionProps={{ timeout: 350 }}
          >
            <PopupHeader style={popup.style || 'congrats'} title={popup.title} imageUrl={popup.imageUrl} size="hero" />
            <DialogContent sx={{ pt: 3, pb: 1 }}>
              <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>{popup.message}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
              {popup.linkUrl && (
                <Button
                  onClick={() => openPopupLink(popup.linkUrl)}
                  variant="contained"
                  size="large"
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    px: 3,
                  }}
                >
                  {popup.linkLabel || 'Open link'}
                </Button>
              )}
              <Button
                onClick={() => dismissPublicPopup(popup.id)}
                variant={popup.linkUrl ? 'outlined' : 'contained'}
                size="large"
                sx={{ fontWeight: 700, borderRadius: 2.5, textTransform: 'none', px: 3 }}
              >
                Got it ✓
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Announcement Marquee */}
      {announcement && (
        <Box
          sx={{
            bgcolor: announcementData?.backgroundColor || "primary.main",
            color: "white",
            py: 1.5,
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            boxShadow: 3,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              whiteSpace: "nowrap",
              animation: "marquee 20s linear infinite",
              paddingLeft: "100%",
              flex: 1,
              "@keyframes marquee": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-100%)" },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                display: "inline-block",
              }}
            >
              📢 {announcement}
            </Typography>
          </Box>
          {announcementData?.linkUrl && (
            <Button
              size="small"
              variant="contained"
              onClick={() => openAnnouncementLink(announcementData.linkUrl)}
              sx={{
                mr: 1,
                whiteSpace: "nowrap",
                bgcolor: "rgba(255,255,255,0.22)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.4)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              {announcementData?.linkLabel || "Open link"}
            </Button>
          )}
        </Box>
      )}

      {/* Hero Section */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 2, md: 3 },
            py: { xs: 2, sm: 2.5, md: 3.5 },
            px: { xs: 2, sm: 3, md: 4 },
            background: hasHeroImage
              ? `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%), url("${currentOrg?.branding?.heroImage}") center/contain no-repeat`
              : isPadelEagle
                ? "#743444"
                : currentOrg?.competitionType === "both" && publicView === "spt"
                  ? "linear-gradient(135deg, #0d2b18 0%, #1a5c30 50%, #0d2b18 100%)"
                  : currentOrg?.competitionType === "both" && publicView === "tournament"
                    ? "linear-gradient(135deg, #1a1f3a 0%, #2d3561 50%, #1a1f3a 100%)"
                    : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #81c784 100%)"),
            backgroundColor: hasHeroImage ? "#141414" : undefined,
            borderRadius: { xs: 2, md: 4 },
            color: "white",
            minHeight: hasHeroImage ? { xs: 200, sm: 260, md: 340 } : undefined,
            display: hasHeroImage ? "flex" : undefined,
            flexDirection: hasHeroImage ? "column" : undefined,
            justifyContent: hasHeroImage ? "flex-end" : undefined,
            boxShadow: hasHeroImage
              ? "0 8px 32px rgba(0, 0, 0, 0.4)"
              : isPadelEagle
                ? "0 8px 32px rgba(116,52,68, 0.4)"
                : currentOrg?.competitionType === "both" && publicView === "spt"
                  ? "0 12px 48px rgba(13, 43, 24, 0.8), inset 0 0 80px rgba(27, 94, 32, 0.2)"
                  : currentOrg?.competitionType === "both" && publicView === "tournament"
                    ? "0 12px 48px rgba(26, 31, 58, 0.8), inset 0 0 80px rgba(80, 90, 140, 0.15)"
                    : "0 8px 32px rgba(25, 118, 210, 0.3)",
            position: "relative",
            overflow: "hidden",
            "&::before": hasHeroImage
              ? {}
              : {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    currentOrg?.competitionType === "both" && publicView === "tournament"
                      ? 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M30 0 L35 20 L55 15 L40 30 L55 45 L35 40 L30 60 L25 40 L5 45 L20 30 L5 15 L25 20 Z\" fill=\"%23ffffff\" fill-opacity=\"0.03\"/%3E%3C/svg%3E\")'
                      : currentOrg?.competitionType === "both" && publicView === "spt"
                        ? 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Ccircle cx=\"10\" cy=\"10\" r=\"3\" fill=\"%23ffffff\" fill-opacity=\"0.04\"/%3E%3Ccircle cx=\"40\" cy=\"30\" r=\"3\" fill=\"%23ffffff\" fill-opacity=\"0.04\"/%3E%3Ccircle cx=\"20\" cy=\"50\" r=\"3\" fill=\"%23ffffff\" fill-opacity=\"0.04\"/%3E%3C/svg%3E\")'
                        : 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.1\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"4\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")',
                  opacity: 0.2,
                  animation:
                    currentOrg?.competitionType === "both" && publicView === "tournament"
                      ? "battlePattern 20s linear infinite"
                      : "none",
                  "@keyframes battlePattern": {
                    "0%": { transform: "translate(0, 0) rotate(0deg)" },
                    "100%": { transform: "translate(60px, 60px) rotate(360deg)" },
                  },
                },
            "&::after": {},
          }}
        >
          {/* Padel Eagle: muted promo video blended into the hero, with a gold inner frame */}
          {isPadelEagle && !hasHeroImage && (
            <>
              <Box
                component="video"
                src="https://video.wixstatic.com/video/615801_2444b967f440421ebb67dcbd3000ee08/480p/mp4/file.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center 38%",
                  opacity: 0.5,
                  mixBlendMode: "luminosity",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(60,18,30,0.35) 0%, rgba(60,18,30,0.15) 50%, rgba(60,18,30,0.5) 100%)",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: { xs: 6, md: 10 },
                  borderRadius: { xs: 1.5, md: 3 },
                  border: "1px solid rgba(228,148,33,0.65)",
                  boxShadow:
                    "inset 0 0 24px rgba(228,148,33,0.25), 0 0 14px rgba(228,148,33,0.2)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            </>
          )}

          {/* Logo Section — suppressed when a custom hero banner is set, since the banner already contains the club's logo */}
          {!hasHeroImage && (
          <Grow in timeout={1500}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 1.5,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  "&:hover": {
                    transform: "scale(1.05) rotate(2deg)",
                    transition: "transform 0.3s ease-in-out",
                  },
                  transition: "transform 0.3s ease-in-out",
                }}
              >
                <img
                  src={orgLogo}
                  alt={`${orgName} Logo`}
                  style={{
                    height: isPadelEagle ? "100px" : "80px",
                    width: "auto",
                    maxWidth: "100%",
                    borderRadius: "16px",
                    boxShadow: isPadelEagle
                      ? "0 8px 32px rgba(116,52,68, 0.5)"
                      : currentOrg?.competitionType === "both" &&
                          publicView === "tournament"
                        ? "0 0 30px rgba(80, 90, 140, 0.4), 0 0 50px rgba(45, 53, 97, 0.6)"
                        : "0 8px 32px rgba(0, 0, 0, 0.3)",
                    border: isPadelEagle
                      ? "3px solid #E49421"
                      : currentOrg?.competitionType === "both" &&
                          publicView === "tournament"
                        ? "3px solid rgba(120, 130, 180, 0.5)"
                        : "3px solid rgba(255, 255, 255, 0.3)",
                    background:
                      currentOrg?.competitionType === "both" &&
                      publicView === "tournament"
                        ? "linear-gradient(rgba(45, 53, 97, 0.4), rgba(80, 90, 140, 0.3))"
                        : "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    filter:
                      currentOrg?.competitionType === "both" &&
                      publicView === "tournament"
                        ? "sepia(1) hue-rotate(180deg) saturate(3) brightness(0.7)"
                        : "none",
                  }}
                />
                {/* Animated glow effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "-5px",
                    left: "-5px",
                    right: "-5px",
                    bottom: "-5px",
                    background:
                      currentOrg?.competitionType === "both" &&
                      publicView === "tournament"
                        ? "linear-gradient(45deg, #ffffff, #c0c8e0, #ffffff, #c0c8e0)"
                        : "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24)",
                    borderRadius: "20px",
                    zIndex: -1,
                    filter: "blur(8px)",
                    opacity:
                      currentOrg?.competitionType === "both" &&
                      publicView === "tournament"
                        ? 0.4
                        : 0.7,
                    animation:
                      currentOrg?.competitionType === "both" &&
                      publicView === "tournament"
                        ? "none"
                        : "rainbow 3s ease-in-out infinite",
                    "@keyframes rainbow": {
                      "0%, 100%": {
                        filter: "blur(8px) hue-rotate(0deg)",
                      },
                      "50%": {
                        filter: "blur(8px) hue-rotate(180deg)",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Grow>
          )}

          {!hasHeroImage && (
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              mb: 2,
              position: "relative",
              zIndex: 1,
              fontSize: { xs: "1.6rem", sm: "2.5rem", md: "3rem" },
              fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
              letterSpacing: "-0.01em",
              color: isPadelEagle ? "#ffffff" : undefined,
              textShadow: isPadelEagle ? "none" : "2px 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            {isPadelEagle ? (
              <>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    animation: "fly 3s ease-in-out infinite",
                    "@keyframes fly": {
                      "0%, 100%": {
                        transform: "translateY(0px) rotate(0deg)",
                      },
                      "25%": {
                        transform: "translateY(-10px) rotate(-5deg)",
                      },
                      "50%": {
                        transform: "translateY(-5px) rotate(0deg)",
                      },
                      "75%": {
                        transform: "translateY(-10px) rotate(5deg)",
                      },
                    },
                  }}
                >
                  🦅
                </Box>
                {" Padel Eagle Social Play"}
              </>
            ) : (
              <>
                {currentOrg?.competitionType === "both" ? (
                  publicView === "league" ? (
                    "🎾 Stuttgart Padel League 🏆"
                  ) : publicView === "spt" ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                      <Box component="span" sx={{ display: "inline-block", filter: "grayscale(1) drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}>
                        🎾
                      </Box>
                      Stuttgart Padel Tournaments
                      <Box component="span" sx={{ display: "inline-block", filter: "grayscale(1) drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}>
                        🏅
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          filter:
                            "grayscale(1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
                        }}
                      >
                        ⚔️
                      </Box>
                      Stuttgart Padel Cups
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          filter:
                            "grayscale(1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
                        }}
                      >
                        🏆
                      </Box>
                    </Box>
                  )
                ) : (
                  currentOrg?.name ? `Welcome to ${currentOrg.name}!` : "🎾 Welcome to Stuttgart Padel League! 🏆"
                )}
              </>
            )}
          </Typography>
          )}

          {/* iOS-Style Segmented Control View Switcher */}
          {currentOrg?.competitionType === "both" && !isPadelEagle && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2.5,
                  p: 0.5,
                  position: "relative",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: isPadelEagle ? "none" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
              >
                {/* SPL Option */}
                <Box
                  onClick={() => setPublicView("league")}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    cursor: "pointer",
                    borderRadius: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: { xs: "100px", sm: "120px" },
                    justifyContent: "center",
                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                >
                  {publicView === "league" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        borderRadius: 2,
                        boxShadow: isPadelEagle ? "none" : "0 2px 8px rgba(0, 0, 0, 0.15)",
                        zIndex: -1,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.4rem" },
                      lineHeight: 1,
                    }}
                  >
                    🏆
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      fontWeight: publicView === "league" ? 700 : 600,
                      color:
                        publicView === "league"
                          ? PE_BLUE
                          : "rgba(255, 255, 255, 0.85)",
                      transition: "color 0.3s ease",
                      letterSpacing: "0.02em",
                    }}
                  >
                    SPL
                  </Typography>
                </Box>

                {/* SPC Option */}
                <Box
                  onClick={() => setPublicView("tournament")}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    cursor: "pointer",
                    borderRadius: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: { xs: "100px", sm: "120px" },
                    justifyContent: "center",
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  {publicView === "tournament" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        borderRadius: 2,
                        boxShadow: isPadelEagle ? "none" : "0 2px 8px rgba(0, 0, 0, 0.15)",
                        zIndex: -1,
                      }}
                    />
                  )}
                  <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" }, lineHeight: 1 }}>⚔️</Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      fontWeight: publicView === "tournament" ? 700 : 600,
                      color: publicView === "tournament" ? "#2d3561" : "rgba(255, 255, 255, 0.85)",
                      transition: "color 0.3s ease",
                      letterSpacing: "0.02em",
                    }}
                  >
                    SPC
                  </Typography>
                </Box>

                {/* SPT Option */}
                <Box
                  onClick={() => setPublicView("spt")}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    cursor: "pointer",
                    borderRadius: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: { xs: "100px", sm: "120px" },
                    justifyContent: "center",
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  {publicView === "spt" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        borderRadius: 2,
                        boxShadow: isPadelEagle ? "none" : "0 2px 8px rgba(0, 0, 0, 0.15)",
                        zIndex: -1,
                      }}
                    />
                  )}
                  <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" }, lineHeight: 1 }}>🎾</Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      fontWeight: publicView === "spt" ? 700 : 600,
                      color: publicView === "spt" ? "#1b5e20" : "rgba(255, 255, 255, 0.85)",
                      transition: "color 0.3s ease",
                      letterSpacing: "0.02em",
                    }}
                  >
                    SPT
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {!hasHeroImage && (
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              opacity: 0.85,
              position: "relative",
              zIndex: 1,
              fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.1rem" },
              px: { xs: 1, sm: 2 },
              fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
              fontWeight: 500,
              fontStyle: "italic",
              letterSpacing: "0.02em",
              color: isPadelEagle ? "#ffffff" : undefined,
              ...(isPadelEagle && { opacity: 1 }),
            }}
          >
            {isPadelEagle ? (
              "Amateur Padel Turniere mit Event-Charakter, die selbst Profis begeistern würden"
            ) : currentOrg?.competitionType === "both" ? (
              publicView === "league" ? (
                "Where Champions Are Made, One Match at a Time"
              ) : (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontWeight: "bold",
                    textShadow: "0 2px 10px rgba(0, 0, 0, 0.6)",
                    letterSpacing: "0.05em",
                  }}
                >
                  Elite Tournament Series - Battle for Glory
                </Box>
              )
            ) : (
              "Where Champions Are Made, One Match at a Time"
            )}
          </Typography>
          )}
          {!hasHeroImage && !isPadelEagle && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 2, sm: 3, md: 4 },
                position: "relative",
                zIndex: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {currentOrg?.competitionType === "both" &&
              (publicView === "tournament" || publicView === "spt") ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 1, md: 2 },
                    width: "100%",
                    maxWidth: "900px",
                    mx: "auto",
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  {(publicView === "spt"
                    ? [
                        { text: "REGISTER", icon: "📋" },
                        { text: "COMPETE", icon: "🎾" },
                        { text: "WIN", icon: "🏅" },
                        { text: "REPEAT", icon: "🔁" },
                      ]
                    : [
                        { text: "COMPETE", icon: "⚔️" },
                        { text: "DOMINATE", icon: "🛡️" },
                        { text: "CONQUER", icon: "⚡" },
                        { text: "REIGN", icon: "👑" },
                      ]
                  ).map((item, index) => (
                    <Box
                      key={item.text}
                      sx={{
                        flex: { xs: "0 1 auto", sm: 1 },
                        minWidth: { xs: 0, sm: "auto" },
                        position: "relative",
                        background:
                          "linear-gradient(180deg, rgba(80, 90, 140, 0.15) 0%, rgba(45, 53, 97, 0.4) 100%)",
                        border: "2px solid rgba(120, 130, 180, 0.3)",
                        borderRadius: 2,
                        p: { xs: 1, sm: 1.5, md: 2.5 },
                        textAlign: "center",
                        backdropFilter: "blur(10px)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: "rgba(120, 130, 180, 0.6)",
                          boxShadow:
                            "0 8px 24px rgba(80, 90, 140, 0.4), inset 0 0 20px rgba(120, 130, 180, 0.1)",
                          background:
                            "linear-gradient(180deg, rgba(80, 90, 140, 0.25) 0%, rgba(45, 53, 97, 0.5) 100%)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, transparent 30%, rgba(120, 130, 180, 0.15) 50%, transparent 70%)",
                          animation: "shine 3s ease-in-out infinite",
                          animationDelay: `${index * 0.3}s`,
                          pointerEvents: "none",
                          "@keyframes shine": {
                            "0%": { transform: "translateX(-100%)" },
                            "100%": { transform: "translateX(100%)" },
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.5rem" },
                          mb: { xs: 0.25, sm: 0.5 },
                          filter:
                            "grayscale(1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.5rem", sm: "0.7rem", md: "1rem" },
                          fontWeight: 900,
                          letterSpacing: { xs: "0.01em", sm: "0.05em", md: "0.1em" },
                          color: "rgba(255, 255, 255, 0.95)",
                          textShadow: "0 2px 8px rgba(0, 0, 0, 0.8)",
                          fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <>
                  {[
                    {
                      value: orgStats?.totalPlayers ?? "—",
                      label: "Active Players",
                      icon: "👥",
                    },
                    {
                      value: orgStats?.totalLeagueDays ?? "—",
                      label: "League Days",
                      icon: "📅",
                    },
                    {
                      value: orgStats?.totalMatches ?? "—",
                      label: "Matches Played",
                      icon: "🎾",
                    },
                  ].map((stat) => (
                    <Box
                      key={stat.label}
                      sx={{
                        flex: { xs: "0 0 calc(33% - 8px)", sm: 1 },
                        minWidth: { xs: 80, sm: 120 },
                        background: "rgba(255,255,255,0.08)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        borderRadius: 3,
                        p: { xs: 1, sm: 1.5 },
                        textAlign: "center",
                        transition: "all 0.25s ease",
                        cursor: "default",
                        "&:hover": {
                          background: "rgba(255,255,255,0.14)",
                          transform: "translateY(-4px)",
                          boxShadow: isPadelEagle ? "none" : "0 8px 24px rgba(0,0,0,0.25)",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: "1rem", sm: "1.2rem" }, mb: 0.25 }}>
                        {stat.icon}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                          fontWeight: 800,
                          fontSize: { xs: "1.1rem", sm: "1.4rem", md: "1.7rem" },
                          color: isPadelEagle ? "#E49421" : "rgba(255,255,255,0.95)",
                          lineHeight: 1,
                          mb: 0.25,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.72rem" },
                          fontWeight: 600,
                          color: "rgba(255,255,255,0.75)",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* FAQ button */}
          {hasFaqItems && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2.5,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Button
              startIcon={<InfoIcon />}
              onClick={() => navigate("/faq")}
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                fontWeight: 600,
                fontSize: { xs: "0.78rem", md: "0.85rem" },
                letterSpacing: "0.04em",
                textTransform: "none",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: 2,
                px: 2.5,
                py: 0.85,
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "rgba(255,255,255,0.22)",
                  borderColor: "rgba(255,255,255,0.55)",
                  color: "white",
                  transform: "translateY(-1px)",
                  boxShadow: isPadelEagle ? "none" : "0 4px 16px rgba(0,0,0,0.2)",
                },
              }}
            >
              FAQ — How does the league work?
            </Button>
          </Box>
          )}
          {/* Keep the Padel Eagle hero the same height when there is no FAQ button */}
          {!hasFaqItems && isPadelEagle && <Box sx={{ mt: 2.5, height: 36.5 }} />}
        </Box>
      </Fade>

      {/* Stats row — shown below the hero (instead of overlaid on it) when a custom banner image is set */}
      {hasHeroImage && (
        <Fade in timeout={1200}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2, md: 3 },
              mb: { xs: 2, md: 3 },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {[
              {
                value: orgStats?.totalPlayers ?? "—",
                label: "Players",
                icon: PlayersIcon,
                color: hasCustomBranding ? orgPrimary : "primary.main",
              },
              {
                value: orgStats?.totalLeagueDays ?? "—",
                label: eventsLabel,
                icon: CalendarIcon,
                color: hasCustomBranding ? "#e67e22" : "primary.main",
              },
              {
                value: orgStats?.totalMatches ?? "—",
                label: "Matches",
                icon: MatchesBoltIcon,
                color: hasCustomBranding ? "#4caf50" : "primary.main",
              },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  flex: { xs: "0 0 calc(33% - 12px)", sm: 1 },
                  minWidth: { xs: 80, sm: 120 },
                  maxWidth: 220,
                  p: { xs: 1.25, sm: 2 },
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <stat.icon sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem" }, mb: 0.25, color: stat.color }} />
                <Typography
                  sx={{
                    fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" },
                    color: stat.color,
                    lineHeight: 1,
                    mb: 0.25,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {stat.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Fade>
      )}

      {/* Next Match Banner — shown to logged-in players when they have an upcoming match */}
      {nextPlayerMatch && (
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              mb: { xs: 2, md: 2.5 },
              borderRadius: 2,
              overflow: "hidden",
              border: "1.5px solid",
              borderColor: isPadelEagle ? "#E49421" : PE_ORANGE,
              background: isPadelEagle
                ? "#FFF5F0"
                : "linear-gradient(135deg, rgba(25,118,210,0.07) 0%, rgba(66,165,245,0.12) 100%)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
                px: { xs: 2, md: 3 },
                py: { xs: 1.5, md: 2 },
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {/* Pulsing icon */}
              <Box
                sx={{
                  fontSize: { xs: "1.6rem", md: "2rem" },
                  flexShrink: 0,
                  animation: "nextMatchBounce 2s ease-in-out infinite",
                  "@keyframes nextMatchBounce": {
                    "0%, 100%": { transform: "translateY(0)" },
                    "50%": { transform: "translateY(-4px)" },
                  },
                }}
              >
                🎾
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.05rem" },
                    color: isPadelEagle ? "#E49421" : PE_BLUE,
                    mb: 0.25,
                  }}
                >
                  Your next match:{" "}
                  {toJsDate(nextPlayerMatch.leagueDay.date).toLocaleDateString(
                    "en-GB",
                    { weekday: "long", day: "numeric", month: "short" },
                  )}
                  {nextPlayerMatch.leagueDay.time
                    ? ` · ${nextPlayerMatch.leagueDay.time}`
                    : ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.82rem" },
                    color: "text.secondary",
                  }}
                >
                  Court {nextPlayerMatch.courtNumber} · Round{" "}
                  {nextPlayerMatch.roundNumber} · with{" "}
                  <strong>{nextPlayerMatch.partner}</strong> vs{" "}
                  {nextPlayerMatch.opponents}
                </Typography>
                <VenueBadge
                  venueName={nextPlayerMatch.leagueDay.venueName}
                  venueAddress={nextPlayerMatch.leagueDay.venueAddress}
                  variant="body2"
                />
              </Box>
              {isLiveNow && (
                <Chip
                  label="Live Now"
                  size="small"
                  sx={{
                    bgcolor: "#4caf50",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    flexShrink: 0,
                    animation: "liveNowPulse 1.5s ease-out infinite",
                  }}
                />
              )}
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Scorekeeper Registration Section */}
      {/* Scorekeeper Registration - Discreet Link */}

      {/* Custom-branded orgs (RPL) show the pill nav above the season/division pickers */}
      {hasCustomBranding &&
        (isPadelEagle || String(currentDivision) !== "All") &&
        (currentOrg?.competitionType !== "both" || publicView === "league") && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: { xs: 1, md: 1.5 },
              mb: { xs: 2, md: 3 },
            }}
          >
            {customTabItems.map((item) => {
              const selected = tabValue === item.value;
              const ItemIcon = item.icon;
              return (
                <Box
                  key={item.value}
                  onClick={() => setTabValue(item.value)}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.4,
                    height: { xs: 56, md: 64 },
                    borderRadius: "14px",
                    px: { xs: 0.5, md: 1 },
                    fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                    fontWeight: 700,
                    fontSize: { xs: "0.65rem", sm: "0.72rem", md: "0.8rem" },
                    textAlign: "center",
                    lineHeight: 1.1,
                    transition: "all 0.2s ease",
                    backgroundColor: selected ? orgPrimary : `${orgSecondary}26`,
                    color: selected ? "#fff" : orgSecondary,
                    boxShadow: selected ? `0 4px 12px ${orgPrimary}59` : "none",
                    "&:hover": {
                      backgroundColor: selected ? orgPrimary : `${orgSecondary}40`,
                    },
                  }}
                >
                  <ItemIcon sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }} />
                  {item.label}
                </Box>
              );
            })}
          </Box>
        )}

      {/* Division Selector - Hide in tournament view for both organizations */}
      {(currentOrg?.competitionType !== "both" || publicView === "league") && (
        <Fade in timeout={1200}>
          <Paper
            elevation={hasCustomBranding ? 0 : 2}
            sx={{
              p: { xs: 2, md: 3 },
              mb: { xs: 2, md: 3 },
              borderRadius: { xs: 2, md: 3 },
              border: hasCustomBranding
                ? "none"
                : isPadelEagle
                  ? "1px solid rgba(116,52,68, 0.2)"
                  : "1px solid rgba(25, 118, 210, 0.1)",
              backgroundColor: hasCustomBranding ? "transparent" : undefined,
              boxShadow: hasCustomBranding ? "none" : undefined,
            }}
          >
            {hasCustomBranding ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: orgSecondary,
                      mb: 0.75,
                    }}
                  >
                    Choose Season
                  </Typography>
                  <SeasonSelector
                    fullWidth={false}
                    excludeDrafts={true}
                    allowAllSeasons={true}
                    showStatus={false}
                    hideLabel={true}
                    size="small"
                    sx={{
                      minWidth: 150,
                      maxWidth: 190,
                      "& .MuiOutlinedInput-root": { borderRadius: "14px" },
                      "& .MuiSelect-select": {
                        py: 0.75,
                        px: 1.75,
                        fontSize: "0.85rem",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: orgSecondary,
                      mb: 0.75,
                    }}
                  >
                    Choose League
                  </Typography>
                  <DivisionSelector
                    fullWidth={false}
                    excludeTest={!isAdmin && !isScorekeeper}
                    excludeUnassigned={!isAdmin && !isScorekeeper}
                    showIcon={false}
                    hideLabel={true}
                    size="small"
                    sx={{
                      minWidth: 150,
                      maxWidth: 190,
                      "& .MuiOutlinedInput-root": { borderRadius: "14px" },
                      "& .MuiSelect-select": {
                        py: 0.75,
                        px: 1.75,
                        fontSize: "0.85rem",
                      },
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "space-evenly" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.5, sm: 2 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: isPadelEagle ? "#743444" : PE_BLUE,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textAlign: { xs: "center", sm: "left" },
                    mb: { xs: 0.5, sm: 0 },
                  }}
                >
                  {isPadelEagle ? "🏆 Select Level" : "🏆 Select Division"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    minWidth: { xs: "100%", sm: "auto" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {/* Padel Eagle tournaments have no season logic */}
                  {!isPadelEagle && (
                    <Box
                      sx={{
                        minWidth: { xs: "100%", sm: 200 },
                        width: { xs: "100%", sm: 250 },
                      }}
                    >
                      <SeasonSelector fullWidth={true} excludeDrafts={true} allowAllSeasons={true} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      minWidth: { xs: "100%", sm: 200 },
                      width: { xs: "100%", sm: 250 },
                    }}
                  >
                    <DivisionSelector
                      fullWidth={true}
                      excludeTest={!isAdmin && !isScorekeeper}
                      excludeUnassigned={!isAdmin && !isScorekeeper}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Fade>
      )}

      {/* Navigation Tabs */}
      {(isPadelEagle || String(currentDivision) !== "All") &&
        (currentOrg?.competitionType !== "both" || publicView === "league") && (
          <Paper
            elevation={hasCustomBranding ? 0 : 3}
            sx={{
              borderRadius: { xs: 2, md: 3 },
              overflow: "hidden",
              mb: { xs: 2, md: 3 },
              p: 0,
              backgroundColor: hasCustomBranding ? "transparent" : undefined,
              boxShadow: hasCustomBranding ? "none" : undefined,
            }}
          >
            {hasCustomBranding ? null : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered={false}
              variant="fullWidth"
              sx={{
                background: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                p: { xs: 0.5, md: 0.75 },
                gap: { xs: 0.5, md: 1 },
                "& .MuiTabs-flexContainer": { gap: { xs: 0.5, md: 1 } },
                "& .MuiTab-root": {
                  fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
                  fontWeight: 700,
                  fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                  py: { xs: 1.25, md: 2 },
                  px: { xs: 0.5, sm: 1, md: 3 },
                  minHeight: { xs: 44, md: 56 },
                  minWidth: { xs: "auto", md: "auto" },
                  borderRadius: { xs: 1.5, md: 2 },
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    background: isPadelEagle
                      ? "#743444"
                      : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"),
                    color: "white",
                    boxShadow: isPadelEagle
                      ? "none"
                      : "0 4px 12px rgba(25, 118, 210, 0.35)",
                  },
                },
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
            >
              {isPadelEagle && (
                <Tab
                  value={PE_TOURNAMENTS_TAB}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box component="span" sx={{ fontSize: "1.2rem" }}>🏅</Box>
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        Tournaments
                      </Box>
                    </Box>
                  }
                />
              )}
              <Tab
                value={0}
                disabled={isPadelEagle}
                sx={isPadelEagle ? { opacity: "0.6 !important" } : undefined}
                label={
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <TrophyIcon
                        sx={{ fontSize: { xs: "1.3rem", md: "1.2rem" } }}
                      />
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        {isPadelEagle ? "Ranking" : "Rankings"}
                      </Box>
                    </Box>
                    {isPadelEagle && (
                      <Box component="span" sx={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
                        Coming soon
                      </Box>
                    )}
                  </Box>
                }
              />
              {isPadelEagle && (
                <Tab
                  value={1}
                  disabled
                  sx={{ opacity: "0.6 !important" }}
                  label={
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <MastersIcon sx={{ fontSize: { xs: "1.3rem", md: "1.2rem" } }} />
                        <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                          Road to Masters
                        </Box>
                      </Box>
                      <Box component="span" sx={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
                        Coming soon
                      </Box>
                    </Box>
                  }
                />
              )}
              {!isPadelEagle && (
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        📅 Results
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "inline", md: "none" },
                          fontSize: "1.3rem",
                        }}
                      >
                        📅
                      </Box>
                      {isLiveNow && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#4caf50",
                            boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
                            animation: "liveNowPulse 1.5s ease-out infinite",
                            "@keyframes liveNowPulse": {
                              "0%": {
                                boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
                              },
                              "70%": {
                                boxShadow: "0 0 0 6px rgba(76, 175, 80, 0)",
                              },
                              "100%": {
                                boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
                              },
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              )}
              {!isPadelEagle && (
                <Tab
                  label={
                    <Box>
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        🎾 Upcoming
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "inline", md: "none" },
                          fontSize: "1.3rem",
                        }}
                      >
                        🎾
                      </Box>
                    </Box>
                  }
                />
              )}
              {!isPadelEagle && (
                <Tab
                  label={
                    <Box>
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        📊 Records
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "inline", md: "none" },
                          fontSize: "1.3rem",
                        }}
                      >
                        📊
                      </Box>
                    </Box>
                  }
                />
              )}
              {!isPadelEagle && (isScorekeeper || isAdmin) && (
                <Tab
                  label={
                    <Box>
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        📝 Score Matches
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "inline", md: "none" },
                          fontSize: "1.3rem",
                        }}
                      >
                        📝
                      </Box>
                    </Box>
                  }
                />
              )}
            </Tabs>
            )}

            {/* Level Specific Rankings Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ minHeight: "600px" }}>
                {!hasCustomBranding && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: { xs: 2.5, md: 3.5 },
                      pb: 1.5,
                      borderBottom: "2px solid",
                      borderBottomColor: isPadelEagle ? "#E49421" : PE_BLUE,
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: { xs: 28, md: 36 },
                        borderRadius: 1,
                        background: isPadelEagle
                          ? "#743444"
                          : (isPadelEagle ? "#743444" : "linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)"),
                        flexShrink: 0,
                      }}
                    />
                    <TrophyIcon sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, color: isPadelEagle ? "#E49421" : PE_ORANGE }} />
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                        fontWeight: 900,
                        fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.6rem" },
                        background: isPadelEagle
                          ? "#743444"
                          : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)"),
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {isPadelEagle ? "Level Specific Ranking" : "Rankings"}
                    </Typography>
                  </Box>
                )}

                {!hasCustomBranding && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: "text.secondary",
                    }}
                  >
                    Current standings for{" "}
                    {isViewingAll ? "All Divisions" : currentDivision}
                  </Typography>
                )}

                {/* Padel Eagle - Masters Tournament Info */}
                {isPadelEagle && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mb: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      icon={<MastersIcon />}
                      label="Best 24 Teams (48 Players) qualify for the end-of-year Masters Tournament"
                      sx={{
                        bgcolor: "rgba(228,148,33, 0.15)",
                        color: "#E49421",
                        border: "2px solid #E49421",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        py: 2.5,
                      }}
                    />
                  </Box>
                )}

                {/* SPL - Promotion/Relegation Info */}
                {!isPadelEagle && !hasCustomBranding && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mb: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    {(currentDivision === "Master" || currentDivision === "Women Division") && (
                      <Chip
                        icon={<MastersIcon />}
                        label="Championship Zone"
                        sx={{
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          color: "#4caf50",
                          border: "2px solid #4caf50",
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          py: 2.5,
                        }}
                      />
                    )}
                    {currentDivision !== "Master" && currentDivision !== "Women Division" && getPromotionSpots(currentDivision) > 0 && (
                      <Chip
                        icon={<TrendingUpIcon />}
                        label={
                          getEffectivePromotionSpots(currentDivision, aboveDivisionRankings) >
                          getPromotionSpots(currentDivision)
                            ? `Top ${getEffectivePromotionSpots(currentDivision, aboveDivisionRankings)} get promoted (extra spot due to relegations above)`
                            : `Top ${getPromotionSpots(currentDivision)} get promoted`
                        }
                        sx={{
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          color: "#4caf50",
                          border: "2px solid #4caf50",
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          height: "auto",
                          py: 1,
                          "& .MuiChip-label": {
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                            display: "block",
                            textAlign: "center",
                            py: { xs: 0.5, sm: 0 },
                          },
                        }}
                      />
                    )}
                    {getRelegationSpots(currentDivision) > 0 && (
                      <Chip
                        icon={<WarningIcon />}
                        label={`Bottom ${getRelegationSpots(currentDivision)} get relegated, as well as anyone with less than 40% win rate after 2 league days played`}
                        sx={{
                          bgcolor: hasCustomBranding ? `${orgPrimary}1a` : "rgba(25, 118, 210, 0.1)",
                          color: hasCustomBranding ? orgPrimary : PE_BLUE,
                          border: `2px solid ${hasCustomBranding ? orgPrimary : PE_BLUE}`,
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          height: "auto",
                          py: 1,
                          "& .MuiChip-label": {
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                            display: "block",
                            textAlign: "center",
                            py: { xs: 0.5, sm: 0 },
                          },
                        }}
                      />
                    )}
                  </Box>
                )}

                {displayedRankings.length > 0 || rankings.length > 0 ? (
                  <Box key={`rankings-${currentDivision}`}>
                    {/* Ranking method legend + guest toggle */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
                      {!isTournamentOrg && !hasCustomBranding && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic", textAlign: "center" }}
                          >
                            Rankings combine total games won this season (40%) and win-loss percentage (60%) — winning more and losing less moves you up.{" "}
                            <Box
                              component="span"
                              onClick={() => setRankingInfoOpen(true)}
                              sx={{ textDecoration: "underline", cursor: "pointer", color: PE_BLUE, fontStyle: "normal", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              Learn more
                            </Box>
                          </Typography>
                          <Tooltip title="How are rankings calculated?">
                            <IconButton
                              size="small"
                              onClick={() => setRankingInfoOpen(true)}
                              sx={{ color: "#E49421", p: 0.25 }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={hideGuests}
                            onChange={(e) => setHideGuests(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            Hide guests
                          </Typography>
                        }
                      />
                    </Box>

                    {/* Dynamic Promotion Podium */}
                    {getTopPlayersForDisplay(
                      displayedRankings,
                      getDisplayPromotionSpots(currentDivision, aboveDivisionRankings),
                    ).length > 0 && (
                      <Box
                        key={`promotion-${currentDivision}`}
                        sx={{ mb: { xs: 3, md: 4 } }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            gap: { xs: 1.5, sm: 2, md: 3 },
                            mb: 3,
                          }}
                        >
                          {(() => {
                            const podiumPlayers = getTopPlayersForDisplay(
                              displayedRankings,
                              getDisplayPromotionSpots(currentDivision, aboveDivisionRankings),
                            );
                            const podiumWithRank = podiumPlayers.map(
                              (player, i) => ({ player, position: i + 1 }),
                            );
                            // Classic pedestal arrangement (2nd-1st-3rd) for
                            // custom-branded orgs, matching their reference design.
                            return hasCustomBranding && podiumWithRank.length >= 3
                              ? [
                                  podiumWithRank[1],
                                  podiumWithRank[0],
                                  podiumWithRank[2],
                                  ...podiumWithRank.slice(3),
                                ]
                              : podiumWithRank;
                          })().map(({ player, position }) => {
                            const isFirst = position === 1;
                            const isCurrentPlayer =
                              playerUser?.playerId === player.playerId;
                            const { color, icon } = getZoneInfo(
                              currentDivision,
                              player,
                              displayedRankings,
                            );

                            // Size and styling based on position
                            const size = hasCustomBranding
                              ? isFirst
                                ? { xs: 76, sm: 86, md: 100 }
                                : position === 2
                                  ? { xs: 60, sm: 68, md: 80 }
                                  : { xs: 52, sm: 60, md: 70 }
                              : isFirst
                                ? { xs: 90, sm: 100, md: 120 }
                                : position === 2
                                  ? { xs: 70, sm: 80, md: 90 }
                                  : { xs: 60, sm: 70, md: 80 };

                            const badgeSize = hasCustomBranding
                              ? isFirst
                                ? { xs: 24, md: 28 }
                                : position === 2
                                  ? { xs: 20, md: 24 }
                                  : { xs: 18, md: 20 }
                              : isFirst
                                ? { xs: 28, md: 32 }
                                : position === 2
                                  ? { xs: 24, md: 28 }
                                  : { xs: 20, md: 24 };

                            const transform =
                              position === 1
                                ? "translateY(0px)"
                                : position === 2
                                  ? "translateY(15px)"
                                  : "translateY(25px)";

                            return (
                              <Grow
                                key={`${currentDivision}-${player.playerId}`}
                                in
                                timeout={500 + position * 100}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    transform,
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: `${transform} scale(1.05)`,
                                    },
                                  }}
                                  onClick={() =>
                                    handlePlayerClick(player.playerId)
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: size,
                                      height: size,
                                      borderRadius: "50%",
                                      background: isCurrentPlayer
                                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                        : color,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "white",
                                      boxShadow: isFirst
                                        ? "0 8px 24px rgba(228,148,33, 0.6)"
                                        : isCurrentPlayer
                                          ? "0 8px 24px rgba(102, 126, 234, 0.8)"
                                          : color === "#E49421"
                                            ? "0 4px 12px rgba(228,148,33, 0.4)"
                                            : "0 4px 12px rgba(0,0,0,0.2)",
                                      position: "relative",
                                      border: isFirst
                                        ? "4px solid white"
                                        : isCurrentPlayer
                                          ? "5px solid #ffffff"
                                          : "3px solid white",
                                      animation: isCurrentPlayer
                                        ? "playerPulse 1.5s ease-in-out infinite"
                                        : "none",
                                      "@keyframes playerPulse": {
                                        "0%, 100%": {
                                          boxShadow:
                                            "0 8px 24px rgba(102, 126, 234, 0.8), 0 0 0 0 rgba(102, 126, 234, 0.7)",
                                          transform: "scale(1)",
                                        },
                                        "50%": {
                                          boxShadow:
                                            "0 12px 36px rgba(102, 126, 234, 1), 0 0 0 10px rgba(102, 126, 234, 0)",
                                          transform: "scale(1.05)",
                                        },
                                      },
                                    }}
                                  >
                                    <SafeAvatar
                                      src={player.profilePicture}
                                      alt={player.playerName}
                                      fallbackText={player.playerName}
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        // Let the parent's zone-color circle show through
                                        // when there's no profile photo to display.
                                        bgcolor: player.profilePicture
                                          ? undefined
                                          : "transparent",
                                        filter: player.privacyPolicyAccepted
                                          ? "none"
                                          : "blur(20px)", // GDPR: blur if privacy not accepted
                                      }}
                                    />
                                    {isFirst && (
                                      <TrophyIcon
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          fontSize: { xs: 20, md: 24 },
                                          filter:
                                            "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                                        }}
                                      />
                                    )}
                                    {/* Position Badge */}
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: isFirst ? -14 : -12,
                                        right: isFirst ? -14 : -12,
                                        width: badgeSize,
                                        height: badgeSize,
                                        borderRadius: "50%",
                                        background: color,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: {
                                          xs: "0.7rem",
                                          md: isFirst ? "1.1rem" : "0.8rem",
                                        },
                                        fontWeight: "bold",
                                        border: isFirst
                                          ? "3px solid white"
                                          : "2px solid white",
                                      }}
                                    >
                                      {position}
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      mt: isFirst ? 2 : 1.5,
                                      textAlign: "center",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant={isFirst ? "h6" : "subtitle2"}
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: isFirst
                                          ? {
                                              xs: "0.9rem",
                                              sm: "1rem",
                                              md: "1.2rem",
                                            }
                                          : {
                                              xs: "0.75rem",
                                              sm: "0.8rem",
                                              md: "0.9rem",
                                            },
                                        maxWidth: {
                                          xs: 80,
                                          md: isFirst ? 140 : 100,
                                        },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: isPadelEagle
                                          ? "#743444"
                                          : hasCustomBranding
                                            ? orgPrimary
                                            : PE_BLUE,
                                      }}
                                    >
                                      {player.playerName}
                                    </Typography>
                                    {hasCustomBranding && position <= 3 && (
                                      <Box
                                        sx={{
                                          minWidth: isFirst ? 36 : 30,
                                          textAlign: "center",
                                          borderRadius: 1.5,
                                          py: 0.25,
                                          px: 1,
                                          fontWeight: "bold",
                                          fontSize: isFirst ? "1rem" : "0.9rem",
                                          color: position === 2 ? "#37474f" : "#fff",
                                          bgcolor: ["#FFD700", "#C0C0C0", "#CD7F32"][
                                            position - 1
                                          ],
                                        }}
                                      >
                                        {position}
                                      </Box>
                                    )}
                                    {(player.isGuestPlayer || isViewingAll) &&
                                      player.division && (
                                        <Chip
                                          label={
                                            isViewingAll
                                              ? player.division
                                              : `From ${player.division}`
                                          }
                                          size="small"
                                          sx={{
                                            height: isFirst ? "20px" : "16px",
                                            fontSize: isFirst
                                              ? "0.7rem"
                                              : "0.6rem",
                                            backgroundColor: isViewingAll
                                              ? "rgba(228,148,33, 0.2)"
                                              : "rgba(156, 39, 176, 0.2)",
                                            color: isViewingAll
                                              ? "#E49421"
                                              : "#9c27b0",
                                            border: isViewingAll
                                              ? "1px solid rgba(228,148,33, 0.5)"
                                              : "1px solid rgba(156, 39, 176, 0.5)",
                                          }}
                                        />
                                      )}
                                    {player.isGuest && (
                                      <Chip
                                        label="Guest"
                                        size="small"
                                        sx={{
                                          height: isFirst ? "20px" : "16px",
                                          fontSize: isFirst
                                            ? "0.7rem"
                                            : "0.6rem",
                                          backgroundColor:
                                            "rgba(255, 152, 0, 0.2)",
                                          color: "#ff9800",
                                          border:
                                            "1px solid rgba(255, 152, 0, 0.5)",
                                        }}
                                      />
                                    )}
                                    {player.isManagementTeam && (
                                      <Chip
                                        label={isPadelEagle ? "Padel Eagle Team" : "SP Team"}
                                        size="small"
                                        sx={{
                                          height: isFirst ? "20px" : "16px",
                                          fontSize: isFirst
                                            ? "0.7rem"
                                            : "0.6rem",
                                          backgroundColor:
                                            "rgba(25, 118, 210, 0.2)",
                                          color: PE_BLUE,
                                          border:
                                            "1px solid rgba(25, 118, 210, 0.5)",
                                          fontWeight: "bold",
                                        }}
                                      />
                                    )}
                                    {!isTournamentOrg && player.hybridScore != null && player.gamesPlayed > 0 && (
                                      <Chip
                                        label={`${(player.hybridScore).toFixed(1)} Score`}
                                        size="small"
                                        sx={{
                                          height: isFirst ? "22px" : "18px",
                                          fontSize: isFirst ? "0.7rem" : "0.62rem",
                                          bgcolor: "rgba(25, 118, 210, 0.12)",
                                          color: PE_BLUE,
                                          border: "1px solid rgba(25, 118, 210, 0.4)",
                                          fontWeight: 700,
                                        }}
                                      />
                                    )}
                                    {!isTournamentOrg && player.gamesPlayed > 0 && (
                                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                        <Chip
                                          label={`${player.totalPoints}pts`}
                                          size="small"
                                          sx={{
                                            height: isFirst ? "18px" : "15px",
                                            fontSize: isFirst ? "0.62rem" : "0.58rem",
                                            bgcolor: "rgba(0,0,0,0.06)",
                                            color: "text.secondary",
                                            border: "1px solid rgba(0,0,0,0.15)",
                                          }}
                                        />
                                        <Chip
                                          label={`${(player.winPercentage ?? 0).toFixed(1)}% WR`}
                                          size="small"
                                          sx={{
                                            height: isFirst ? "18px" : "15px",
                                            fontSize: isFirst ? "0.62rem" : "0.58rem",
                                            bgcolor: "rgba(0,0,0,0.06)",
                                            color: "text.secondary",
                                            border: "1px solid rgba(0,0,0,0.15)",
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                  <Tooltip
                                    title={
                                      !comparingPlayerId
                                        ? "Compare with another player"
                                        : comparingPlayerId === player.playerId
                                          ? "Cancel selection"
                                          : "Click to compare"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      color={
                                        comparingPlayerId === player.playerId
                                          ? "primary"
                                          : "default"
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCompareClick(
                                          e,
                                          player.playerId,
                                          player.playerName,
                                        );
                                      }}
                                      sx={{
                                        backgroundColor:
                                          comparingPlayerId === player.playerId
                                            ? "primary.light"
                                            : "transparent",
                                        mt: 0.5,
                                      }}
                                    >
                                      <CompareIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Grow>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Remaining Players - All Rankings */}
                    {(() => {
                      const topPlayers = getTopPlayersForDisplay(
                        displayedRankings,
                        getDisplayPromotionSpots(currentDivision, aboveDivisionRankings),
                      );
                      const topPlayerIds = new Set(
                        topPlayers.map((p) => p.playerId),
                      );
                      const remainingPlayers = getFinalDisplayOrder(
                        displayedRankings,
                        currentDivision,
                      ).filter((p) => !topPlayerIds.has(p.playerId));

                      return (
                        remainingPlayers.length > 0 && (
                          <Grid
                            container
                            spacing={{ xs: 1, sm: 2 }}
                            key={`remaining-${currentDivision}`}
                          >
                            {remainingPlayers.map((player, index) => {
                              const actualPosition =
                                topPlayers.length + index + 1;
                              const isCurrentPlayer =
                                playerUser?.playerId === player.playerId;
                              const { color, icon, text, zone } = getZoneInfo(
                                currentDivision,
                                player,
                                displayedRankings,
                              );
                              const isRelegationZone = zone === "relegation";
                              const isMastersZone = zone === "masters";

                              return (
                                <Grow
                                  in
                                  timeout={800 + index * 50}
                                  key={`${currentDivision}-${player.playerId}`}
                                >
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Card
                                      sx={{
                                        backgroundColor: isCurrentPlayer
                                          ? "rgba(102, 126, 234, 0.1)"
                                          : isMastersZone
                                            ? "rgba(228,148,33, 0.08)"
                                            : isRelegationZone
                                              ? "action.selected"
                                              : "background.paper",
                                        border: isCurrentPlayer
                                          ? "2px solid"
                                          : isMastersZone
                                            ? "2px solid"
                                            : isRelegationZone
                                              ? "1px solid"
                                              : "1px solid",
                                        borderColor: isCurrentPlayer
                                          ? "#667eea"
                                          : isMastersZone
                                            ? "#E49421"
                                            : isRelegationZone
                                              ? "primary.main"
                                              : "divider",
                                        borderLeft: isCurrentPlayer
                                          ? "4px solid #667eea"
                                          : isMastersZone
                                            ? "4px solid #E49421"
                                            : actualPosition <= 3
                                              ? `4px solid ${["#FFD700", "#C0C0C0", "#CD7F32"][actualPosition - 1]}`
                                              : isRelegationZone
                                                ? `4px solid ${PE_BLUE}`
                                                : "1px solid",
                                        borderRadius: { xs: 1.5, md: 2 },
                                        transition: "all 0.25s ease",
                                        cursor: "pointer",
                                        boxShadow: isCurrentPlayer
                                          ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                                          : isMastersZone
                                            ? "0 2px 8px rgba(228,148,33, 0.2)"
                                            : "none",
                                        "&:hover": {
                                          transform: { xs: "none", md: "translateY(-3px)" },
                                          boxShadow: isCurrentPlayer
                                            ? "0 8px 20px rgba(102, 126, 234, 0.4)"
                                            : isMastersZone
                                              ? "0 6px 16px rgba(228,148,33, 0.3)"
                                              : isRelegationZone
                                                ? "0 6px 16px rgba(33, 150, 243, 0.15)"
                                                : "0 6px 20px rgba(30, 58, 138, 0.18)",
                                        },
                                      }}
                                      onClick={() =>
                                        handlePlayerClick(player.playerId)
                                      }
                                    >
                                      <CardContent
                                        sx={{
                                          p: hasCustomBranding
                                            ? { xs: 1, md: 1.25 }
                                            : { xs: 1.5, md: 2 },
                                          "&:last-child": {
                                            pb: hasCustomBranding
                                              ? { xs: 1, md: 1.25 }
                                              : undefined,
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: { xs: 1, md: 1.5 },
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: "bold",
                                                color: isMastersZone
                                                  ? "#E49421"
                                                  : isRelegationZone
                                                    ? "#d32f2f"
                                                    : PE_BLUE,
                                                fontSize: {
                                                  xs: "1rem",
                                                  md: "1.1rem",
                                                },
                                                minWidth: "fit-content",
                                              }}
                                            >
                                              #{actualPosition}
                                            </Typography>
                                            {player.rankChange !== undefined &&
                                              player.rankChange !== 0 && (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.25,
                                                    animation:
                                                      "slideIn 0.5s ease-out",
                                                    "@keyframes slideIn": {
                                                      from: {
                                                        opacity: 0,
                                                        transform:
                                                          "translateY(10px)",
                                                      },
                                                      to: {
                                                        opacity: 1,
                                                        transform:
                                                          "translateY(0)",
                                                      },
                                                    },
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: {
                                                        xs: "1.1rem",
                                                        md: "1.3rem",
                                                      },
                                                      color:
                                                        player.rankChange > 0
                                                          ? "#4caf50"
                                                          : "#f44336",
                                                      lineHeight: 1,
                                                    }}
                                                  >
                                                    {player.rankChange > 0
                                                      ? "⬆️"
                                                      : "⬇️"}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontWeight: "bold",
                                                      color:
                                                        player.rankChange > 0
                                                          ? "#4caf50"
                                                          : "#f44336",
                                                      fontSize: {
                                                        xs: "0.7rem",
                                                        md: "0.75rem",
                                                      },
                                                    }}
                                                  >
                                                    {Math.abs(
                                                      player.rankChange,
                                                    )}
                                                  </Typography>
                                                </Box>
                                              )}
                                          </Box>
                                          <SafeAvatar
                                            src={player.profilePicture}
                                            alt={player.playerName}
                                            fallbackText={player.playerName}
                                            sx={{
                                              width: hasCustomBranding
                                                ? { xs: 28, md: 32 }
                                                : { xs: 32, md: 36 },
                                              height: hasCustomBranding
                                                ? { xs: 28, md: 32 }
                                                : { xs: 32, md: 36 },
                                              filter:
                                                player.privacyPolicyAccepted
                                                  ? "none"
                                                  : "blur(20px)",
                                            }}
                                          />
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: hasCustomBranding ? 0.25 : 0.5,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle1"
                                                sx={{
                                                  fontWeight: "bold",
                                                  color: isMastersZone
                                                    ? "#E49421"
                                                    : isRelegationZone
                                                      ? isPadelEagle
                                                        ? "#743444"
                                                        : "#d32f2f"
                                                      : isPadelEagle
                                                        ? "#743444"
                                                        : PE_BLUE,
                                                  fontSize: {
                                                    xs: "0.85rem",
                                                    md: "0.95rem",
                                                  },
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  width: "100%",
                                                }}
                                              >
                                                {player.playerName}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  gap: 0.5,
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                {(player.isGuestPlayer ||
                                                  isViewingAll) &&
                                                  player.division && (
                                                    <Chip
                                                      label={
                                                        isViewingAll
                                                          ? player.division
                                                          : `From ${player.division}`
                                                      }
                                                      size="small"
                                                      sx={{
                                                        height: "18px",
                                                        fontSize: "0.65rem",
                                                        backgroundColor:
                                                          isViewingAll
                                                            ? "rgba(228,148,33, 0.15)"
                                                            : "rgba(156, 39, 176, 0.15)",
                                                        color: isViewingAll
                                                          ? "#E49421"
                                                          : "#9c27b0",
                                                        border: isViewingAll
                                                          ? "1px solid rgba(228,148,33, 0.3)"
                                                          : "1px solid rgba(156, 39, 176, 0.3)",
                                                      }}
                                                    />
                                                  )}
                                                {player.isGuest && (
                                                  <Chip
                                                    label="Guest"
                                                    size="small"
                                                    sx={{
                                                      height: "18px",
                                                      fontSize: "0.65rem",
                                                      backgroundColor:
                                                        "rgba(255, 152, 0, 0.15)",
                                                      color: "#ff9800",
                                                      border:
                                                        "1px solid rgba(255, 152, 0, 0.3)",
                                                    }}
                                                  />
                                                )}
                                                {player.isManagementTeam && (
                                                  <Chip
                                                    label={isPadelEagle ? "Padel Eagle Team" : "SP Team"}
                                                    size="small"
                                                    sx={{
                                                      height: "18px",
                                                      fontSize: "0.65rem",
                                                      backgroundColor:
                                                        "rgba(25, 118, 210, 0.15)",
                                                      color: PE_BLUE,
                                                      border:
                                                        "1px solid rgba(25, 118, 210, 0.3)",
                                                      fontWeight: "bold",
                                                    }}
                                                  />
                                                )}
                                              </Box>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                gap: 0.5,
                                                mt: hasCustomBranding ? 0.25 : 0.5,
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {!isTournamentOrg && player.hybridScore != null && player.gamesPlayed > 0 && (
                                                <Chip
                                                  label={`${(player.hybridScore).toFixed(1)} Score`}
                                                  size="small"
                                                  sx={{
                                                    fontSize: { xs: "0.62rem", md: "0.67rem" },
                                                    height: { xs: 22, md: 24 },
                                                    bgcolor: "rgba(25, 118, 210, 0.12)",
                                                    color: PE_BLUE,
                                                    border: "1px solid rgba(25, 118, 210, 0.4)",
                                                    fontWeight: 700,
                                                  }}
                                                />
                                              )}
                                              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                                <Chip
                                                  label={`${isTournamentOrg ? player.totalTournamentPoints || 0 : player.totalPoints}pts`}
                                                  size="small"
                                                  sx={{
                                                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                                                    height: { xs: 20, md: 22 },
                                                    bgcolor: "rgba(0,0,0,0.06)",
                                                    color: "text.secondary",
                                                    border: "1px solid rgba(0,0,0,0.15)",
                                                  }}
                                                />
                                                {!isTournamentOrg && player.gamesPlayed > 0 && (
                                                  <Chip
                                                    label={`${(player.winPercentage ?? 0).toFixed(1)}% WR`}
                                                    size="small"
                                                    sx={{
                                                      fontSize: { xs: "0.6rem", md: "0.65rem" },
                                                      height: { xs: 20, md: 22 },
                                                      bgcolor: "rgba(0,0,0,0.06)",
                                                      color: "text.secondary",
                                                      border: "1px solid rgba(0,0,0,0.15)",
                                                    }}
                                                  />
                                                )}
                                              </Box>
                                              {isMastersZone && (
                                                <Chip
                                                  icon={
                                                    <MastersIcon
                                                      sx={{
                                                        fontSize:
                                                          "0.85rem !important",
                                                      }}
                                                    />
                                                  }
                                                  label="Masters"
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.6rem",
                                                      md: "0.65rem",
                                                    },
                                                    height: { xs: 20, md: 22 },
                                                    bgcolor:
                                                      "rgba(228,148,33, 0.15)",
                                                    color: "#E49421",
                                                    border: "1px solid #E49421",
                                                    fontWeight: 600,
                                                  }}
                                                />
                                              )}
                                              {isRelegationZone && (
                                                <Chip
                                                  label={`${icon} Relegation`}
                                                  color="error"
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.6rem",
                                                      md: "0.65rem",
                                                    },
                                                    height: { xs: 20, md: 22 },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                          <Tooltip
                                            title={
                                              !comparingPlayerId
                                                ? "Compare with another player"
                                                : comparingPlayerId ===
                                                    player.playerId
                                                  ? "Cancel selection"
                                                  : "Click to compare"
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              color={
                                                comparingPlayerId ===
                                                player.playerId
                                                  ? "primary"
                                                  : "default"
                                              }
                                              onClick={(e) =>
                                                handleCompareClick(
                                                  e,
                                                  player.playerId,
                                                  player.playerName,
                                                )
                                              }
                                              sx={{
                                                backgroundColor:
                                                  comparingPlayerId ===
                                                  player.playerId
                                                    ? "primary.light"
                                                    : "transparent",
                                                ml: 0.5,
                                              }}
                                            >
                                              <CompareIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                </Grow>
                              );
                            })}
                          </Grid>
                        )
                      );
                    })()}
                  </Box>
                ) : loading ? (
                  <Box sx={{ mt: 2 }}>
                    {[...Array(8)].map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="40%" height={20} />
                          <Skeleton variant="text" width="25%" height={16} />
                        </Box>
                        <Skeleton variant="rounded" width={48} height={22} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <TrophyIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No rankings available for {currentDivision} yet. Stay
                      tuned for exciting matches!
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* ── Ranking explanation dialog ─────────────────────────────── */}
            {!isTournamentOrg && (() => {
              // Pick up to 3 representative players (with games played) for the examples
              const activePlayers = displayedRankings.filter(
                (p) => p.gamesPlayed > 0 && !p.isGuestPlayer,
              );
              const topPlayer = activePlayers[0];
              const midPlayer =
                activePlayers[Math.floor(activePlayers.length / 2)];
              const bottomPlayer = activePlayers[activePlayers.length - 1];
              const examplePlayers = [topPlayer, midPlayer, bottomPlayer]
                .filter(Boolean)
                .filter(
                  (p, i, arr) =>
                    arr.findIndex((x) => x.playerId === p.playerId) === i,
                );

              const maxPts = Math.max(...activePlayers.map((p) => p.totalPoints));
              const minPts = Math.max(
                Math.min(...activePlayers.map((p) => p.totalPoints)),
                5,
              );
              const ptsRange = maxPts - minPts || 1;

              return (
                <Dialog
                  open={rankingInfoOpen}
                  onClose={() => setRankingInfoOpen(false)}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h6" fontWeight={700}>
                        How Rankings Work
                      </Typography>
                      <IconButton size="small" onClick={() => setRankingInfoOpen(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {currentDivision} · this season
                    </Typography>
                  </DialogTitle>

                  <DialogContent dividers>
                    {/* Step 1 */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        1 · Volume score — how many games have you won?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Every game you win earns +1 point. Badges (e.g. Clean Sweep) add bonus points.
                        Your total is then scaled to a 0–100 score using min-max normalization across everyone in {currentDivision}:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ my: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                        Volume = (your points − lowest) ÷ (highest − lowest) × 100
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The player with the most points scores 100 and the one with the fewest scores 0 — but the gaps between players reflect the actual point differences, not just their rank position.
                      </Typography>
                    </Box>

                    {/* Step 2 */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        2 · Quality score — how consistent is your win rate?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We use a <em>stabilised</em> win percentage. Early in the season (few games),
                        your rate is nudged toward 50 % so a single lucky day doesn't rocket you to #1.
                        As you play more, your true form takes over. Your stabilised win % is used directly
                        as your quality score — so a 55 % win rate gives a quality score of 55, regardless
                        of what others in {currentDivision} are doing.
                      </Typography>
                    </Box>

                    {/* Step 3 */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        3 · Final score = (Vol. × 0.4) + (Qual. × 0.6)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The two scores are combined into one number (0–100).
                        This <strong>final score</strong> is what determines the ranking order — higher score = higher rank.
                        Consistency (win rate) carries slightly more weight than raw wins.
                      </Typography>
                      {examplePlayers.length > 0 && (() => {
                        const p = examplePlayers[0];
                        const vol = ptsRange > 0
                          ? Math.max(0, Math.round(((p.totalPoints - minPts) / ptsRange) * 100))
                          : 50;
                        const bwr = p.bayesianWinRate ?? p.winPercentage;
                        const qual = Math.round(bwr);
                        const score = Math.round(vol * 0.4 + qual * 0.6);
                        return (
                          <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1, fontFamily: "monospace" }}>
                            <Typography variant="caption" sx={{ fontFamily: "monospace", display: "block", color: "text.secondary" }}>
                              Example — {p.playerName}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: "monospace", display: "block" }}>
                              ({vol} × 0.4) + ({qual} × 0.6) = {Math.round(vol * 0.4)} + {Math.round(qual * 0.6)} = <strong>{score}</strong>
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Players with 0 games */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        4 · Players who haven't played yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Players with <strong>0 games played</strong> are excluded from the score
                        calculation entirely — they don't affect the 0–100 volume scale, and they are
                        not assigned a ranking score. They show a neutral "No games played" badge and
                        are not evaluated for promotion or relegation.
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Live examples */}
                    {examplePlayers.length > 0 && (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          Live examples from {currentDivision}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                          Players are sorted by <strong>Score</strong> — the higher the score, the higher the rank.
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Player</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Pts</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Vol.</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Win%*</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Qual.</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Score 🏆</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {examplePlayers.map((p) => {
                                const vol = ptsRange > 0
                                  ? Math.max(0, Math.round(((p.totalPoints - minPts) / ptsRange) * 100))
                                  : 50;
                                const bwr = p.bayesianWinRate ?? p.winPercentage;
                                const qual = Math.round(bwr);
                                const score = Math.round(vol * 0.4 + qual * 0.6);
                                return (
                                  <TableRow key={p.playerId}>
                                    <TableCell sx={{ fontSize: "0.75rem" }}>
                                      {p.playerName}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: "0.75rem" }}>{p.totalPoints}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: "0.75rem" }}>{vol}/100</TableCell>
                                    <TableCell align="right" sx={{ fontSize: "0.75rem" }}>{bwr.toFixed(1)}%</TableCell>
                                    <TableCell align="right" sx={{ fontSize: "0.75rem" }}>{qual}%</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{score}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          * Stabilised win % (adjusted for games played so early results don't skew the ranking). Vol. is 0–100 within this division; Qual. is your stabilised win % used directly (0–100).
                        </Typography>
                      </>
                    )}
                  </DialogContent>

                  <DialogActions>
                    <Button onClick={() => setRankingInfoOpen(false)} size="small">
                      Got it
                    </Button>
                  </DialogActions>
                </Dialog>
              );
            })()}

            {/* Road to Masters Tab - All Levels Combined (Padel Eagle only) */}
            {isPadelEagle && (
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ minHeight: "600px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <MastersIcon sx={{ fontSize: "2rem", color: "#E49421" }} />
                    <Typography
                      variant="h4"
                      sx={{
                        textAlign: "center",
                        background:
                          "#743444",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontWeight: "bold",
                      }}
                    >
                      Road to Masters
                    </Typography>
                    <MastersIcon sx={{ fontSize: "2rem", color: "#E49421" }} />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      mb: 2,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    Track your journey to the end-of-year Masters tournament
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      mb: 3,
                      color: "text.secondary",
                    }}
                  >
                    Combined rankings across all levels
                  </Typography>

                  {/* Same ranking display as Level Specific, but with all divisions data */}
                  {roadToMastersLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : roadToMastersRankings.length > 0 ? (
                    <Box>
                      {/* Display all players in grid format */}
                      <Grid
                        container
                        spacing={{ xs: 1, sm: 2 }}
                        key="road-to-masters-all"
                      >
                        {roadToMastersRankings.map((player, index) => {
                          const actualPosition = index + 1;
                          const isCurrentPlayer =
                            playerUser?.playerId === player.playerId;

                          // Masters qualification zone (top 48)
                          const isMastersZone = actualPosition <= 48;

                          return (
                            <Grow
                              in
                              timeout={800 + index * 50}
                              key={`masters-${player.playerId}`}
                            >
                              <Grid item xs={12} sm={6} md={4}>
                                <Card
                                  sx={{
                                    backgroundColor: isCurrentPlayer
                                      ? "rgba(102, 126, 234, 0.1)"
                                      : isMastersZone
                                        ? "rgba(228,148,33, 0.08)"
                                        : "background.paper",
                                    border: isCurrentPlayer
                                      ? "2px solid"
                                      : isMastersZone
                                        ? "2px solid"
                                        : "1px solid",
                                    borderColor: isCurrentPlayer
                                      ? "#667eea"
                                      : isMastersZone
                                        ? "#E49421"
                                        : "divider",
                                    borderRadius: { xs: 1.5, md: 2 },
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    boxShadow: isCurrentPlayer
                                      ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                                      : isMastersZone
                                        ? "0 2px 8px rgba(228,148,33, 0.2)"
                                        : "none",
                                    "&:hover": {
                                      transform: {
                                        xs: "none",
                                        md: "translateY(-4px)",
                                      },
                                      boxShadow: isCurrentPlayer
                                        ? "0 8px 20px rgba(102, 126, 234, 0.4)"
                                        : isMastersZone
                                          ? "0 6px 16px rgba(228,148,33, 0.3)"
                                          : "0 6px 16px rgba(25, 118, 210, 0.15)",
                                    },
                                  }}
                                  onClick={() =>
                                    handlePlayerClick(player.playerId)
                                  }
                                >
                                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: { xs: 1, md: 1.5 },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: isMastersZone
                                              ? "#E49421"
                                              : PE_BLUE,
                                            fontSize: {
                                              xs: "1rem",
                                              md: "1.1rem",
                                            },
                                            minWidth: "fit-content",
                                          }}
                                        >
                                          #{actualPosition}
                                        </Typography>
                                        {player.rankChange !== undefined &&
                                          player.rankChange !== 0 && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.25,
                                                animation:
                                                  "slideIn 0.5s ease-out",
                                                "@keyframes slideIn": {
                                                  from: {
                                                    opacity: 0,
                                                    transform:
                                                      "translateY(10px)",
                                                  },
                                                  to: {
                                                    opacity: 1,
                                                    transform: "translateY(0)",
                                                  },
                                                },
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: {
                                                    xs: "1.1rem",
                                                    md: "1.3rem",
                                                  },
                                                  color:
                                                    player.rankChange > 0
                                                      ? "#4caf50"
                                                      : "#f44336",
                                                  lineHeight: 1,
                                                }}
                                              >
                                                {player.rankChange > 0
                                                  ? "⬆️"
                                                  : "⬇️"}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: "bold",
                                                  color:
                                                    player.rankChange > 0
                                                      ? "#4caf50"
                                                      : "#f44336",
                                                  fontSize: {
                                                    xs: "0.7rem",
                                                    md: "0.75rem",
                                                  },
                                                }}
                                              >
                                                {Math.abs(player.rankChange)}
                                              </Typography>
                                            </Box>
                                          )}
                                      </Box>
                                      <SafeAvatar
                                        src={player.profilePicture}
                                        alt={player.playerName}
                                        fallbackText={player.playerName}
                                        sx={{
                                          width: { xs: 32, md: 36 },
                                          height: { xs: 32, md: 36 },
                                          filter: player.privacyPolicyAccepted
                                            ? "none"
                                            : "blur(20px)",
                                        }}
                                      />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              fontWeight: "bold",
                                              color: isMastersZone
                                                ? "#E49421"
                                                : isPadelEagle
                                                  ? "#743444"
                                                  : PE_BLUE,
                                              fontSize: {
                                                xs: "0.85rem",
                                                md: "0.95rem",
                                              },
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              width: "100%",
                                            }}
                                          >
                                            {player.playerName}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              gap: 0.5,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {player.division && (
                                              <Chip
                                                label={player.division}
                                                size="small"
                                                sx={{
                                                  height: "18px",
                                                  fontSize: "0.65rem",
                                                  backgroundColor:
                                                    "rgba(228,148,33, 0.15)",
                                                  color: "#E49421",
                                                  border:
                                                    "1px solid rgba(228,148,33, 0.3)",
                                                }}
                                              />
                                            )}
                                            {player.isGuest && (
                                              <Chip
                                                label="Guest"
                                                size="small"
                                                sx={{
                                                  height: "18px",
                                                  fontSize: "0.65rem",
                                                  backgroundColor:
                                                    "rgba(255, 152, 0, 0.15)",
                                                  color: "#ff9800",
                                                  border:
                                                    "1px solid rgba(255, 152, 0, 0.3)",
                                                }}
                                              />
                                            )}
                                            {player.isManagementTeam && (
                                              <Chip
                                                label={isPadelEagle ? "Padel Eagle Team" : "SP Team"}
                                                size="small"
                                                sx={{
                                                  height: "18px",
                                                  fontSize: "0.65rem",
                                                  backgroundColor:
                                                    "rgba(25, 118, 210, 0.15)",
                                                  color: PE_BLUE,
                                                  border:
                                                    "1px solid rgba(25, 118, 210, 0.3)",
                                                  fontWeight: "bold",
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.5,
                                            mt: 0.5,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {!isTournamentOrg && player.hybridScore != null && player.gamesPlayed > 0 && (
                                            <Chip
                                              label={`${(player.hybridScore).toFixed(1)} Score`}
                                              size="small"
                                              sx={{
                                                fontSize: { xs: "0.62rem", md: "0.67rem" },
                                                height: { xs: 22, md: 24 },
                                                bgcolor: "rgba(25, 118, 210, 0.12)",
                                                color: PE_BLUE,
                                                border: "1px solid rgba(25, 118, 210, 0.4)",
                                                fontWeight: 700,
                                              }}
                                            />
                                          )}
                                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                            <Chip
                                              label={`${player.totalTournamentPoints || 0}pts`}
                                              size="small"
                                              sx={{
                                                fontSize: { xs: "0.6rem", md: "0.65rem" },
                                                height: { xs: 20, md: 22 },
                                                bgcolor: "rgba(0,0,0,0.06)",
                                                color: "text.secondary",
                                                border: "1px solid rgba(0,0,0,0.15)",
                                              }}
                                            />
                                            {!isTournamentOrg && player.gamesPlayed > 0 && (
                                              <Chip
                                                label={`${(player.winPercentage ?? 0).toFixed(1)}% WR`}
                                                size="small"
                                                sx={{
                                                  fontSize: { xs: "0.6rem", md: "0.65rem" },
                                                  height: { xs: 20, md: 22 },
                                                  bgcolor: "rgba(0,0,0,0.06)",
                                                  color: "text.secondary",
                                                  border: "1px solid rgba(0,0,0,0.15)",
                                                }}
                                              />
                                            )}
                                          </Box>
                                          {isMastersZone && (
                                            <Chip
                                              icon={
                                                <MastersIcon
                                                  sx={{
                                                    fontSize:
                                                      "0.85rem !important",
                                                  }}
                                                />
                                              }
                                              label="Qualified"
                                              size="small"
                                              sx={{
                                                fontSize: {
                                                  xs: "0.6rem",
                                                  md: "0.65rem",
                                                },
                                                height: { xs: 20, md: 22 },
                                                bgcolor:
                                                  "rgba(228,148,33, 0.15)",
                                                color: "#E49421",
                                                border: "1px solid #E49421",
                                                fontWeight: 600,
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                      <Tooltip
                                        title={
                                          !comparingPlayerId
                                            ? "Compare with another player"
                                            : comparingPlayerId ===
                                                player.playerId
                                              ? "Cancel selection"
                                              : "Click to compare"
                                        }
                                      >
                                        <IconButton
                                          size="small"
                                          color={
                                            comparingPlayerId ===
                                            player.playerId
                                              ? "primary"
                                              : "default"
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCompareClick(
                                              e,
                                              player.playerId,
                                              player.playerName,
                                            );
                                          }}
                                          sx={{
                                            backgroundColor:
                                              comparingPlayerId ===
                                              player.playerId
                                                ? "primary.light"
                                                : "transparent",
                                            ml: 0.5,
                                          }}
                                        >
                                          <CompareIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grow>
                          );
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                      }}
                    >
                      <Typography variant="h6" color="text.secondary">
                        No rankings available yet. Stay tuned for exciting
                        tournaments!
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            )}

            {/* League Days Tab */}
            <TabPanel value={tabValue} index={isPadelEagle ? 2 : 1}>
              <Box sx={{ minHeight: { xs: "400px", md: "600px" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: { xs: 2.5, md: 3.5 },
                    pb: 1.5,
                    borderBottom: "2px solid",
                    borderBottomColor: "#4caf50",
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: { xs: 28, md: 36 },
                      borderRadius: 1,
                      background: (isPadelEagle ? "#743444" : "linear-gradient(180deg, #4caf50 0%, #81c784 100%)"),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                      fontWeight: 800,
                      fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.6rem" },
                      background: "linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    📅 {eventsLabel} History
                  </Typography>
                </Box>

                {leagueDays.length > 0 ? (
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    {[...leagueDays]
                      .sort((a, b) => {
                        // Sort by date in descending order (newest first) for display
                        const dateA = new Date(a.date);
                        const dateB = new Date(b.date);
                        return dateB.getTime() - dateA.getTime();
                      })
                      .map((leagueDay, index) => {
                        // Calculate chronological day number (oldest = 1, newest = n)
                        const dayNumber = leagueDays.length - index;

                        return (
                          <Grow
                            in
                            timeout={500 + index * 100}
                            key={leagueDay.id}
                          >
                            <Grid item xs={12} sm={6} md={6}>
                              <Card
                                sx={{
                                  backgroundColor: leagueDay.isCompleted
                                    ? "success.light"
                                    : "warning.light",
                                  border: leagueDay.isCompleted
                                    ? "2px solid"
                                    : "2px solid",
                                  borderColor: leagueDay.isCompleted
                                    ? "success.main"
                                    : "warning.main",
                                  borderRadius: { xs: 2, md: 3 },
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform: {
                                      xs: "none",
                                      md: "translateY(-5px)",
                                    },
                                    boxShadow: leagueDay.isCompleted
                                      ? "0 10px 25px rgba(76, 175, 80, 0.3)"
                                      : "0 10px 25px rgba(255, 152, 0, 0.3)",
                                  },
                                  "&:active": {
                                    transform: {
                                      xs: "scale(0.98)",
                                      md: "translateY(-5px)",
                                    },
                                  },
                                }}
                                onClick={() => handleLeagueDayClick(leagueDay)}
                              >
                                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: { xs: 1.5, md: 2 },
                                      mb: 2,
                                      flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                      },
                                      textAlign: { xs: "center", sm: "left" },
                                    }}
                                  >
                                    <EventIcon
                                      sx={{
                                        color: leagueDay.isCompleted
                                          ? "#4caf50"
                                          : "#ff9800",
                                        fontSize: { xs: 24, md: 28 },
                                      }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        color: leagueDay.isCompleted
                                          ? "#2e7d32"
                                          : "#e65100",
                                        fontSize: { xs: "1rem", md: "1.25rem" },
                                      }}
                                    >
                                      {eventLabel} #{dayNumber}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mb: 2,
                                      fontSize: {
                                        xs: "0.875rem",
                                        md: "0.875rem",
                                      },
                                      textAlign: { xs: "center", sm: "left" },
                                    }}
                                  >
                                    📅{" "}
                                    {formatDateTime(
                                      leagueDay.date,
                                      leagueDay.time,
                                    )}
                                  </Typography>
                                  <VenueBadge
                                    venueName={leagueDay.venueName}
                                    venueAddress={leagueDay.venueAddress}
                                    variant="body2"
                                  />
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      flexWrap: "wrap",
                                      justifyContent: {
                                        xs: "center",
                                        sm: "flex-start",
                                      },
                                    }}
                                  >
                                    <Chip
                                      label={`🎾 ${
                                        leagueDay.selectedPlayers?.length || 0
                                      } Players`}
                                      color="success"
                                      size="small"
                                      sx={{
                                        fontSize: {
                                          xs: "0.75rem",
                                          md: "0.8125rem",
                                        },
                                      }}
                                    />
                                    {leagueDay.isCompleted ? (
                                      <Chip
                                        label="🏁 Completed"
                                        color="primary"
                                        size="small"
                                        sx={{
                                          fontSize: {
                                            xs: "0.75rem",
                                            md: "0.8125rem",
                                          },
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        label="⏳ In Progress"
                                        color="warning"
                                        size="small"
                                        sx={{
                                          fontSize: {
                                            xs: "0.75rem",
                                            md: "0.8125rem",
                                          },
                                        }}
                                      />
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grow>
                        );
                      })}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: { xs: 4, md: 8 } }}>
                    <EventIcon
                      sx={{
                        fontSize: { xs: 60, md: 80 },
                        color: "#ccc",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "1rem", md: "1.25rem" },
                        px: { xs: 2, md: 0 },
                      }}
                    >
                      No {eventsLabel.toLowerCase()} yet for {currentDivision}.
                      The excitement is about to begin!
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Upcoming Matches Tab */}
            <TabPanel value={tabValue} index={isPadelEagle ? 3 : 2}>
              <Box sx={{ minHeight: { xs: "400px", md: "600px" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: { xs: 2.5, md: 3.5 },
                    pb: 1.5,
                    borderBottom: "2px solid",
                    borderBottomColor: "#ff6b35",
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: { xs: 28, md: 36 },
                      borderRadius: 1,
                      background: "linear-gradient(180deg, #ff6b35 0%, #ff8a65 100%)",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                      fontWeight: 800,
                      fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.6rem" },
                      background: "linear-gradient(45deg, #e64a19 30%, #ff8a65 90%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    🎾 Upcoming Matches
                  </Typography>
                </Box>

                {/* Login prompt for non-logged-in visitors */}
                {!playerUser && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      "& .MuiAlert-message": { width: "100%" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Want to register for an upcoming league day?{" "}
                        <strong>Log in with your player account</strong> and the registration button will appear here.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        href="/player/login"
                        sx={{ flexShrink: 0, fontWeight: 700 }}
                      >
                        Player Login
                      </Button>
                    </Box>
                  </Alert>
                )}

                {upcomingLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <Typography>Loading upcoming matches...</Typography>
                  </Box>
                ) : Object.keys(upcomingMatches).length > 0 ? (
                  <Box>
                    {Object.entries(upcomingMatches)
                      .sort(
                        ([, a], [, b]) =>
                          toJsDate(a.leagueDay.date).getTime() -
                          toJsDate(b.leagueDay.date).getTime(),
                      )
                      .map(([leagueDayId, { leagueDay, matches }]) => (
                        <Box key={leagueDayId} sx={{ mb: 4 }}>
                          {/* League Day Header */}
                          <Paper
                            sx={{
                              p: { xs: 2, md: 3 },
                              mb: 2,
                              backgroundColor:
                                toJsDate(leagueDay.date) < new Date()
                                  ? "error.light"
                                  : "success.light",
                              borderLeft:
                                "6px solid",
                              borderLeftColor:
                                toJsDate(leagueDay.date) < new Date()
                                  ? "error.main"
                                  : "success.main",
                              borderRadius: { xs: 1, md: 2 },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: "bold",
                                    color:
                                      toJsDate(leagueDay.date) < new Date()
                                        ? "#c62828"
                                        : "#2e7d32",
                                    fontSize: { xs: "1rem", md: "1.25rem" },
                                  }}
                                >
                                  {formatDateTime(
                                    leagueDay.date,
                                    leagueDay.time,
                                  )}
                                </Typography>
                                <VenueBadge
                                  venueName={leagueDay.venueName}
                                  venueAddress={leagueDay.venueAddress}
                                  variant="body2"
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      toJsDate(leagueDay.date) < new Date()
                                        ? "#d32f2f"
                                        : "#388e3c",
                                    fontSize: { xs: "0.8rem", md: "0.875rem" },
                                  }}
                                >
                                  {matches.length > 0
                                    ? `${matches.length} pending match${matches.length !== 1 ? "es" : ""}${toJsDate(leagueDay.date) < new Date() ? " (Overdue)" : ""}`
                                    : leagueDay.registrationOpen
                                    ? "Registration open — matches not yet scheduled"
                                    : "No pending matches"}
                                </Typography>
                              </Box>
                              {toJsDate(leagueDay.date) < new Date() && (
                                <Chip
                                  label="Overdue"
                                  color="error"
                                  size="small"
                                  sx={{
                                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                                  }}
                                />
                              )}
                              {/* Register button / status — shown for any league day when logged in */}
                              {playerUser?.playerId && (() => {
                                const myPlayerId = playerUser.playerId as string;
                                const isSelected = leagueDay.selectedPlayers?.includes(myPlayerId);
                                const isWaitlist = leagueDay.waitlistPlayerIds?.includes(myPlayerId);
                                if (isSelected) {
                                  return (
                                    <Chip
                                      label="✓ Registered"
                                      color="success"
                                      variant="filled"
                                      size="small"
                                      sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                                    />
                                  );
                                }
                                if (isWaitlist) {
                                  return (
                                    <Chip
                                      label="On Waitlist"
                                      color="warning"
                                      variant="filled"
                                      size="small"
                                      sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                                    />
                                  );
                                }
                                // Only show Register button for future open league days the player is eligible for
                                if (!leagueDay.registrationOpen || toJsDate(leagueDay.date) < new Date()) {
                                  return null;
                                }
                                if (!eligibleLeagueDayIds.has(leagueDayId)) {
                                  return null;
                                }
                                return (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="success"
                                    disabled={registeringLeagueDayId === leagueDayId}
                                    onClick={() => handleRegisterFromUpcoming(leagueDayId)}
                                    sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.8rem" } }}
                                  >
                                    {registeringLeagueDayId === leagueDayId ? "Registering…" : "Register"}
                                  </Button>
                                );
                              })()}
                            </Box>

                            {/* Player list — only for days with no matches yet */}
                            {matches.length === 0 && leagueDay.selectedPlayers?.length > 0 && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#2e7d32", mb: 1, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                                  Registered ({leagueDay.selectedPlayers.length}):
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                  {(leagueDay.selectedPlayerNames && leagueDay.selectedPlayerNames.length > 0
                                    ? leagueDay.selectedPlayerNames
                                    : leagueDay.selectedPlayers
                                  ).map((name: string, i: number) => (
                                    <Chip key={i} label={name} size="small" sx={{ bgcolor: "rgba(255,255,255,0.7)", color: "#2e7d32", border: "1px solid #a5d6a7", fontSize: { xs: "0.7rem", md: "0.75rem" } }} />
                                  ))}
                                </Box>
                                {leagueDay.waitlistPlayerIds && leagueDay.waitlistPlayerIds.length > 0 && (
                                  <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#e65100", mb: 1, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                                      Waitlist ({leagueDay.waitlistPlayerIds.length}):
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                      {(leagueDay.waitlistPlayerNames && leagueDay.waitlistPlayerNames.length > 0
                                        ? leagueDay.waitlistPlayerNames
                                        : leagueDay.waitlistPlayerIds
                                      ).map((name: string, i: number) => (
                                        <Chip key={i} label={name} size="small" sx={{ bgcolor: "rgba(255,255,255,0.7)", color: "#e65100", border: "1px solid #ffcc80", fontSize: { xs: "0.7rem", md: "0.75rem" } }} />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Paper>
                          {/* Per-leagueDay error message */}
                          {registerError?.leagueDayId === leagueDayId && (
                            <Alert
                              severity={registerError.isBalance ? "warning" : "error"}
                              onClose={() => setRegisterError(null)}
                              sx={{ mb: 2, borderRadius: 2 }}
                            >
                              {registerError.message}
                            </Alert>
                          )}

                          {/* Matches Grouped by Round */}
                          <Box>
                            {/* Group matches by round */}
                            {Object.entries(
                              matches.reduce((acc: any, match: any) => {
                                const roundNum = match.roundNumber;
                                if (!acc[roundNum]) {
                                  acc[roundNum] = [];
                                }
                                acc[roundNum].push(match);
                                return acc;
                              }, {}),
                            )
                              .sort(([a], [b]) => Number(a) - Number(b))
                              .map(
                                ([roundNumber, roundMatches]: [
                                  string,
                                  any,
                                ]) => (
                                  <Accordion
                                    key={roundNumber}
                                    sx={{
                                      mb: 2,
                                      borderRadius: 2,
                                      "&:before": {
                                        display: "none",
                                      },
                                      boxShadow: isPadelEagle ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      sx={{
                                        background:
                                          (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"),
                                        color: "white",
                                        borderRadius: "8px 8px 0 0",
                                        "&:hover": {
                                          background:
                                            (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"),
                                        },
                                        "& .MuiAccordionSummary-expandIconWrapper":
                                          {
                                            color: "white",
                                          },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: "bold",
                                            fontSize: {
                                              xs: "1rem",
                                              md: "1.125rem",
                                            },
                                          }}
                                        >
                                          Round {roundNumber}
                                        </Typography>
                                        <Chip
                                          label={`${roundMatches.length} match${
                                            roundMatches.length !== 1
                                              ? "es"
                                              : ""
                                          }`}
                                          size="small"
                                          sx={{
                                            backgroundColor:
                                              "rgba(255,255,255,0.2)",
                                            color: "white",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails
                                      sx={{
                                        p: 0,
                                        backgroundColor: "action.hover",
                                      }}
                                    >
                                      {/* Mobile: stacked match cards */}
                                      <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1, p: 1 }}>
                                        {roundMatches
                                          .sort((a: any, b: any) => a.courtNumber - b.courtNumber)
                                          .map((match: any) => (
                                            <Box
                                              key={match.id}
                                              sx={{
                                                borderRadius: 1.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Box sx={{ px: 1.5, py: 0.75, bgcolor: "action.selected", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Chip label={`Court ${match.courtNumber}`} color="secondary" size="small" sx={{ fontWeight: "bold", fontSize: "0.7rem" }} />
                                              </Box>
                                              <Box sx={{ p: 1.25 }}>
                                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#e3f2fd", mb: 0.75 }}>
                                                  <Typography sx={{ fontWeight: "bold", color: PE_BLUE, fontSize: "0.82rem" }}>
                                                    {match.pair1?.player1Name} & {match.pair1?.player2Name}
                                                  </Typography>
                                                </Box>
                                                <Typography sx={{ textAlign: "center", fontWeight: "bold", color: "#666", fontSize: "0.75rem", my: 0.25 }}>VS</Typography>
                                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: "rgba(123,31,162,0.07)" }}>
                                                  <Typography sx={{ fontWeight: "bold", color: "#7b1fa2", fontSize: "0.82rem" }}>
                                                    {match.pair2?.player1Name} & {match.pair2?.player2Name}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          ))}
                                      </Box>
                                      {/* Desktop: table */}
                                      <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
                                        <Table>
                                          <TableHead>
                                            <TableRow
                                              sx={{
                                                backgroundColor:
                                                  "action.selected",
                                              }}
                                            >
                                              <TableCell
                                                sx={{
                                                  fontWeight: "bold",
                                                  width: "80px",
                                                }}
                                              >
                                                Court
                                              </TableCell>
                                              <TableCell
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Team 1
                                              </TableCell>
                                              <TableCell
                                                align="center"
                                                sx={{
                                                  fontWeight: "bold",
                                                  width: "60px",
                                                }}
                                              >
                                                VS
                                              </TableCell>
                                              <TableCell
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Team 2
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {roundMatches
                                              .sort(
                                                (a: any, b: any) =>
                                                  a.courtNumber - b.courtNumber,
                                              )
                                              .map((match: any) => (
                                                <TableRow
                                                  key={match.id}
                                                  sx={{
                                                    "&:nth-of-type(odd)": {
                                                      backgroundColor:
                                                        "action.hover",
                                                    },
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "action.selected",
                                                    },
                                                  }}
                                                >
                                                  <TableCell>
                                                    <Chip
                                                      label={`Court ${match.courtNumber}`}
                                                      color="secondary"
                                                      size="small"
                                                      sx={{
                                                        fontWeight: "bold",
                                                      }}
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        backgroundColor:
                                                          "#e3f2fd",
                                                      }}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontWeight: "bold",
                                                          color: PE_BLUE,
                                                          fontSize: {
                                                            xs: "0.875rem",
                                                            md: "1rem",
                                                          },
                                                        }}
                                                      >
                                                        {
                                                          match.pair1
                                                            ?.player1Name
                                                        }{" "}
                                                        &{" "}
                                                        {
                                                          match.pair1
                                                            ?.player2Name
                                                        }
                                                      </Typography>
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell
                                                    align="center"
                                                    sx={{
                                                      fontWeight: "bold",
                                                      color: "#666",
                                                    }}
                                                  >
                                                    VS
                                                  </TableCell>
                                                  <TableCell>
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        backgroundColor:
                                                          "action.hover",
                                                      }}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontWeight: "bold",
                                                          color: "#7b1fa2",
                                                          fontSize: {
                                                            xs: "0.875rem",
                                                            md: "1rem",
                                                          },
                                                        }}
                                                      >
                                                        {
                                                          match.pair2
                                                            ?.player1Name
                                                        }{" "}
                                                        &{" "}
                                                        {
                                                          match.pair2
                                                            ?.player2Name
                                                        }
                                                      </Typography>
                                                    </Box>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </AccordionDetails>
                                  </Accordion>
                                ),
                              )}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", p: 4 }}>
                    <PadelIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No upcoming matches in {currentDivision}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All matches have been completed or no league days have
                      been started yet.
                    </Typography>
                  </Box>
                )}

                {/* Draft League Days Section */}
                {draftLeagueDays.length > 0 && (
                  <Box sx={{ mt: 6 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        textAlign: "center",
                        mb: 3,
                        background:
                          "linear-gradient(45deg, #ffa726 30%, #ffb74d 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontWeight: "bold",
                        fontSize: { xs: "1.5rem", md: "1.75rem" },
                        px: { xs: 1, md: 0 },
                      }}
                    >
                      📅 Scheduled {eventsLabel} 📅
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        mb: 3,
                        color: "text.secondary",
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }}
                    >
                      These {eventsLabel.toLowerCase()} are scheduled but
                      matches haven't been created yet
                    </Typography>

                    {!playerUser && (
                      <Alert
                        severity="info"
                        sx={{ mb: 3, borderRadius: 2, "& .MuiAlert-message": { width: "100%" } }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            Want to register?{" "}
                            <strong>Log in with your player account</strong> and the registration button will appear here.
                          </Typography>
                          <Button variant="outlined" size="small" color="info" href="/player/login" sx={{ flexShrink: 0, fontWeight: 700 }}>
                            Player Login
                          </Button>
                        </Box>
                      </Alert>
                    )}

                    {draftLeagueDays
                      .filter((d) => !(d.id in upcomingMatches))
                      .map((draftDay) => (
                      <React.Fragment key={draftDay.id}>
                      <Paper
                        sx={{
                          p: { xs: 2, md: 3 },
                          mb: 3,
                          background:
                            "linear-gradient(135deg, #fff9e6 0%, #ffe4b3 100%)",
                          borderLeft: "6px solid #ffa726",
                          borderRadius: { xs: 1, md: 2 },
                          transition: "all 0.3s",
                          "&:hover": {
                            transform: { xs: "none", md: "translateY(-2px)" },
                            boxShadow: isPadelEagle ? "none" : "0 8px 20px rgba(255,167,38,0.2)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <CalendarIcon
                              sx={{
                                fontSize: { xs: 32, md: 40 },
                                color: "#f57c00",
                              }}
                            />
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "bold",
                                  color: "#e65100",
                                  fontSize: { xs: "1rem", md: "1.25rem" },
                                }}
                              >
                                {(() => {
                                  // Handle Firebase Timestamp
                                  let dateObj;
                                  if (
                                    draftDay.date &&
                                    typeof draftDay.date.toDate === "function"
                                  ) {
                                    dateObj = draftDay.date.toDate();
                                  } else if (
                                    draftDay.date &&
                                    draftDay.date._seconds
                                  ) {
                                    dateObj = new Date(
                                      draftDay.date._seconds * 1000,
                                    );
                                  } else if (draftDay.date) {
                                    dateObj = new Date(draftDay.date);
                                  } else {
                                    return "Invalid Date";
                                  }
                                  return format(dateObj, "EEEE, MMMM d, yyyy");
                                })()}
                              </Typography>
                              {draftDay.time && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#f57c00",
                                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                                  }}
                                >
                                  🕒 {draftDay.time}
                                </Typography>
                              )}
                              <VenueBadge
                                venueName={draftDay.venueName}
                                venueAddress={draftDay.venueAddress}
                                variant="body2"
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#f57c00",
                                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                                }}
                              >
                                Division {draftDay.division}
                              </Typography>
                            </Box>
                          </Box>

                          <Chip
                            label="DRAFT"
                            sx={{
                              bgcolor: "#ff9800",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: { xs: "0.7rem", md: "0.75rem" },
                            }}
                          />
                          {/* Registration status / action for logged-in players */}
                          {playerUser && (() => {
                            const isSelected = draftDay.selectedPlayerIds?.includes(playerUser.playerId);
                            const isWaitlist = draftDay.waitlistPlayerIds?.includes(playerUser.playerId);
                            if (isSelected) {
                              return (
                                <Chip
                                  label="✓ Registered"
                                  color="success"
                                  variant="filled"
                                  size="small"
                                  sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                                />
                              );
                            }
                            if (isWaitlist) {
                              return (
                                <Chip
                                  label="On Waitlist"
                                  color="warning"
                                  variant="filled"
                                  size="small"
                                  sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                                />
                              );
                            }
                            if (draftDay.registrationOpen && eligibleLeagueDayIds.has(draftDay.id)) {
                              return (
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  disabled={registeringLeagueDayId === draftDay.id}
                                  onClick={() => handleRegisterFromUpcoming(draftDay.id)}
                                  sx={{ fontWeight: 700, fontSize: { xs: "0.7rem", md: "0.8rem" } }}
                                >
                                  {registeringLeagueDayId === draftDay.id ? "Registering…" : "Register"}
                                </Button>
                              );
                            }
                            return null;
                          })()}
                        </Box>

                        {/* Registered Players */}
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "#e65100",
                              mb: 1.5,
                              fontSize: { xs: "0.875rem", md: "1rem" },
                            }}
                          >
                            Registered Players ({draftDay.playerCount}):
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {draftDay.selectedPlayers.map(
                              (playerName: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={playerName}
                                  size="small"
                                  sx={{
                                    bgcolor: "background.paper",
                                    color: "#f57c00",
                                    border: "1px solid #ffb74d",
                                    fontSize: { xs: "0.75rem", md: "0.8rem" },
                                  }}
                                />
                              ),
                            )}
                          </Box>
                        </Box>
                      </Paper>
                      {/* Per-draft-day error */}
                      {registerError && registerError.leagueDayId === draftDay.id && (
                        <Alert
                          severity={registerError.isBalance ? "warning" : "error"}
                          onClose={() => setRegisterError(null)}
                          sx={{ mb: 2, borderRadius: 2 }}
                        >
                          {registerError.message}
                        </Alert>
                      )}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Records Tab */}
            <TabPanel value={tabValue} index={isPadelEagle ? 4 : 3}>
              <Box sx={{ minHeight: "600px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: { xs: 2.5, md: 3.5 },
                    pb: 1.5,
                    borderBottom: "2px solid",
                    borderBottomColor: isPadelEagle ? "#E49421" : PE_BLUE,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: { xs: 28, md: 36 },
                      borderRadius: 1,
                      background: isPadelEagle
                        ? "#743444"
                        : (isPadelEagle ? "#743444" : "linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)"),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: isPadelEagle ? PE_BODY_FONT : MONTSERRAT,
                      fontWeight: 800,
                      fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.6rem" },
                      background: isPadelEagle
                        ? "#743444"
                        : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1565c0 30%, #42a5f5 90%)"),
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    📊 Records & Hall of Fame
                  </Typography>
                </Box>

                {String(currentDivision) === "All" ? (
                  <Typography
                    variant="h6"
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    Please select a specific division to view records
                  </Typography>
                ) : records ? (
                  <Grid container spacing={3}>
                    {/* Perfect Days */}
                    {records.perfectDays.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            height: "100%",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            position: "relative",
                            overflow: "visible",
                          }}
                        >
                          {/* Top Player Profile Picture with Animation */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: -30,
                              left: "50%",
                              transform: "translateX(-50%)",
                              zIndex: 10,
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            {(() => {
                              const topScore =
                                records.perfectDays[0]?.perfectDaysCount;
                              const topPlayers = records.perfectDays
                                .filter((p) => p.perfectDaysCount === topScore)
                                .slice(0, 3);
                              return topPlayers.map((player, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    position: "relative",
                                    "@keyframes float": {
                                      "0%, 100%": {
                                        transform: "translateY(0px)",
                                      },
                                      "50%": { transform: "translateY(-10px)" },
                                    },
                                    animation: `float ${3 + idx * 0.5}s ease-in-out infinite`,
                                  }}
                                >
                                  <SafeAvatar
                                    src={player?.profilePicture}
                                    alt={player?.playerName || "Player"}
                                    fallbackText={
                                      player?.playerName || "Player"
                                    }
                                    sx={{
                                      width: topPlayers.length > 1 ? 60 : 80,
                                      height: topPlayers.length > 1 ? 60 : 80,
                                      border: "4px solid white",
                                      boxShadow: isPadelEagle ? "none" : "0 4px 20px rgba(0,0,0,0.3)",
                                      filter: player?.privacyPolicyAccepted
                                        ? "none"
                                        : "blur(20px)",
                                    }}
                                  />
                                  {topPlayers.length === 1 && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: -10,
                                        right: -10,
                                        width: 25,
                                        height: 25,
                                        borderRadius: "50%",
                                        background:
                                          "radial-gradient(circle at 30% 30%, #c8ff00, #76b900)",
                                        boxShadow:
                                          "0 2px 10px rgba(118, 185, 0, 0.6)",
                                        "@keyframes spin": {
                                          "0%": { transform: "rotate(0deg)" },
                                          "100%": {
                                            transform: "rotate(360deg)",
                                          },
                                        },
                                        animation: "spin 2s linear infinite",
                                      }}
                                    />
                                  )}
                                </Box>
                              ));
                            })()}
                          </Box>
                          <CardContent sx={{ pt: 6 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                mb: 2,
                                color: "white",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              🌟 Perfect League Days
                            </Typography>
                            {(() => {
                              let currentRank = 0;
                              let previousScore: number | null | undefined =
                                null;
                              return records.perfectDays
                                .slice(0, 5)
                                .map((record, idx) => {
                                  if (
                                    record.perfectDaysCount !== previousScore
                                  ) {
                                    currentRank = currentRank + 1;
                                  }
                                  previousScore = record.perfectDaysCount;
                                  return (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Chip
                                        label={`#${currentRank}`}
                                        size="small"
                                        sx={{
                                          bgcolor: "rgba(255,255,255,0.3)",
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      />
                                      <SafeAvatar
                                        src={record.profilePicture}
                                        alt={record.playerName || "Player"}
                                        fallbackText={
                                          record.playerName || "Player"
                                        }
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          filter: record.privacyPolicyAccepted
                                            ? "none"
                                            : "blur(20px)",
                                        }}
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "white",
                                          }}
                                        >
                                          {record.playerName}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "rgba(255,255,255,0.9)",
                                          }}
                                        >
                                          {record.perfectDaysCount} perfect days
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                });
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Best Game Difference */}
                    {records.bestGameDifference.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card
                          sx={{
                            height: "100%",
                            background:
                              "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                            position: "relative",
                            overflow: "visible",
                          }}
                        >
                          {/* Top Player Profile Picture with Animation */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: -30,
                              left: "50%",
                              transform: "translateX(-50%)",
                              zIndex: 10,
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            {(() => {
                              const topScore =
                                records.bestGameDifference[0]?.gameDifference;
                              const topPlayers = records.bestGameDifference
                                .filter((p) => p.gameDifference === topScore)
                                .slice(0, 3);
                              return topPlayers.map((player, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    position: "relative",
                                    "@keyframes bounce": {
                                      "0%, 100%": {
                                        transform: "translateY(0px) scale(1)",
                                      },
                                      "50%": {
                                        transform:
                                          "translateY(-15px) scale(1.05)",
                                      },
                                    },
                                    animation: `bounce ${2 + idx * 0.3}s ease-in-out infinite`,
                                  }}
                                >
                                  <SafeAvatar
                                    src={player?.profilePicture}
                                    alt={player?.playerName || "Player"}
                                    fallbackText={
                                      player?.playerName || "Player"
                                    }
                                    sx={{
                                      width: topPlayers.length > 1 ? 60 : 80,
                                      height: topPlayers.length > 1 ? 60 : 80,
                                      border: "4px solid white",
                                      boxShadow: isPadelEagle ? "none" : "0 4px 20px rgba(0,0,0,0.3)",
                                      filter: player?.privacyPolicyAccepted
                                        ? "none"
                                        : "blur(20px)",
                                    }}
                                  />
                                  {topPlayers.length === 1 && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: -10,
                                        right: -10,
                                        width: 25,
                                        height: 25,
                                        borderRadius: "50%",
                                        background:
                                          "radial-gradient(circle at 30% 30%, #c8ff00, #76b900)",
                                        boxShadow:
                                          "0 2px 10px rgba(118, 185, 0, 0.6)",
                                        "@keyframes spin": {
                                          "0%": { transform: "rotate(0deg)" },
                                          "100%": {
                                            transform: "rotate(360deg)",
                                          },
                                        },
                                        animation: "spin 2s linear infinite",
                                      }}
                                    />
                                  )}
                                </Box>
                              ));
                            })()}
                          </Box>
                          <CardContent sx={{ pt: 6 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                mb: 2,
                                color: "white",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              🎯 Best Game Difference in a Day
                            </Typography>
                            {(() => {
                              let currentRank = 0;
                              let previousScore: number | null | undefined =
                                null;
                              return records.bestGameDifference
                                .slice(0, 5)
                                .map((record, idx) => {
                                  if (record.gameDifference !== previousScore) {
                                    currentRank = currentRank + 1;
                                  }
                                  previousScore = record.gameDifference;
                                  return (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Chip
                                        label={`#${currentRank}`}
                                        size="small"
                                        sx={{
                                          bgcolor: "rgba(255,255,255,0.3)",
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      />
                                      <SafeAvatar
                                        src={record.profilePicture}
                                        alt={record.playerName || "Player"}
                                        fallbackText={
                                          record.playerName || "Player"
                                        }
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          filter: record.privacyPolicyAccepted
                                            ? "none"
                                            : "blur(20px)",
                                        }}
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "white",
                                          }}
                                        >
                                          {record.playerName}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "rgba(255,255,255,0.9)",
                                          }}
                                        >
                                          +{record.gameDifference} (
                                          {record.gamesWon}W -{" "}
                                          {record.gamesLost}
                                          L)
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                });
                            })()}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Overall Game Difference */}
                    {records.overallGameDifference &&
                      records.overallGameDifference.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              height: "100%",
                              background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            {/* Top Player Profile Picture with Animation */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: -30,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                display: "flex",
                                gap: 1,
                              }}
                            >
                              {(() => {
                                const topScore =
                                  records.overallGameDifference[0]
                                    ?.gameDifference;
                                const topPlayers = records.overallGameDifference
                                  .filter((p) => p.gameDifference === topScore)
                                  .slice(0, 3);
                                return topPlayers.map((player, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      position: "relative",
                                      "@keyframes float": {
                                        "0%, 100%": {
                                          transform: "translateY(0px)",
                                        },
                                        "50%": {
                                          transform: "translateY(-10px)",
                                        },
                                      },
                                      animation: `float ${3 + idx * 0.3}s ease-in-out infinite`,
                                    }}
                                  >
                                    <SafeAvatar
                                      src={player?.profilePicture}
                                      alt={player?.playerName || "Player"}
                                      fallbackText={
                                        player?.playerName || "Player"
                                      }
                                      sx={{
                                        width: topPlayers.length > 1 ? 60 : 80,
                                        height: topPlayers.length > 1 ? 60 : 80,
                                        border: "4px solid white",
                                        boxShadow: isPadelEagle ? "none" : "0 4px 20px rgba(0,0,0,0.3)",
                                        filter: player?.privacyPolicyAccepted
                                          ? "none"
                                          : "blur(20px)",
                                      }}
                                    />
                                    {topPlayers.length === 1 && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          bottom: -10,
                                          right: -10,
                                          width: 25,
                                          height: 25,
                                          borderRadius: "50%",
                                          background:
                                            "radial-gradient(circle at 30% 30%, #c8ff00, #76b900)",
                                          boxShadow:
                                            "0 2px 10px rgba(118, 185, 0, 0.6)",
                                          "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": {
                                              transform: "rotate(360deg)",
                                            },
                                          },
                                          animation: "spin 2s linear infinite",
                                        }}
                                      />
                                    )}
                                  </Box>
                                ));
                              })()}
                            </Box>
                            <CardContent sx={{ pt: 6 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  color: "white",
                                  fontWeight: "bold",
                                  textAlign: "center",
                                }}
                              >
                                🏆 Best Overall Game Difference
                              </Typography>
                              {(() => {
                                let currentRank = 0;
                                let previousScore: number | null | undefined =
                                  null;
                                return records.overallGameDifference
                                  .slice(0, 5)
                                  .map((record, idx) => {
                                    if (
                                      record.gameDifference !== previousScore
                                    ) {
                                      currentRank = currentRank + 1;
                                    }
                                    previousScore = record.gameDifference;
                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          mb: 2,
                                          p: 1.5,
                                          bgcolor: "rgba(255,255,255,0.2)",
                                          borderRadius: 2,
                                        }}
                                      >
                                        <Chip
                                          label={`#${currentRank}`}
                                          size="small"
                                          sx={{
                                            bgcolor: "rgba(255,255,255,0.3)",
                                            color: "white",
                                            fontWeight: "bold",
                                          }}
                                        />
                                        <SafeAvatar
                                          src={record.profilePicture}
                                          alt={record.playerName || "Player"}
                                          fallbackText={
                                            record.playerName || "Player"
                                          }
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            filter: record.privacyPolicyAccepted
                                              ? "none"
                                              : "blur(20px)",
                                          }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: "bold",
                                              color: "white",
                                            }}
                                          >
                                            {record.playerName}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: "rgba(255,255,255,0.9)",
                                            }}
                                          >
                                            +{record.gameDifference} (
                                            {record.totalGamesWon}W -{" "}
                                            {record.totalGamesLost}L)
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  });
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                    {/* Best Game Win Percentage */}
                    {records.gameWinPercentage &&
                      records.gameWinPercentage.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              height: "100%",
                              background:
                                "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            {/* Top Player Profile Picture with Animation */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: -30,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                display: "flex",
                                gap: 1,
                              }}
                            >
                              {(() => {
                                const topScore =
                                  records.gameWinPercentage[0]?.winPercentage;
                                const topPlayers = records.gameWinPercentage
                                  .filter(
                                    (p) =>
                                      Math.abs(
                                        (p.winPercentage || 0) -
                                          (topScore || 0),
                                      ) < 0.01,
                                  )
                                  .slice(0, 3);
                                return topPlayers.map((player, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      position: "relative",
                                      "@keyframes bounce": {
                                        "0%, 100%": {
                                          transform: "translateY(0px) scale(1)",
                                        },
                                        "50%": {
                                          transform:
                                            "translateY(-15px) scale(1.05)",
                                        },
                                      },
                                      animation: `bounce ${2.5 + idx * 0.3}s ease-in-out infinite`,
                                    }}
                                  >
                                    <SafeAvatar
                                      src={player?.profilePicture}
                                      alt={player?.playerName || "Player"}
                                      fallbackText={
                                        player?.playerName || "Player"
                                      }
                                      sx={{
                                        width: topPlayers.length > 1 ? 60 : 80,
                                        height: topPlayers.length > 1 ? 60 : 80,
                                        border: "4px solid white",
                                        boxShadow: isPadelEagle ? "none" : "0 4px 20px rgba(0,0,0,0.3)",
                                        filter: player?.privacyPolicyAccepted
                                          ? "none"
                                          : "blur(20px)",
                                      }}
                                    />
                                    {topPlayers.length === 1 && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          bottom: -10,
                                          right: -10,
                                          width: 25,
                                          height: 25,
                                          borderRadius: "50%",
                                          background:
                                            "radial-gradient(circle at 30% 30%, #c8ff00, #76b900)",
                                          boxShadow:
                                            "0 2px 10px rgba(118, 185, 0, 0.6)",
                                          "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": {
                                              transform: "rotate(360deg)",
                                            },
                                          },
                                          animation: "spin 2s linear infinite",
                                        }}
                                      />
                                    )}
                                  </Box>
                                ));
                              })()}
                            </Box>
                            <CardContent sx={{ pt: 6 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  color: "white",
                                  fontWeight: "bold",
                                  textAlign: "center",
                                }}
                              >
                                📊 Best Game Win %
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  textAlign: "center",
                                  color: "rgba(255,255,255,0.8)",
                                  mb: 2,
                                }}
                              >
                                Minimum 10 games played
                              </Typography>
                              {(() => {
                                let currentRank = 0;
                                let previousScore: number | null | undefined =
                                  null;
                                return records.gameWinPercentage
                                  .slice(0, 5)
                                  .map((record, idx) => {
                                    if (
                                      Math.abs(
                                        (record.winPercentage || 0) -
                                          (previousScore || 0),
                                      ) > 0.01
                                    ) {
                                      currentRank = currentRank + 1;
                                    }
                                    previousScore = record.winPercentage;
                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          mb: 2,
                                          p: 1.5,
                                          bgcolor: "rgba(255,255,255,0.2)",
                                          borderRadius: 2,
                                        }}
                                      >
                                        <Chip
                                          label={`#${currentRank}`}
                                          size="small"
                                          sx={{
                                            bgcolor: "rgba(255,255,255,0.3)",
                                            color: "white",
                                            fontWeight: "bold",
                                          }}
                                        />
                                        <SafeAvatar
                                          src={record.profilePicture}
                                          alt={record.playerName || "Player"}
                                          fallbackText={
                                            record.playerName || "Player"
                                          }
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            filter: record.privacyPolicyAccepted
                                              ? "none"
                                              : "blur(20px)",
                                          }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: "bold",
                                              color: "white",
                                            }}
                                          >
                                            {record.playerName}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: "rgba(255,255,255,0.9)",
                                            }}
                                          >
                                            {record.winPercentage?.toFixed(1)}%
                                            ({record.totalGamesWon}W -{" "}
                                            {record.totalGamesLost}L)
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  });
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                    {/* Best Match Win Percentage */}
                    {records.matchWinPercentage &&
                      records.matchWinPercentage.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              height: "100%",
                              background:
                                "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            {/* Top Player Profile Picture with Animation */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: -30,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                display: "flex",
                                gap: 1,
                              }}
                            >
                              {(() => {
                                const topScore =
                                  records.matchWinPercentage[0]?.winPercentage;
                                const topPlayers = records.matchWinPercentage
                                  .filter(
                                    (p) =>
                                      Math.abs(
                                        (p.winPercentage || 0) -
                                          (topScore || 0),
                                      ) < 0.01,
                                  )
                                  .slice(0, 3);
                                return topPlayers.map((player, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      position: "relative",
                                      "@keyframes float": {
                                        "0%, 100%": {
                                          transform: "translateY(0px)",
                                        },
                                        "50%": {
                                          transform: "translateY(-10px)",
                                        },
                                      },
                                      animation: `float ${2.8 + idx * 0.3}s ease-in-out infinite`,
                                    }}
                                  >
                                    <SafeAvatar
                                      src={player?.profilePicture}
                                      alt={player?.playerName || "Player"}
                                      fallbackText={
                                        player?.playerName || "Player"
                                      }
                                      sx={{
                                        width: topPlayers.length > 1 ? 60 : 80,
                                        height: topPlayers.length > 1 ? 60 : 80,
                                        border: "4px solid white",
                                        boxShadow: isPadelEagle ? "none" : "0 4px 20px rgba(0,0,0,0.3)",
                                        filter: player?.privacyPolicyAccepted
                                          ? "none"
                                          : "blur(20px)",
                                      }}
                                    />
                                    {topPlayers.length === 1 && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          bottom: -10,
                                          right: -10,
                                          width: 25,
                                          height: 25,
                                          borderRadius: "50%",
                                          background:
                                            "radial-gradient(circle at 30% 30%, #c8ff00, #76b900)",
                                          boxShadow:
                                            "0 2px 10px rgba(118, 185, 0, 0.6)",
                                          "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": {
                                              transform: "rotate(360deg)",
                                            },
                                          },
                                          animation: "spin 2s linear infinite",
                                        }}
                                      />
                                    )}
                                  </Box>
                                ));
                              })()}
                            </Box>
                            <CardContent sx={{ pt: 6 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  color: "white",
                                  fontWeight: "bold",
                                  textAlign: "center",
                                }}
                              >
                                🏅 Best Match Win %
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  textAlign: "center",
                                  color: "rgba(255,255,255,0.8)",
                                  mb: 2,
                                }}
                              >
                                Minimum 5 matches played
                              </Typography>
                              {(() => {
                                let currentRank = 0;
                                let previousScore: number | null | undefined =
                                  null;
                                return records.matchWinPercentage
                                  .slice(0, 5)
                                  .map((record, idx) => {
                                    if (
                                      Math.abs(
                                        (record.winPercentage || 0) -
                                          (previousScore || 0),
                                      ) > 0.01
                                    ) {
                                      currentRank = currentRank + 1;
                                    }
                                    previousScore = record.winPercentage;
                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          mb: 2,
                                          p: 1.5,
                                          bgcolor: "rgba(255,255,255,0.2)",
                                          borderRadius: 2,
                                        }}
                                      >
                                        <Chip
                                          label={`#${currentRank}`}
                                          size="small"
                                          sx={{
                                            bgcolor: "rgba(255,255,255,0.3)",
                                            color: "white",
                                            fontWeight: "bold",
                                          }}
                                        />
                                        <SafeAvatar
                                          src={record.profilePicture}
                                          alt={record.playerName || "Player"}
                                          fallbackText={
                                            record.playerName || "Player"
                                          }
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            filter: record.privacyPolicyAccepted
                                              ? "none"
                                              : "blur(20px)",
                                          }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: "bold",
                                              color: "white",
                                            }}
                                          >
                                            {record.playerName}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: "rgba(255,255,255,0.9)",
                                            }}
                                          >
                                            {record.winPercentage?.toFixed(1)}%
                                            ({record.totalMatchesWon}W -{" "}
                                            {record.totalMatchesLost}L)
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  });
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                    {/* Best Partnerships */}
                    {records.partnerships.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            mb: 1,
                            fontWeight: "bold",
                            color: PE_BLUE,
                          }}
                        >
                          👥 Best Partnership Combos
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            mb: 2,
                            color: "text.secondary",
                          }}
                        >
                          Minimum 3 matches played together
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            overflowX: "auto",
                            pb: 2,
                            "&::-webkit-scrollbar": { height: 8 },
                            "&::-webkit-scrollbar-track": {
                              background: "#f1f1f1",
                              borderRadius: 4,
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "#888",
                              borderRadius: 4,
                            },
                          }}
                        >
                          {records.partnerships
                            .slice(0, 10)
                            .map((partnership, idx) => (
                              <Card
                                key={`${partnership.player1Id}-${partnership.player2Id}`}
                                sx={{
                                  minWidth: 280,
                                  background:
                                    idx === 0
                                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                      : "white",
                                  color: idx === 0 ? "white" : "inherit",
                                  border:
                                    idx === 0 ? "none" : "1px solid #e0e0e0",
                                }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Chip
                                      label={`#${idx + 1}`}
                                      size="small"
                                      sx={{
                                        bgcolor:
                                          idx === 0
                                            ? "rgba(255,255,255,0.2)"
                                            : PE_BLUE,
                                        color: "white",
                                        fontWeight: "bold",
                                      }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        color: idx === 0 ? "white" : "#4caf50",
                                      }}
                                    >
                                      {partnership.winRate.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <SafeAvatar
                                      src={partnership.player1ProfilePicture}
                                      alt={partnership.player1Name}
                                      fallbackText={partnership.player1Name}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        filter:
                                          partnership.player1PrivacyAccepted
                                            ? "none"
                                            : "blur(20px)",
                                      }}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: "bold", flex: 1 }}
                                    >
                                      {partnership.player1Name}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 2,
                                    }}
                                  >
                                    <SafeAvatar
                                      src={partnership.player2ProfilePicture}
                                      alt={partnership.player2Name}
                                      fallbackText={partnership.player2Name}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        filter:
                                          partnership.player2PrivacyAccepted
                                            ? "none"
                                            : "blur(20px)",
                                      }}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: "bold", flex: 1 }}
                                    >
                                      {partnership.player2Name}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Chip
                                      label={`${partnership.gamesPlayed} games`}
                                      size="small"
                                      sx={{
                                        bgcolor:
                                          idx === 0
                                            ? "rgba(255,255,255,0.2)"
                                            : "#e3f2fd",
                                        color: idx === 0 ? "white" : PE_BLUE,
                                      }}
                                    />
                                    <Chip
                                      label={`${partnership.gamesWon}W-${partnership.gamesPlayed - partnership.gamesWon}L`}
                                      size="small"
                                      sx={{
                                        bgcolor:
                                          idx === 0
                                            ? "rgba(255,255,255,0.2)"
                                            : "#e8f5e9",
                                        color: idx === 0 ? "white" : "#4caf50",
                                      }}
                                    />
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography
                    variant="h6"
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    No records available for this division
                  </Typography>
                )}
              </Box>
            </TabPanel>

            {/* Rules Tab (custom-branded orgs only, e.g. RPL) */}
            {hasCustomBranding && (
              <TabPanel value={tabValue} index={4}>
                <Box sx={{ minHeight: "400px", p: { xs: 1, md: 2 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: "bold",
                      color: "#9c27b0",
                      mb: 3,
                    }}
                  >
                    <RulesBookIcon /> How is it played?
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                    {[
                      {
                        color: "#ff9800",
                        title: "Draw & start",
                        body: "At the start, it's drawn who starts on which court — 4 courts for 16 people, 3 courts for 12 people. Partners and opponents are also drawn.",
                      },
                      {
                        color: "#e91e63",
                        title: "Scoring per game",
                        body: "Scored like tennis: 15, 30, 40. A game goes to 4 points. At 40:40 there's one advantage point. If it goes back to deuce after that, the Golden Point decides — whoever wins that point wins the game.\n\nAt the end of the matchday, bonus points for the table are given once depending on which court you finish on: court 1 = 2 bonus points, court 2 = 1 bonus point, court 3 = 0 bonus points.",
                      },
                      {
                        color: "#4caf50",
                        title: "Rotation after each round",
                        body: "The winning pair moves up one court, the losing pair moves down one court. Partners are then split up — you play against your previous partner with a new partner.",
                      },
                      {
                        color: orgPrimary,
                        title: "How a matchday runs",
                        body: "4 rounds are played. In the first 3 rounds each game goes to 4 points, in the 4th and final round to 5 points. The final takes place on court 1 — the winning pair there is the matchday winner.",
                      },
                    ].map((card) => (
                      <Paper
                        key={card.title}
                        elevation={1}
                        sx={{
                          p: 2,
                          borderLeft: `4px solid ${card.color}`,
                          borderRadius: 2,
                        }}
                      >
                        <Typography sx={{ fontWeight: "bold", color: card.color, mb: 0.5 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                          {card.body}
                        </Typography>
                      </Paper>
                    ))}
                    <Paper
                      elevation={0}
                      sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255, 193, 7, 0.15)" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Important: please arrive on time so there's enough time to warm up and all rounds can be played calmly.
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </TabPanel>
            )}

            {/* Info Tab (custom-branded orgs only, e.g. RPL) */}
            {hasCustomBranding && (
              <TabPanel value={tabValue} index={5}>
                <Box sx={{ minHeight: "400px", p: { xs: 1, md: 2 }, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Paper elevation={1} sx={{ p: 2, borderLeft: `4px solid ${PE_BLUE}`, borderRadius: 2 }}>
                    <Typography sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: "bold", color: PE_BLUE, mb: 1 }}>
                      <PhoneIcon /> Contact
                    </Typography>
                    <Typography variant="body2">Florian Fernandes</Typography>
                    <Typography variant="body2">Fernandes_FN@live.de</Typography>
                  </Paper>
                  <Paper elevation={1} sx={{ p: 2, borderLeft: "4px solid #ff9800", borderRadius: 2 }}>
                    <Typography sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: "bold", color: "#ff9800", mb: 1 }}>
                      <CampaignIcon /> Announcements
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                      {announcement || "No announcements right now — check back soon."}
                    </Typography>
                  </Paper>
                </Box>
              </TabPanel>
            )}

            {/* Scorekeeper Matches Tab */}
            {(isScorekeeper || isAdmin) && (
              <TabPanel value={tabValue} index={hasCustomBranding ? 6 : isPadelEagle ? 5 : 4}>
                <Box sx={{ minHeight: { xs: "400px", md: "600px" } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: "center",
                      mb: { xs: 3, md: 4 },
                      background:
                        (isPadelEagle ? "#743444" : "linear-gradient(45deg, #4caf50 30%, #81c784 90%)"),
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: "bold",
                      fontSize: { xs: "1.75rem", md: "2.125rem" },
                      px: { xs: 1, md: 0 },
                    }}
                  >
                    📝 Score Matches 📝
                  </Typography>

                  {scorekeeperError ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography
                        color="error"
                        sx={{ mb: 2, maxWidth: 600, mx: "auto" }}
                      >
                        {scorekeeperError}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => fetchScorekeeperMatches()}
                        startIcon={<RepeatIcon />}
                      >
                        Try Again
                      </Button>
                    </Box>
                  ) : scorekeeperLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 4 }}
                    >
                      <Typography>Loading matches...</Typography>
                    </Box>
                  ) : scorekeeperMatches.length > 0 ? (
                    (() => {
                      // Helper to convert Firestore timestamp or string to Date
                      const parseLeagueDayDate = (
                        dateValue: any,
                      ): Date | null => {
                        if (!dateValue) return null;

                        // If it's a Firestore timestamp with _seconds
                        if (dateValue._seconds !== undefined) {
                          return new Date(dateValue._seconds * 1000);
                        }

                        // If it's already a Date object
                        if (dateValue instanceof Date) {
                          return dateValue;
                        }

                        // If it's a string or number
                        const parsed = new Date(dateValue);
                        return isNaN(parsed.getTime()) ? null : parsed;
                      };

                      // Build a map of league day IDs to their info and calculate day numbers
                      const leagueDayMap = new Map<
                        string,
                        { date: Date; dayNumber: number }
                      >();

                      // Collect all unique league day IDs with their dates
                      const uniqueLeagueDays = new Set<string>();
                      scorekeeperMatches.forEach((match: any) => {
                        uniqueLeagueDays.add(match.leagueDayId);
                      });

                      // Find the league day info from leagueDays array AND from matches themselves
                      const leagueDayDates: Array<{ id: string; date: Date }> =
                        [];
                      uniqueLeagueDays.forEach((ldId) => {
                        // First try to find in the leagueDays array
                        const foundInArray = leagueDays.find(
                          (ld) => ld.id === ldId,
                        );
                        if (foundInArray) {
                          const parsedDate = parseLeagueDayDate(
                            foundInArray.date,
                          );
                          if (parsedDate) {
                            leagueDayDates.push({ id: ldId, date: parsedDate });
                            return; // Found it, move to next
                          }
                        }

                        // If not found in array, try to extract from the match itself
                        // Find any match with this league day ID
                        const matchWithLeagueDay: any = scorekeeperMatches.find(
                          (m: any) => m.leagueDayId === ldId,
                        );
                        if (matchWithLeagueDay) {
                          // Check if match has league day date info embedded
                          if (matchWithLeagueDay.leagueDayDate) {
                            const parsedDate = parseLeagueDayDate(
                              matchWithLeagueDay.leagueDayDate,
                            );
                            if (parsedDate) {
                              leagueDayDates.push({
                                id: ldId,
                                date: parsedDate,
                              });
                            }
                          } else if (matchWithLeagueDay.createdAt) {
                            // Fallback: use match creation date as approximation
                            const parsedDate = parseLeagueDayDate(
                              matchWithLeagueDay.createdAt,
                            );
                            if (parsedDate) {
                              leagueDayDates.push({
                                id: ldId,
                                date: parsedDate,
                              });
                            }
                          }
                        }
                      });

                      console.log(
                        "[Score Matches] League day dates found:",
                        leagueDayDates.length,
                        "out of",
                        uniqueLeagueDays.size,
                      );

                      // Sort by date to assign day numbers
                      leagueDayDates.sort(
                        (a, b) => a.date.getTime() - b.date.getTime(),
                      );

                      // Assign day numbers based on chronological order
                      leagueDayDates.forEach((ld, index) => {
                        leagueDayMap.set(ld.id, {
                          date: ld.date,
                          dayNumber: index + 1,
                        });
                      });

                      // Group matches by league day
                      const matchesByLeagueDay = scorekeeperMatches.reduce(
                        (acc: any, match: any) => {
                          const leagueDayId = match.leagueDayId;
                          if (!acc[leagueDayId]) {
                            acc[leagueDayId] = {
                              matches: [],
                              leagueDayInfo:
                                leagueDayMap.get(leagueDayId) || null,
                            };
                          }
                          acc[leagueDayId].matches.push(match);
                          return acc;
                        },
                        {},
                      );

                      // Sort matches within each league day: incomplete first, then by round/court
                      Object.values(matchesByLeagueDay).forEach(
                        (group: any) => {
                          group.matches.sort((a: any, b: any) => {
                            // Incomplete matches first
                            if (a.isCompleted !== b.isCompleted) {
                              return a.isCompleted ? 1 : -1;
                            }
                            // Then by round number
                            if (a.roundNumber !== b.roundNumber) {
                              return a.roundNumber - b.roundNumber;
                            }
                            // Then by court number
                            return a.courtNumber - b.courtNumber;
                          });
                        },
                      );

                      return (
                        <Box>
                          {Object.entries(matchesByLeagueDay).map(
                            ([leagueDayId, group]: [string, any]) => {
                              const incompleteCount = group.matches.filter(
                                (m: any) => !m.isCompleted,
                              ).length;
                              const completedCount = group.matches.filter(
                                (m: any) => m.isCompleted,
                              ).length;

                              return (
                                <Accordion
                                  key={leagueDayId}
                                  defaultExpanded={incompleteCount > 0} // Auto-expand if has incomplete matches
                                  sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    "&:before": { display: "none" },
                                    boxShadow: 2,
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                      backgroundColor:
                                        incompleteCount > 0
                                          ? "warning.light"
                                          : "success.light",
                                      borderRadius: 2,
                                      "&.Mui-expanded": {
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: 0,
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        width: "100%",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          {(() => {
                                            const leagueDayInfo =
                                              group.leagueDayInfo;

                                            if (
                                              leagueDayInfo &&
                                              leagueDayInfo.dayNumber
                                            ) {
                                              const dateStr = formatDate(
                                                leagueDayInfo.date,
                                              );
                                              return `League Day #${
                                                leagueDayInfo.dayNumber
                                              }${dateStr ? ` - ${dateStr}` : ""}`;
                                            }

                                            // Fallback: try to find in leagueDays array
                                            const leagueDay = leagueDays.find(
                                              (ld) => ld.id === leagueDayId,
                                            );
                                            if (leagueDay) {
                                              const dayIndex =
                                                leagueDays.findIndex(
                                                  (ld) => ld.id === leagueDayId,
                                                );
                                              const dayNumber =
                                                dayIndex >= 0
                                                  ? dayIndex + 1
                                                  : null;
                                              if (dayNumber) {
                                                const dateStr = formatDate(
                                                  leagueDay.date,
                                                );
                                                return `League Day #${dayNumber}${
                                                  dateStr ? ` - ${dateStr}` : ""
                                                }`;
                                              }
                                            }

                                            // Final fallback
                                            return `League Day (${leagueDayId.substring(
                                              0,
                                              8,
                                            )}...)`;
                                          })()}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                          {incompleteCount > 0 && (
                                            <Chip
                                              label={`${incompleteCount} pending`}
                                              color="warning"
                                              size="small"
                                              sx={{ fontWeight: "bold" }}
                                            />
                                          )}
                                          {completedCount > 0 && (
                                            <Chip
                                              label={`${completedCount} completed`}
                                              color="success"
                                              size="small"
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                      {/* Reset button - only show if all matches are completed */}
                                      {incompleteCount === 0 &&
                                        completedCount > 0 && (
                                          <Tooltip title="Reset all match scores for this league day">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={(e) => {
                                                e.stopPropagation(); // Prevent accordion from toggling
                                                const leagueDayInfo =
                                                  group.leagueDayInfo;
                                                let leagueDayName = "";

                                                if (
                                                  leagueDayInfo &&
                                                  leagueDayInfo.dayNumber
                                                ) {
                                                  const dateStr = formatDate(
                                                    leagueDayInfo.date,
                                                  );
                                                  leagueDayName = `League Day #${
                                                    leagueDayInfo.dayNumber
                                                  }${
                                                    dateStr
                                                      ? ` - ${dateStr}`
                                                      : ""
                                                  }`;
                                                } else {
                                                  const leagueDay =
                                                    leagueDays.find(
                                                      (ld) =>
                                                        ld.id === leagueDayId,
                                                    );
                                                  if (leagueDay) {
                                                    const dayIndex =
                                                      leagueDays.findIndex(
                                                        (ld) =>
                                                          ld.id === leagueDayId,
                                                      );
                                                    const dayNumber =
                                                      dayIndex >= 0
                                                        ? dayIndex + 1
                                                        : null;
                                                    if (dayNumber) {
                                                      const dateStr =
                                                        formatDate(
                                                          leagueDay.date,
                                                        );
                                                      leagueDayName = `League Day #${dayNumber}${
                                                        dateStr
                                                          ? ` - ${dateStr}`
                                                          : ""
                                                      }`;
                                                    }
                                                  }
                                                }

                                                if (!leagueDayName) {
                                                  leagueDayName = `League Day (${leagueDayId.substring(
                                                    0,
                                                    8,
                                                  )}...)`;
                                                }

                                                handleResetLeagueDayScores(
                                                  leagueDayId,
                                                  leagueDayName,
                                                  group.matches.length,
                                                );
                                              }}
                                              sx={{ mr: 1 }}
                                            >
                                              <RestartAltIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                    </Box>
                                  </AccordionSummary>

                                  <AccordionDetails sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                      {group.matches.map((match: any) => (
                                        <Grid
                                          item
                                          xs={12}
                                          md={6}
                                          key={match.id}
                                        >
                                          <Card
                                            sx={{
                                              p: 2,
                                              border: match.isCompleted
                                                ? "2px solid"
                                                : "2px solid",
                                              borderColor: match.isCompleted
                                                ? "success.main"
                                                : "warning.main",
                                              borderRadius: 2,
                                              backgroundColor: match.isCompleted
                                                ? "success.light"
                                                : "warning.light",
                                            }}
                                          >
                                            <CardContent>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  mb: 2,
                                                }}
                                              >
                                                <Typography
                                                  variant="h6"
                                                  sx={{ fontWeight: "bold" }}
                                                >
                                                  Court {match.courtNumber} -
                                                  Round {match.roundNumber}
                                                </Typography>
                                                <Chip
                                                  label={
                                                    match.isCompleted
                                                      ? "Completed"
                                                      : "Pending"
                                                  }
                                                  color={
                                                    match.isCompleted
                                                      ? "success"
                                                      : "warning"
                                                  }
                                                  size="small"
                                                />
                                              </Box>

                                              <Box sx={{ mb: 2 }}>
                                                <Typography
                                                  variant="subtitle2"
                                                  color="text.secondary"
                                                  gutterBottom
                                                >
                                                  Team 1
                                                </Typography>
                                                <Typography
                                                  variant="body1"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    mb: 1,
                                                  }}
                                                >
                                                  {match.pair1?.player1Name} &{" "}
                                                  {match.pair1?.player2Name}
                                                </Typography>

                                                <Typography
                                                  variant="subtitle2"
                                                  color="text.secondary"
                                                  gutterBottom
                                                >
                                                  Team 2
                                                </Typography>
                                                <Typography
                                                  variant="body1"
                                                  sx={{ fontWeight: "bold" }}
                                                >
                                                  {match.pair2?.player1Name} &{" "}
                                                  {match.pair2?.player2Name}
                                                </Typography>
                                              </Box>

                                              {match.isCompleted && (
                                                <Box
                                                  sx={{
                                                    mb: 2,
                                                    p: 2,
                                                    background:
                                                      "rgba(76, 175, 80, 0.1)",
                                                    borderRadius: 1,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="h6"
                                                    sx={{
                                                      textAlign: "center",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    Final Score:{" "}
                                                    {match.score?.pair1Games} -{" "}
                                                    {match.score?.pair2Games}
                                                  </Typography>
                                                  {match.scoredBy && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        display: "block",
                                                        textAlign: "center",
                                                        mt: 1,
                                                      }}
                                                    >
                                                      Scored by:{" "}
                                                      {scorekeeperNames[
                                                        match.scoredBy
                                                      ] || match.scoredBy}
                                                      {match.scoredAt &&
                                                        (() => {
                                                          try {
                                                            // Handle Firestore Timestamp with _seconds (serialized format)
                                                            if (
                                                              match.scoredAt
                                                                ._seconds
                                                            ) {
                                                              return ` on ${new Date(
                                                                match.scoredAt
                                                                  ._seconds *
                                                                  1000,
                                                              ).toLocaleString(
                                                                "en-GB",
                                                              )}`;
                                                            }
                                                            // Handle Firestore Timestamp with seconds (direct format)
                                                            if (
                                                              match.scoredAt
                                                                .seconds
                                                            ) {
                                                              return ` on ${new Date(
                                                                match.scoredAt
                                                                  .seconds *
                                                                  1000,
                                                              ).toLocaleString(
                                                                "en-GB",
                                                              )}`;
                                                            }
                                                            // Handle Firestore Timestamp with toDate method
                                                            if (
                                                              typeof match
                                                                .scoredAt
                                                                .toDate ===
                                                              "function"
                                                            ) {
                                                              return ` on ${match.scoredAt
                                                                .toDate()
                                                                .toLocaleString(
                                                                  "en-GB",
                                                                )}`;
                                                            }
                                                            // Handle regular Date object
                                                            if (
                                                              match.scoredAt instanceof
                                                              Date
                                                            ) {
                                                              return ` on ${match.scoredAt.toLocaleString(
                                                                "en-GB",
                                                              )}`;
                                                            }
                                                            // Handle date string
                                                            return ` on ${new Date(
                                                              match.scoredAt,
                                                            ).toLocaleString(
                                                              "en-GB",
                                                            )}`;
                                                          } catch (error) {
                                                            console.error(
                                                              "Error formatting date:",
                                                              error,
                                                              match.scoredAt,
                                                            );
                                                            return "";
                                                          }
                                                        })()}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              )}

                                              <Button
                                                variant={
                                                  match.isCompleted
                                                    ? "outlined"
                                                    : "contained"
                                                }
                                                fullWidth
                                                onClick={() =>
                                                  handleScoreMatch(match)
                                                }
                                                sx={{
                                                  fontWeight: "bold",
                                                  ...(match.isCompleted
                                                    ? {}
                                                    : {
                                                        background:
                                                          "linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)",
                                                        "&:hover": {
                                                          background:
                                                            "linear-gradient(45deg, #f57c00 30%, #ff9800 90%)",
                                                        },
                                                      }),
                                                }}
                                              >
                                                {match.isCompleted
                                                  ? "Edit Score"
                                                  : "Enter Score"}
                                              </Button>
                                            </CardContent>
                                          </Card>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </AccordionDetails>
                                </Accordion>
                              );
                            },
                          )}
                        </Box>
                      );
                    })()
                  ) : (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        No matches available for scoring
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All matches have been completed or no matches are
                        assigned yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            )}
          </Paper>
        )}

      {/* Tournament Stats View - For "both" organizations when tournament view is selected */}
      {currentOrg?.competitionType === "both" &&
        publicView === "tournament" && (
          <>
            {/* Tournaments Section */}
            <Paper
              elevation={3}
              sx={{
                borderRadius: { xs: 2, md: 3 },
                p: { xs: 3, md: 4 },
                mb: { xs: 2, md: 3 },
                background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  textAlign: "center",
                  color: "rgba(255, 255, 255, 0.95)",
                  fontWeight: "bold",
                  fontSize: { xs: "1.75rem", md: "2.5rem" },
                  textShadow: "0 2px 10px rgba(0, 0, 0, 0.8)",
                  letterSpacing: "0.05em",
                }}
              >
                ⚔️ Tournaments ⚔️
              </Typography>

              <Grid container spacing={3}>
                {/* Nations League Cards */}
                {nationsLeagueEvents.map((event) => (
                  <Grid item xs={12} md={6} key={event.id}>
                    <Card
                      onClick={() => (window.location.href = `/cupOfNations/${event.id}`)}
                      sx={{
                        cursor: "pointer",
                        height: "100%",
                        background: `linear-gradient(rgba(45, 53, 97, 0.9), rgba(26, 31, 58, 0.95)), url(${nationsBanner})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "white",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        filter: "grayscale(0.7) contrast(1.1)",
                        "&:hover": {
                          transform: "translateY(-8px) scale(1.02)",
                          boxShadow: isPadelEagle ? "none" : "0 20px 60px rgba(80, 90, 140, 0.4)",
                          filter: "grayscale(0.6) contrast(1.1)",
                        },
                      }}
                    >
                      <CardContent sx={{ position: "relative", zIndex: 1, p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={event.status === "active" ? "ACTIVE" : "COMPLETED"}
                            sx={{
                              background: event.status === "active"
                                ? "rgba(255, 193, 7, 0.9)"
                                : "rgba(76, 175, 80, 0.9)",
                              color: "#ffffff",
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                            }}
                          />
                          <Box
                            sx={{
                              fontSize: "2rem",
                              animation: "float 3s ease-in-out infinite",
                              "@keyframes float": {
                                "0%, 100%": { transform: "translateY(0px)" },
                                "50%": { transform: "translateY(-10px)" },
                              },
                            }}
                          >
                            🏆
                          </Box>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            mb: 2,
                            textShadow: "2px 2px 8px rgba(0,0,0,0.8)",
                          }}
                        >
                          {event.name}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            mb: 3,
                            opacity: 0.9,
                            fontSize: "1rem",
                          }}
                        >
                          {(event.countries?.length || 8)} Nations fighting for first place to become{" "}
                          {event.name} champions
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 2,
                            fontSize: "1.75rem",
                          }}
                        >
                          {(event.countries || []).map((c: any, idx: number) => (
                            <span key={idx} title={c.name}>
                              {c.isoCode.length > 2
                                ? c.isoCode
                                : String.fromCodePoint(
                                    ...c.isoCode
                                      .toUpperCase()
                                      .split("")
                                      .map((ch: string) => 127397 + ch.charCodeAt(0)),
                                  )}
                            </span>
                          ))}
                        </Box>

                        <Box
                          sx={{
                            mt: 3,
                            pt: 2,
                            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                opacity: 0.8,
                                fontWeight: "medium",
                              }}
                            >
                              Click to view full standings
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNationsFaqEvent(event);
                              }}
                              sx={{
                                color: "white",
                                borderColor: "rgba(255,255,255,0.5)",
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": { borderColor: "white", background: "rgba(255,255,255,0.12)" },
                              }}
                            >
                              FAQ
                            </Button>
                          </Box>
                          <Box
                            sx={{
                              fontSize: "1.5rem",
                              animation: "bounce 2s ease-in-out infinite",
                              "@keyframes bounce": {
                                "0%, 100%": { transform: "translateX(0)" },
                                "50%": { transform: "translateX(5px)" },
                              },
                            }}
                          >
                            →
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                <NationsLeagueFaqDialog
                  open={!!nationsFaqEvent}
                  onClose={() => setNationsFaqEvent(null)}
                  title={nationsFaqEvent?.name || ""}
                  faq={nationsFaqEvent?.faq || []}
                  pageUrl={`/cupOfNations/${nationsFaqEvent?.id}/faq`}
                />

              </Grid>
            </Paper>
          </>
        )}

      {/* SPT — Stuttgart Padel Tournaments */}
      {((currentOrg?.competitionType === "both" && publicView === "spt") ||
        (isPadelEagle && tabValue === PE_TOURNAMENTS_TAB)) && (
        <Paper
          elevation={3}
          sx={{
            borderRadius: { xs: 2, md: 3 },
            p: isPadelEagle ? { xs: 1.5, md: 4 } : { xs: 3, md: 4 },
            mb: { xs: 2, md: 3 },
            background: isPadelEagle
              ? "linear-gradient(135deg, #3d1520 0%, #5a1f2e 100%)"
              : "linear-gradient(135deg, #0d2b18 0%, #1a3a22 100%)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: isPadelEagle ? { xs: 2, md: 4 } : 4,
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.95)",
              fontWeight: "bold",
              fontSize: { xs: "1.75rem", md: "2.5rem" },
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.8)",
              letterSpacing: "0.05em",
            }}
          >
            🎾 Tournaments 🏅
          </Typography>

          <Grid container spacing={isPadelEagle ? { xs: 1.5, md: 3 } : 3}>
            {visibleSptTournaments.length === 0 ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    color: "rgba(255,255,255,0.4)",
                  }}
                >
                  <Typography sx={{ fontSize: "3rem", mb: 2 }}>🎾</Typography>
                  <Typography variant="h5" sx={{ mb: 1, opacity: 0.5 }}>Tournaments coming soon</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.3 }}>Check back for upcoming events</Typography>
                </Box>
              </Grid>
            ) : (
              visibleSptTournaments.map((t: any) => {
                const isUpcoming = t.status === "upcoming";
                const isRegistration = t.status === "registration";
                const isCompleted = t.status === "completed";
                const isInProgress = t.status === "group-stage" || t.status === "knockout";
                const startDate = t.startDate
                  ? new Date(t.startDate.seconds ? t.startDate.seconds * 1000 : t.startDate)
                  : null;
                const endDate = t.endDate
                  ? new Date(t.endDate.seconds ? t.endDate.seconds * 1000 : t.endDate)
                  : null;
                const isTournamentDay = (() => {
                  if (!startDate) return false;
                  const today = new Date();
                  const dayStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                  const lastDay = endDate || startDate;
                  const dayEnd = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate(), 23, 59, 59, 999);
                  return today >= dayStart && today <= dayEnd;
                })();
                const registeredTeamsCount = Array.isArray(t.registeredTeams) ? t.registeredTeams.length : 0;
                const maxTeams = Number(t.maxTeams) || 0;
                const spotsRemaining = maxTeams > 0 ? Math.max(0, maxTeams - registeredTeamsCount) : 0;
                const registrationRatio = maxTeams > 0 ? registeredTeamsCount / maxTeams : 0;
                const showFewSpotsStamp = isRegistration && maxTeams > 0 && registrationRatio >= 0.5 && spotsRemaining > 0;
                const isAdminManagedRegistration =
                  t.playerRegistrationManagedByAdmin === true;

                const tournamentRegistrations = sptRegistrations.filter(
                  (reg: any) => reg.tournamentId === t.id,
                );
                const activeRegistrationStatuses = new Set([
                  "confirmed",
                  "pending-approval",
                  "waitlist",
                ]);
                let myRegistration: any =
                  tournamentRegistrations.find((reg: any) =>
                    activeRegistrationStatuses.has(reg.status),
                  ) || null;
                const registeredTeam = t.registeredTeams?.find(
                  (team: any) =>
                    team.player1Id === playerUser?.playerId ||
                    team.player2Id === playerUser?.playerId,
                );
                const myPendingInvitation = sptPendingInvitations.find(
                  (inv: any) => inv.tournamentId === t.id,
                );
                const myReceivedInvitation = sptReceivedInvitations.find(
                  (inv: any) => inv.tournamentId === t.id,
                );

                let registrationStatus = "none";
                let partnerName = "";
                let invitationId = "";

                if (!myRegistration && registeredTeam) {
                  myRegistration = {
                    id: registeredTeam.id,
                    tournamentId: t.id,
                    status: "confirmed",
                    player1Id: registeredTeam.player1Id,
                    player2Id: registeredTeam.player2Id,
                    player1Name: registeredTeam.player1Name,
                    player2Name: registeredTeam.player2Name,
                  };
                }

                if (myRegistration) {
                  if (myRegistration.status === "confirmed") {
                    registrationStatus = "registered";
                    partnerName =
                      playerUser?.playerId === myRegistration.player1Id
                        ? myRegistration.player2Name
                        : myRegistration.player1Name;
                  } else if (myRegistration.status === "pending-approval") {
                    registrationStatus = "pending-approval";
                    partnerName =
                      playerUser?.playerId === myRegistration.player1Id
                        ? myRegistration.player2Name
                        : myRegistration.player1Name;
                  } else if (myRegistration.status === "waitlist") {
                    registrationStatus = "waitlisted";
                    partnerName =
                      playerUser?.playerId === myRegistration.player1Id
                        ? myRegistration.player2Name
                        : myRegistration.player1Name;
                  }
                } else if (myPendingInvitation) {
                  if (myPendingInvitation.isNewPlayer) {
                    registrationStatus = "awaiting-registration";
                    partnerName =
                      myPendingInvitation.recipientName ||
                      myPendingInvitation.recipientEmail;
                    invitationId = myPendingInvitation.id;
                  } else {
                    registrationStatus = "awaiting-response";
                    partnerName =
                      myPendingInvitation.recipientName ||
                      myPendingInvitation.recipientEmail;
                    invitationId = myPendingInvitation.id;
                  }
                }

                const checklistEntry =
                  playerUser?.playerId && t.playerChecklist
                    ? t.playerChecklist[playerUser.playerId]
                    : null;
                const partnerPlayerId =
                  registrationStatus === "registered" && myRegistration && playerUser?.playerId
                    ? (playerUser.playerId === myRegistration.player1Id
                        ? myRegistration.player2Id
                        : myRegistration.player1Id)
                    : null;
                const partnerChecklistEntry =
                  partnerPlayerId && t.playerChecklist
                    ? t.playerChecklist[partnerPlayerId]
                    : null;
                const selfPaymentConfirmed = !!checklistEntry?.paid;
                const partnerPaymentConfirmed = !!partnerChecklistEntry?.paid;
                const paymentChipLabel =
                  registrationStatus === "waitlisted"
                    ? "Currently on the waitlist"
                    : registrationStatus === "registered"
                    ? `${selfPaymentConfirmed ? "Payment Confirmed" : "Pending Payment - Email with Payment Info will be sent soon"} · ${partnerPaymentConfirmed ? "Partner Payment Confirmed" : "Partner Payment Pending"}`
                    : null;
                const drawEmailNote =
                  registrationStatus === "registered"
                    ? "Tournament draw will be sent to your email at least 1 week before the tournament."
                    : null;
                const paymentIsPending =
                  !!paymentChipLabel &&
                  paymentChipLabel.startsWith("Pending Payment");
                const paymentIsWaitlist =
                  !!paymentChipLabel &&
                  paymentChipLabel.startsWith("Currently on the waitlist");
                const paymentDisplayLabel = paymentChipLabel
                  ? paymentIsPending
                    ? `⚠ ${paymentChipLabel}`
                    : paymentIsWaitlist
                      ? `⏳ ${paymentChipLabel}`
                    : `✅ ${paymentChipLabel}`
                  : null;
                const teammatePlayerId =
                  myRegistration && playerUser?.playerId
                    ? (playerUser.playerId === myRegistration.player1Id
                        ? myRegistration.player2Id
                        : myRegistration.player1Id)
                    : null;
                const teammateChecklistEntry =
                  teammatePlayerId && t.playerChecklist
                    ? t.playerChecklist[teammatePlayerId]
                    : null;
                const withdrawalBlockedByPayment = !!(checklistEntry?.paid || teammateChecklistEntry?.paid);
                const withdrawalBlockedMessage = "To cancel your registration please contact the tournament organizers directly on WhatsApp at +49 176 2019 3968.";

                const statusChipBase = isUpcoming
                  ? { label: "COMING SOON", bg: "rgba(100,160,100,0.85)" }
                  : isRegistration
                  ? { label: "REGISTRATION OPEN", bg: "rgba(56,142,60,0.9)" }
                  : isInProgress
                  ? (isTournamentDay
                    ? { label: "IN PROGRESS", bg: "rgba(245,124,0,0.9)" }
                    : { label: "DRAWS GENERATED", bg: "rgba(25,118,210,0.9)" })
                  : isCompleted
                  ? { label: "COMPLETED", bg: "rgba(76,175,80,0.9)" }
                  : { label: t.status.toUpperCase(), bg: "rgba(100,100,100,0.8)" };
                // Padel Eagle CI: one chip color for every status
                const statusChip = isPadelEagle ? { ...statusChipBase, bg: "#F4C48B" } : statusChipBase;

                const ctaLabel = isRegistration
                  ? (isAdminManagedRegistration ? "Registration managed by admin" : "Register Now →")
                  : isInProgress
                  ? "View Tournament Info →"
                  : isCompleted
                  ? "See Results →"
                  : null;

                const regOpenDate = t.registrationOpenDate
                  ? new Date(t.registrationOpenDate.seconds ? t.registrationOpenDate.seconds * 1000 : t.registrationOpenDate)
                  : null;
                const isNotYetOpen = isUpcoming;

                const levelGradients: Record<string, string> = isPadelEagle ? {
                  Open: "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.94))",
                  Advanced: "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.94))",
                  Intermediate: "linear-gradient(rgba(228,148,33,0.9), rgba(228,148,33,0.94))",
                  Beginner: "linear-gradient(rgba(228,148,33,0.9), rgba(228,148,33,0.94))",
                  Mixed: "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.94))",
                  Women: "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.94))",
                } : {
                  Open: "linear-gradient(rgba(27,94,32,0.88), rgba(10,50,15,0.95))",
                  Advanced: "linear-gradient(rgba(0,77,64,0.88), rgba(0,40,30,0.95))",
                  Intermediate: "linear-gradient(rgba(46,125,50,0.88), rgba(20,60,25,0.95))",
                  Beginner: "linear-gradient(rgba(104,159,56,0.88), rgba(50,80,20,0.95))",
                  Mixed: "linear-gradient(rgba(230,81,0,0.88), rgba(130,40,0,0.95))",
                  Women: "linear-gradient(rgba(136,14,79,0.88), rgba(80,5,50,0.95))",
                };
                // Must be an image layer (not a plain color) so it can be stacked over the poster
                const gradientOverlay = isPadelEagle
                  ? (isCompleted
                      ? "linear-gradient(rgba(116,88,108,0.92), rgba(116,88,108,0.92))"
                      : "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.9))")
                  : isCompleted
                  ? (isPadelEagle ? "linear-gradient(rgba(116,88,108,0.92), rgba(116,88,108,0.95))" : "linear-gradient(rgba(66,66,66,0.88), rgba(30,30,30,0.95))")
                  : levelGradients[t.level] || (isPadelEagle ? "linear-gradient(rgba(116,52,68,0.9), rgba(116,52,68,0.94))" : "linear-gradient(rgba(27,94,32,0.88), rgba(13,43,20,0.95))");
                // Padel Eagle: let the poster show through, fading into solid maroon at the bottom where the text sits
                const peHasPoster = isPadelEagle && !!t.posterImageUrl;
                const peOverlay = isCompleted ? "116,88,108" : "116,52,68";
                const background = peHasPoster
                  ? `linear-gradient(to bottom, rgba(${peOverlay},0.1) 0%, rgba(${peOverlay},0.2) 35%, rgba(${peOverlay},0.92) 72%, rgba(${peOverlay},0.98) 100%), url(${t.posterImageUrl})`
                  : t.posterImageUrl
                  ? `${gradientOverlay}, url(${t.posterImageUrl})`
                  : gradientOverlay;

                return (
                  <Grid key={t.id} item xs={12} md={6}>
                    <Card
                      onClick={() => !isUpcoming && (window.location.href = `/tournament/${t.id}`)}
                      sx={{
                        cursor: isUpcoming ? "default" : "pointer",
                        height: "100%",
                        minHeight: peHasPoster ? { xs: 380, sm: 460 } : isPadelEagle ? { xs: 0, sm: 320 } : 320,
                        background,
                        backgroundSize: "cover",
                        backgroundPosition: peHasPoster ? "top center" : "center",
                        backgroundRepeat: "no-repeat",
                        color: "white",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                        filter: isUpcoming ? "grayscale(0.3) contrast(1)" : "grayscale(0) contrast(1.05)",
                        "&:hover": isUpcoming ? {} : {
                          transform: "translateY(-8px) scale(1.02)",
                          boxShadow: isPadelEagle ? "0 20px 60px rgba(228,148,33,0.3)" : "0 20px 60px rgba(27,94,32,0.4)",
                        },
                      }}
                    >
                      {showFewSpotsStamp && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 14,
                            right: -38,
                            zIndex: 2,
                            transform: "rotate(24deg)",
                            background: "linear-gradient(135deg, #c62828 0%, #ef6c00 100%)",
                            color: "#fff",
                            px: 5.25,
                            py: 0.65,
                            borderTop: "2px solid rgba(255,255,255,0.45)",
                            borderBottom: "2px solid rgba(255,255,255,0.45)",
                            boxShadow: isPadelEagle ? "none" : "0 8px 24px rgba(0,0,0,0.45)",
                            textAlign: "center",
                            animation: "fewSpotsPulse 1.4s ease-in-out infinite",
                            "@keyframes fewSpotsPulse": {
                              "0%": { filter: "brightness(1)", transform: "rotate(24deg) scale(1)" },
                              "50%": { filter: "brightness(1.1)", transform: "rotate(24deg) scale(1.03)" },
                              "100%": { filter: "brightness(1)", transform: "rotate(24deg) scale(1)" },
                            },
                          }}
                        >
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 900, letterSpacing: "0.08em", lineHeight: 1.1 }}>
                            FEW SPOTS LEFT
                          </Typography>
                        </Box>
                      )}

                      <CardContent sx={{ position: "relative", zIndex: 1, p: isPadelEagle ? { xs: 1.75, sm: 3 } : 3, "&:last-child": { pb: isPadelEagle ? { xs: 1.75, sm: 3 } : 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: isPadelEagle ? { xs: 1, sm: 2 } : 2 }}>
                          <Chip
                            label={statusChip.label}
                            size="small"
                            sx={{ background: statusChip.bg, color: isPadelEagle ? "#743444" : "#fff", fontWeight: "bold", fontSize: "0.7rem" }}
                          />
                          {getTournamentLevels(t).map((lvl) => (
                            <Chip
                              key={lvl}
                              label={lvl}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: "rgba(255,255,255,0.5)", color: "rgba(255,255,255,0.85)", fontSize: "0.7rem" }}
                            />
                          ))}
                          {isPadelEagle && getLevelRangeLabel(getTournamentLevels(t)) && (
                            <Chip
                              label={getLevelRangeLabel(getTournamentLevels(t))}
                              size="small"
                              sx={{ background: "#E49421", color: "#fff", fontWeight: "bold", fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>

                        {paymentDisplayLabel && (
                          <>
                            <Chip
                              label={paymentDisplayLabel}
                              size="medium"
                              variant="filled"
                              sx={{
                                mb: 1,
                                width: "100%",
                                borderRadius: 2,
                                fontWeight: 800,
                                letterSpacing: "0.01em",
                                border: paymentIsPending
                                  ? "2px solid #ff8f00"
                                  : paymentIsWaitlist
                                    ? "2px solid #6a1b9a"
                                  : "2px solid #1b5e20",
                                backgroundColor: paymentIsPending
                                  ? "#ffca28"
                                  : paymentIsWaitlist
                                    ? "#ce93d8"
                                  : "#2e7d32",
                                color: paymentIsPending
                                  ? "#1f1f1f"
                                  : paymentIsWaitlist
                                    ? "#1f1f1f"
                                  : "#ffffff",
                                boxShadow: paymentIsPending
                                  ? "0 0 0 2px rgba(255, 179, 0, 0.35), 0 6px 16px rgba(255, 152, 0, 0.35)"
                                  : paymentIsWaitlist
                                    ? "0 0 0 2px rgba(106, 27, 154, 0.25), 0 6px 16px rgba(142, 36, 170, 0.25)"
                                  : "0 0 0 2px rgba(46, 125, 50, 0.24)",
                                animation: paymentIsPending
                                  ? "paymentPulse 1.2s ease-in-out infinite"
                                  : "none",
                                "@keyframes paymentPulse": {
                                  "0%": { transform: "scale(1)", boxShadow: "0 0 0 2px rgba(255, 179, 0, 0.35), 0 6px 16px rgba(255, 152, 0, 0.25)" },
                                  "50%": { transform: "scale(1.01)", boxShadow: "0 0 0 3px rgba(255, 179, 0, 0.48), 0 8px 22px rgba(255, 152, 0, 0.42)" },
                                  "100%": { transform: "scale(1)", boxShadow: "0 0 0 2px rgba(255, 179, 0, 0.35), 0 6px 16px rgba(255, 152, 0, 0.25)" },
                                },
                                height: "auto",
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  display: "block",
                                  py: 0.65,
                                },
                              }}
                            />
                            {drawEmailNote && (
                              <Typography
                                variant="caption"
                                sx={{
                                  mb: 1,
                                  display: "block",
                                  fontWeight: 600,
                                  color: "rgba(255,255,255,0.9)",
                                  textShadow: "0 1px 4px rgba(0,0,0,0.55)",
                                }}
                              >
                                {drawEmailNote}
                              </Typography>
                            )}
                          </>
                        )}

                        <Box sx={{ textAlign: "center", my: isPadelEagle ? { xs: 0.5, sm: 2 } : 2, flex: 1, ...(peHasPoster ? { display: "flex", flexDirection: "column", justifyContent: "flex-end" } : {}) }}>
                          {!peHasPoster && <Box sx={{ fontSize: "3rem", mb: 1, display: isPadelEagle ? { xs: "none", sm: "block" } : "block" }}>🎾</Box>}
                          <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", textShadow: isPadelEagle && !peHasPoster ? "none" : "0 2px 8px rgba(0,0,0,0.6)", mb: 0.5, fontSize: isPadelEagle ? { xs: "1.1rem", sm: "1.5rem" } : undefined }}>
                            {t.name}
                          </Typography>
                        </Box>

                        {/* Sponsor logos */}
                        {Array.isArray(t.sponsors) && t.sponsors.length > 0 && (
                          <Box sx={{ mt: 1, mb: 0.5, px: 1, py: 0.75, borderTop: "1px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                            <Typography variant="caption" sx={{ opacity: 0.5, whiteSpace: "nowrap" }}>Sponsored by</Typography>
                            {t.sponsors.map((s: any, i: number) => (
                              <Box
                                key={i}
                                component={s.websiteUrl ? "a" : "div"}
                                href={s.websiteUrl || undefined}
                                target={s.websiteUrl ? "_blank" : undefined}
                                rel={s.websiteUrl ? "noopener noreferrer" : undefined}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: 32,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: "rgba(255,255,255,0.92)",
                                  boxShadow: isPadelEagle ? "none" : "0 2px 10px rgba(0,0,0,0.18)",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={s.logoUrl}
                                  alt={s.name}
                                  sx={{
                                    height: 24,
                                    maxWidth: 72,
                                    objectFit: "contain",
                                    display: "block",
                                  }}
                                />
                              </Box>
                            ))}
                          </Box>
                        )}

                        <Box sx={{ mt: "auto" }}>
                          {startDate && (
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5, textAlign: "center" }}>
                              📅 {startDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                              {t.startTime && ` · ${t.startTime}`}
                              {t.venue && ` · ${t.venue}`}
                            </Typography>
                          )}
                          {isUpcoming && regOpenDate && (
                            <Typography variant="body2" sx={{ opacity: 0.7, mb: 1, textAlign: "center", fontStyle: "italic" }}>
                              Registration opens {regOpenDate.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
                            </Typography>
                          )}
                          {(
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 1, gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                              {t.description && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSptInfoTournament(t);
                                }}
                                sx={{
                                  borderColor: "rgba(255,255,255,0.6)",
                                  color: "rgba(255,255,255,0.92)",
                                  textTransform: "none",
                                  backdropFilter: "blur(6px)",
                                  backgroundColor: "rgba(255,255,255,0.08)",
                                  "&:hover": {
                                    borderColor: "rgba(255,255,255,0.9)",
                                    backgroundColor: "rgba(255,255,255,0.16)",
                                  },
                                }}
                              >
                                Show tournament info
                              </Button>
                              )}

                              {!playerUser && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    window.location.href = `/player-registration?tournamentId=${t.id}`;
                                  }}
                                  sx={{
                                    borderColor: "rgba(255,255,255,0.6)",
                                    color: "rgba(255,255,255,0.92)",
                                    textTransform: "none",
                                    backdropFilter: "blur(6px)",
                                    backgroundColor: "rgba(255,255,255,0.08)",
                                    "&:hover": {
                                      borderColor: "rgba(255,255,255,0.9)",
                                      backgroundColor: "rgba(255,255,255,0.16)",
                                    },
                                  }}
                                >
                                  Create tournament account
                                </Button>
                              )}

                              {!playerUser && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={isNotYetOpen}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    window.location.href = "/player/login";
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    backgroundColor: isPadelEagle ? "#E49421" : "rgba(76, 175, 80, 0.9)",
                                    "&:hover": { backgroundColor: isPadelEagle ? "#74586C" : "rgba(56, 142, 60, 0.95)" },
                                    "&.Mui-disabled": {
                                      backgroundColor: "rgba(180, 180, 180, 0.45)",
                                      color: "rgba(255,255,255,0.72)",
                                    },
                                  }}
                                >
                                  Login & register
                                </Button>
                              )}

                              {playerUser && myReceivedInvitation && isRegistration && !myRegistration && (
                                <>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    disabled={sptProcessingInviteId !== null}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSptDeclineInvitation(myReceivedInvitation.id);
                                    }}
                                  >
                                    {sptProcessingInviteId === myReceivedInvitation.id ? "..." : "Decline"}
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    disabled={sptProcessingInviteId !== null}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSptAcceptInvitation(myReceivedInvitation.id);
                                    }}
                                  >
                                    {sptProcessingInviteId === myReceivedInvitation.id ? "..." : "Accept invite"}
                                  </Button>
                                </>
                              )}

                              {playerUser && (!myReceivedInvitation || !isRegistration || !!myRegistration) && registrationStatus === "none" && !isAdminManagedRegistration && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={isNotYetOpen}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    window.location.href = "/view-tournaments";
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    backgroundColor: isPadelEagle ? "#E49421" : "rgba(76, 175, 80, 0.9)",
                                    "&:hover": { backgroundColor: isPadelEagle ? "#74586C" : "rgba(56, 142, 60, 0.95)" },
                                    "&.Mui-disabled": {
                                      backgroundColor: "rgba(180, 180, 180, 0.45)",
                                      color: "rgba(255,255,255,0.72)",
                                    },
                                  }}
                                >
                                  Register to tournament
                                </Button>
                              )}

                              {playerUser && registrationStatus === "none" && isRegistration && isAdminManagedRegistration && (
                                <Button variant="outlined" size="small" disabled>
                                  Registration managed by admin
                                </Button>
                              )}

                              {playerUser && (registrationStatus === "awaiting-response" || registrationStatus === "awaiting-registration") && (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  disabled
                                >
                                  {registrationStatus === "awaiting-registration"
                                    ? `Awaiting ${partnerName}'s registration`
                                    : `Awaiting ${partnerName}'s response`}
                                </Button>
                              )}

                              {playerUser && registrationStatus === "pending-approval" && (
                                <Button variant="outlined" color="info" size="small" disabled>
                                  Pending admin approval{partnerName ? ` with ${partnerName}` : ""}
                                </Button>
                              )}

                              {playerUser && (registrationStatus === "registered" || registrationStatus === "waitlisted") && (
                                <>
                                  <Button variant="contained" color="success" size="small" disabled>
                                    {registrationStatus === "waitlisted" ? "Waitlisted" : "Registered"}
                                    {partnerName ? ` with ${partnerName}` : ""}
                                  </Button>
                                  {myRegistration?.id && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      disabled={withdrawalBlockedByPayment}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (withdrawalBlockedByPayment) return;
                                        handleSptWithdrawRegistration(myRegistration.id, t.name);
                                      }}
                                    >
                                      {withdrawalBlockedByPayment ? withdrawalBlockedMessage : "Withdraw registration"}
                                    </Button>
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                          {isNotYetOpen && regOpenDate && (
                            <Typography variant="caption" sx={{ display: "block", textAlign: "center", opacity: 0.8, mb: 1 }}>
                              Registration opens in {formatCountdown(regOpenDate)}
                            </Typography>
                          )}
                          {isNotYetOpen && (!regOpenDate || regOpenDate.getTime() <= now) && (
                            <Typography variant="caption" sx={{ display: "block", textAlign: "center", opacity: 0.8, mb: 1 }}>
                              Registration is not open yet
                            </Typography>
                          )}
                          {ctaLabel && (
                            <Typography variant="body2" sx={{ textAlign: "center", opacity: 0.9, fontWeight: 700, mt: 1, letterSpacing: "0.05em", color: isPadelEagle ? "#F4C48B" : "#a5d6a7" }}>
                              {ctaLabel}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Paper>
      )}

      <Dialog
        open={!!sptInfoTournament}
        onClose={() => setSptInfoTournament(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{sptInfoTournament?.name || "Tournament info"}</DialogTitle>
        <DialogContent dividers>
          {sptInfoTournament?.description ? (
            <FormattedTextBlock text={sptInfoTournament.description} color="text.primary" />
          ) : (
            <Typography variant="body1">No additional information available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSptInfoTournament(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Division Comparison for "All" (non-Padel Eagle orgs only) */}
      {String(currentDivision) === "All" &&
        !isPadelEagle &&
        divisionComparison.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              borderRadius: { xs: 2, md: 3 },
              overflow: "hidden",
              mb: { xs: 2, md: 3 },
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                textAlign: "center",
                mb: 3,
                fontWeight: "bold",
                color: PE_BLUE,
              }}
            >
              📊 Division Comparison
            </Typography>
            <Grid container spacing={2}>
              {divisionComparison.map((div) => (
                <Grid item xs={12} md={6} lg={4} key={div.division}>
                  <Card
                    sx={{
                      height: "100%",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 2 }}
                      >
                        {div.division}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Players
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {div.totalPlayers}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            League Days
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {div.totalLeagueDays}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Total Matches
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {div.totalMatches}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Avg Points/Player
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {div.avgPointsPerPlayer.toFixed(1)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider
                            sx={{ my: 1, bgcolor: "white", opacity: 0.3 }}
                          />
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Top Player
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {div.topPlayer} ({div.topPlayerPoints} pts)
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Tooltip title="Difference in points between 1st place and lowest ranked active player (excludes players with 0 points)">
                            <Typography
                              variant="body2"
                              sx={{ opacity: 0.9, cursor: "help" }}
                            >
                              Point Spread ℹ️
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {div.pointSpread}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Tooltip title="Number of matches played in the last 30 days">
                            <Typography
                              variant="body2"
                              sx={{ opacity: 0.9, cursor: "help" }}
                            >
                              Recent Activity ℹ️
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {div.recentActivity} matches
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

      {/* League Day Details Modal */}
      <Dialog
        open={!!selectedLeagueDay}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600} // Full screen on mobile
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            maxHeight: { xs: "100vh", sm: "90vh" },
            margin: { xs: 0, sm: "32px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: (isPadelEagle ? "#743444" : "linear-gradient(135deg, #4caf50 0%, #81c784 100%)"),
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 2 },
            }}
          >
            <EventIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", md: "1.25rem" },
                lineHeight: 1.2,
              }}
            >
              League Day Details -{" "}
              {selectedLeagueDay &&
                formatDateTime(selectedLeagueDay.date, selectedLeagueDay.time)}
            </Typography>
          </Box>
          <Button
            onClick={handleCloseModal}
            sx={{ color: "white", minWidth: "auto", p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {modalLoading ? (
            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
              <Typography>Loading match details...</Typography>
            </Box>
          ) : (
            <Box>
              {/* League Day Summary */}
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2, md: 3 },
                  backgroundColor: "action.hover",
                }}
              >
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: { xs: 1, sm: 1.5, md: 2 },
                        textAlign: "center",
                        backgroundColor: "success.light",
                        borderRadius: { xs: 1, md: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                        }}
                      >
                        {selectedLeagueDay?.selectedPlayers?.length || 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: {
                            xs: "0.65rem",
                            sm: "0.75rem",
                            md: "0.875rem",
                          },
                        }}
                      >
                        Players
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: { xs: 1, sm: 1.5, md: 2 },
                        textAlign: "center",
                        backgroundColor: "info.light",
                        borderRadius: { xs: 1, md: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                        }}
                      >
                        {matchStats.totalMatches}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: {
                            xs: "0.65rem",
                            sm: "0.75rem",
                            md: "0.875rem",
                          },
                        }}
                      >
                        Matches
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: { xs: 1, sm: 1.5, md: 2 },
                        textAlign: "center",
                        backgroundColor: "warning.light",
                        borderRadius: { xs: 1, md: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                        }}
                      >
                        {matchStats.completedMatches}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: {
                            xs: "0.65rem",
                            sm: "0.75rem",
                            md: "0.875rem",
                          },
                        }}
                      >
                        Completed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      sx={{
                        p: { xs: 1, sm: 1.5, md: 2 },
                        textAlign: "center",
                        backgroundColor: "error.light",
                        borderRadius: { xs: 1, md: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                        }}
                      >
                        {topPerformers.length > 0
                          ? topPerformers[0]?.gamesWon || 0
                          : 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: {
                            xs: "0.65rem",
                            sm: "0.75rem",
                            md: "0.875rem",
                          },
                        }}
                      >
                        Top Score
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Top Performers Section */}
              {topPerformers.length > 0 && (
                <>
                  <Divider />
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2, md: 3 },
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 2, md: 3 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        fontSize: { xs: "1rem", md: "1.25rem" },
                      }}
                    >
                      <TrophyIcon
                        sx={{ color: "#FFD700", fontSize: { xs: 20, md: 24 } }}
                      />
                      Top Performers of the Day
                    </Typography>

                    {/* Podium Display */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "end",
                        justifyContent: "center",
                        gap: { xs: 2, sm: 3, md: 4 },
                        mb: 2,
                      }}
                    >
                      {/* 2nd Place */}
                      {topPerformers[1] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transform: "translateY(10px)",
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 60, sm: 70, md: 80 },
                              height: { xs: 60, sm: 70, md: 80 },
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #C0C0C0 0%, #90A4AE 100%)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              boxShadow: isPadelEagle ? "none" : "0 4px 12px rgba(0,0,0,0.3)",
                              position: "relative",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: {
                                  xs: "1.2rem",
                                  sm: "1.4rem",
                                  md: "1.6rem",
                                },
                                fontWeight: "bold",
                                lineHeight: 1,
                              }}
                            >
                              {topPerformers[1].gamesWon}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: {
                                  xs: "0.6rem",
                                  sm: "0.7rem",
                                  md: "0.75rem",
                                },
                                lineHeight: 1,
                                opacity: 0.9,
                              }}
                            >
                              wins
                            </Typography>
                            <Box
                              sx={{
                                position: "absolute",
                                top: -8,
                                right: -8,
                                width: { xs: 20, md: 24 },
                                height: { xs: 20, md: 24 },
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, #C0C0C0 0%, #90A4AE 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: { xs: "0.7rem", md: "0.8rem" },
                                fontWeight: "bold",
                              }}
                            >
                              2
                            </Box>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.8rem",
                                md: "0.875rem",
                              },
                              maxWidth: { xs: 80, md: 100 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {topPerformers[1].name}
                          </Typography>
                        </Box>
                      )}

                      {/* 1st Place */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 80, sm: 90, md: 100 },
                            height: { xs: 80, sm: 90, md: 100 },
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #FFD700 0%, #FFA726 100%)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            boxShadow: isPadelEagle ? "none" : "0 6px 20px rgba(255,215,0,0.4)",
                            position: "relative",
                          }}
                        >
                          <TrophyIcon
                            sx={{ fontSize: { xs: 20, md: 24 }, mb: 0.5 }}
                          />
                          <Typography
                            variant="h5"
                            sx={{
                              fontSize: {
                                xs: "1.4rem",
                                sm: "1.6rem",
                                md: "1.8rem",
                              },
                              fontWeight: "bold",
                              lineHeight: 1,
                            }}
                          >
                            {topPerformers[0].gamesWon}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: {
                                xs: "0.6rem",
                                sm: "0.7rem",
                                md: "0.75rem",
                              },
                              lineHeight: 1,
                              opacity: 0.9,
                            }}
                          >
                            wins
                          </Typography>
                          <Box
                            sx={{
                              position: "absolute",
                              top: -10,
                              right: -10,
                              width: { xs: 24, md: 28 },
                              height: { xs: 24, md: 28 },
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #FFD700 0%, #FFA726 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "0.8rem", md: "0.9rem" },
                              fontWeight: "bold",
                              border: "2px solid white",
                            }}
                          >
                            1
                          </Box>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            mt: 1,
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: {
                              xs: "0.8rem",
                              sm: "0.9rem",
                              md: "1rem",
                            },
                            maxWidth: { xs: 100, md: 120 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {topPerformers[0].name}
                        </Typography>
                      </Box>

                      {/* 3rd Place */}
                      {topPerformers[2] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transform: "translateY(20px)",
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 50, sm: 60, md: 70 },
                              height: { xs: 50, sm: 60, md: 70 },
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #CD7F32 0%, #8D6E63 100%)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              boxShadow: isPadelEagle ? "none" : "0 3px 10px rgba(0,0,0,0.2)",
                              position: "relative",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: {
                                  xs: "1rem",
                                  sm: "1.2rem",
                                  md: "1.4rem",
                                },
                                fontWeight: "bold",
                                lineHeight: 1,
                              }}
                            >
                              {topPerformers[2].gamesWon}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: {
                                  xs: "0.5rem",
                                  sm: "0.6rem",
                                  md: "0.7rem",
                                },
                                lineHeight: 1,
                                opacity: 0.9,
                              }}
                            >
                              wins
                            </Typography>
                            <Box
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                width: { xs: 18, md: 20 },
                                height: { xs: 18, md: 20 },
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, #CD7F32 0%, #8D6E63 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: { xs: "0.6rem", md: "0.7rem" },
                                fontWeight: "bold",
                              }}
                            >
                              3
                            </Box>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: {
                                xs: "0.7rem",
                                sm: "0.75rem",
                                md: "0.8rem",
                              },
                              maxWidth: { xs: 70, md: 90 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {topPerformers[2].name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              <Divider />

              {/* Matches Results */}
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: { xs: 1.5, md: 2 },
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: { xs: "1rem", md: "1.25rem" },
                  }}
                >
                  <ScoreIcon sx={{ fontSize: { xs: 20, md: 24 } }} /> Match
                  Results
                </Typography>

                {leagueDayMatches.length > 0 ? (
                  (() => {
                    const handleRoundToggle = (roundName: string) => {
                      const newExpanded = new Set(expandedRounds);
                      if (newExpanded.has(roundName)) {
                        newExpanded.delete(roundName);
                      } else {
                        newExpanded.add(roundName);
                      }
                      setExpandedRounds(newExpanded);
                    };

                    const matchesByRound = leagueDayMatches.reduce(
                      (acc: any, match: any) => {
                        const roundKey = `Round ${match.roundNumber}`;
                        if (!acc[roundKey]) {
                          acc[roundKey] = [];
                        }
                        acc[roundKey].push(match);
                        return acc;
                      },
                      {},
                    );

                    return (
                      <Box
                        sx={{
                          maxHeight: { xs: 300, md: 400 },
                          overflow: "auto",
                        }}
                      >
                        {Object.entries(matchesByRound).map(
                          ([roundName, matches]: [string, any]) => (
                            <Accordion
                              key={roundName}
                              expanded={expandedRounds.has(roundName)}
                              onChange={() => handleRoundToggle(roundName)}
                              sx={{
                                mb: 1,
                                borderRadius: { xs: 1, md: 2 },
                                "&:before": { display: "none" },
                                boxShadow: 1,
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  backgroundColor: "action.hover",
                                  borderRadius: { xs: 1, md: 2 },
                                  "&.Mui-expanded": {
                                    borderBottomLeftRadius: 0,
                                    borderBottomRightRadius: 0,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: { xs: "0.9rem", md: "1rem" },
                                    }}
                                  >
                                    {roundName}
                                  </Typography>
                                  <Chip
                                    label={`${matches.length} matches`}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      fontSize: { xs: "0.7rem", md: "0.75rem" },
                                    }}
                                  />
                                  <Chip
                                    label={`${
                                      matches.filter((m: any) => m.isCompleted)
                                        .length
                                    } completed`}
                                    size="small"
                                    color="success"
                                    sx={{
                                      fontSize: { xs: "0.7rem", md: "0.75rem" },
                                    }}
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0 }}>
                                {/* Mobile: stacked match result cards */}
                                <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1, p: 1 }}>
                                  {matches.map((match: any) => (
                                    <Box
                                      key={match.id}
                                      sx={{
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: match.isCompleted ? "success.light" : "divider",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box sx={{ px: 1.25, py: 0.6, bgcolor: "action.selected", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 600 }}>
                                          Court {match.courtNumber}
                                        </Typography>
                                        {match.isCompleted ? (
                                          <Chip label={`${match.score.pair1Games} – ${match.score.pair2Games}`} size="small" color={match.score.winnerPair === 1 ? "success" : "error"} sx={{ fontSize: "0.7rem", fontWeight: 700, height: 20 }} />
                                        ) : (
                                          <Chip label="Pending" size="small" color="default" sx={{ fontSize: "0.7rem", height: 20 }} />
                                        )}
                                      </Box>
                                      <Box sx={{ p: 1 }}>
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: match.isCompleted && match.score.winnerPair === 1 ? 700 : 400, color: match.isCompleted && match.score.winnerPair === 1 ? "success.dark" : "text.primary" }}>
                                          {match.pair1.player1Name} & {match.pair1.player2Name}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: "text.disabled", textAlign: "center", my: 0.25 }}>vs</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: match.isCompleted && match.score.winnerPair === 2 ? 700 : 400, color: match.isCompleted && match.score.winnerPair === 2 ? "success.dark" : "text.primary" }}>
                                          {match.pair2.player1Name} & {match.pair2.player2Name}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                                {/* Desktop: full table */}
                                <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
                                  <Table
                                    size="medium"
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            fontSize: {
                                              xs: "0.75rem",
                                              md: "0.875rem",
                                            },
                                          }}
                                        >
                                          Team 1
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: {
                                              xs: "0.75rem",
                                              md: "0.875rem",
                                            },
                                          }}
                                        >
                                          Score
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontSize: {
                                              xs: "0.75rem",
                                              md: "0.875rem",
                                            },
                                          }}
                                        >
                                          Team 2
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontSize: {
                                              xs: "0.75rem",
                                              md: "0.875rem",
                                            },
                                          }}
                                        >
                                          Court
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: {
                                              xs: "0.75rem",
                                              md: "0.875rem",
                                            },
                                          }}
                                        >
                                          Status
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {matches.map(
                                        (match: any, index: number) => (
                                          <TableRow key={match.id}>
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: {
                                                    xs: "0.75rem",
                                                    md: "0.875rem",
                                                  },
                                                }}
                                              >
                                                {match.pair1.player1Name} &{" "}
                                                {match.pair1.player2Name}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                              {match.isCompleted ? (
                                                <Chip
                                                  label={`${match.score.pair1Games} - ${match.score.pair2Games}`}
                                                  color={
                                                    match.score.winnerPair === 1
                                                      ? "success"
                                                      : "error"
                                                  }
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.7rem",
                                                      md: "0.75rem",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                <Chip
                                                  label="0 - 0"
                                                  color="default"
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.7rem",
                                                      md: "0.75rem",
                                                    },
                                                  }}
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: {
                                                    xs: "0.75rem",
                                                    md: "0.875rem",
                                                  },
                                                }}
                                              >
                                                {match.pair2.player1Name} &{" "}
                                                {match.pair2.player2Name}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: {
                                                    xs: "0.75rem",
                                                    md: "0.875rem",
                                                  },
                                                }}
                                              >
                                                Court {match.courtNumber}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                              {match.isCompleted ? (
                                                <Chip
                                                  label="Completed"
                                                  color="success"
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.7rem",
                                                      md: "0.75rem",
                                                    },
                                                  }}
                                                />
                                              ) : (
                                                <Chip
                                                  label="Pending"
                                                  color="default"
                                                  size="small"
                                                  sx={{
                                                    fontSize: {
                                                      xs: "0.7rem",
                                                      md: "0.75rem",
                                                    },
                                                  }}
                                                />
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ),
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          ),
                        )}
                      </Box>
                    );
                  })()
                ) : (
                  <Typography
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    No match data available for this league day.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "action.hover" }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scorekeeper Registration Dialog */}
      <ScorekeeperRegistrationDialog
        open={scorekeeperDialogOpen}
        onClose={() => setScorekeeperDialogOpen(false)}
      />

      {/* Match Scoring Dialog */}
      <Dialog
        open={scoringDialogOpen}
        onClose={() => setScoringDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            background: (isPadelEagle ? "#743444" : "linear-gradient(45deg, #4caf50 30%, #81c784 90%)"),
            color: "white",
            fontWeight: "bold",
          }}
        >
          {scoringMatch?.isCompleted ? "Edit Match Score" : "Enter Match Score"}
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {scoringMatch && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ textAlign: "center", mb: 3 }}
              >
                Court {scoringMatch.courtNumber} - Round{" "}
                {scoringMatch.roundNumber}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Team 1
                    </Typography>
                    <Typography variant="body2">
                      {scoringMatch.pair1?.player1Name ||
                        `Player ${scoringMatch.pair1?.player1Id}`}
                    </Typography>
                    <Typography variant="body2">
                      {scoringMatch.pair1?.player2Name ||
                        `Player ${scoringMatch.pair1?.player2Id}`}
                    </Typography>
                    <TextField
                      type="number"
                      label="Games Won"
                      value={scoringMatch.score?.pair1Games || 0}
                      onChange={(e) => {
                        if (scoringMatch) {
                          setScoringMatch({
                            ...scoringMatch,
                            score: {
                              ...scoringMatch.score,
                              pair1Games: parseInt(e.target.value) || 0,
                            },
                          });
                        }
                      }}
                      inputProps={{ min: 0, max: 10 }}
                      sx={{ mt: 2, width: "100%" }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    sx={{ p: 2, textAlign: "center", background: "#fff3e0" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Team 2
                    </Typography>
                    <Typography variant="body2">
                      {scoringMatch.pair2?.player1Name ||
                        `Player ${scoringMatch.pair2?.player1Id}`}
                    </Typography>
                    <Typography variant="body2">
                      {scoringMatch.pair2?.player2Name ||
                        `Player ${scoringMatch.pair2?.player2Id}`}
                    </Typography>
                    <TextField
                      type="number"
                      label="Games Won"
                      value={scoringMatch.score?.pair2Games || 0}
                      onChange={(e) => {
                        if (scoringMatch) {
                          setScoringMatch({
                            ...scoringMatch,
                            score: {
                              ...scoringMatch.score,
                              pair2Games: parseInt(e.target.value) || 0,
                            },
                          });
                        }
                      }}
                      inputProps={{ min: 0, max: 10 }}
                      sx={{ mt: 2, width: "100%" }}
                    />
                  </Paper>
                </Grid>
              </Grid>

              {/* Show edit history for completed matches */}
              {scoringMatch.isCompleted &&
                (scoringMatch.scoredBy || scoringMatch.scoredAt) && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      background: "rgba(76, 175, 80, 0.1)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      <strong>Previously edited by:</strong>{" "}
                      {scoringMatch.scoredBy || "Unknown"}
                      {scoringMatch.scoredAt && (
                        <span>
                          {" "}
                          on{" "}
                          {new Date(scoringMatch.scoredAt).toLocaleString(
                            "en-GB",
                          )}
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setScoringDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (scoringMatch) {
                console.log("Scoring match:", scoringMatch);
                console.log("Match ID:", scoringMatch.id);
                handleUpdateMatchScore(scoringMatch.id, {
                  pair1Games: scoringMatch.score?.pair1Games || 0,
                  pair2Games: scoringMatch.score?.pair2Games || 0,
                });
              }
            }}
            variant="contained"
            disabled={!scoringMatch?.score}
            sx={{
              background: (isPadelEagle ? "#743444" : "linear-gradient(45deg, #4caf50 30%, #81c784 90%)"),
              "&:hover": {
                background: "linear-gradient(45deg, #388e3c 30%, #66bb6a 90%)",
              },
            }}
          >
            {scoringMatch?.isCompleted ? "Update Score" : "Save Score"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset League Day Scores Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !resetLoading && setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            background: "linear-gradient(45deg, #f44336 30%, #e57373 90%)",
            color: "white",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <WarningIcon />
          Reset All Match Scores
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {leagueDayToReset && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ textAlign: "center", mb: 2 }}
              >
                Are you sure?
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: "warning.light",
                  color: "warning.contrastText",
                }}
              >
                <Typography variant="body1" gutterBottom>
                  <strong>League Day:</strong> {leagueDayToReset.name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Division:</strong> {currentDivision}
                </Typography>
                <Typography variant="body1">
                  <strong>Matches to Reset:</strong>{" "}
                  {leagueDayToReset.matchCount}
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 2,
                  bgcolor: "error.light",
                  border: "1px solid",
                  borderColor: "error.main",
                  color: "error.contrastText",
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>⚠️ This action will:</strong>
                </Typography>
                <Box component="ul" sx={{ margin: 0, pl: 2 }}>
                  <li>
                    <Typography variant="body2">
                      Reset ALL match scores in this league day to 0-0
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Mark all matches as "Pending"
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Reset the league day to Round 1
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Remove all scorekeeper attribution
                    </Typography>
                  </li>
                </Box>
              </Paper>

              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  fontStyle: "italic",
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                This ONLY affects <strong>{leagueDayToReset.name}</strong> in{" "}
                <strong>{currentDivision}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            variant="outlined"
            disabled={resetLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmResetLeagueDayScores}
            variant="contained"
            color="error"
            disabled={resetLoading}
            sx={{
              background: "linear-gradient(45deg, #f44336 30%, #e57373 90%)",
              "&:hover": {
                background: "linear-gradient(45deg, #d32f2f 30%, #f44336 90%)",
              },
            }}
          >
            {resetLoading ? "Resetting..." : "Yes, Reset All Scores"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Profile Modal */}
      <Dialog
        open={playerProfileOpen}
        onClose={handleClosePlayerProfile}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            maxHeight: { xs: "100vh", sm: "90vh" },
            margin: { xs: 0, sm: "32px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: isPadelEagle
              ? "#743444"
              : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"),
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <StarIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
              Player Profile
            </Typography>
          </Box>
          <Button
            onClick={handleClosePlayerProfile}
            sx={{ color: "white", minWidth: "auto", p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {playerModalLoading ? (
            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
              <Typography>Loading player profile...</Typography>
            </Box>
          ) : playerHistory && selectedPlayerId ? (
            <Box>
              {/* Player Header */}
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor: "action.hover",
                  borderBottom: "2px solid",
                  borderBottomColor: isPadelEagle ? "#743444" : "primary.main",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  {(() => {
                    const sp = rankings.find((p) => p.playerId === selectedPlayerId);
                    const canPreview = !!sp?.profilePicture && !!sp?.privacyPolicyAccepted;
                    return (
                      <Box
                        onClick={() => canPreview && setAvatarPreviewSrc(sp!.profilePicture!)}
                        sx={{
                          cursor: canPreview ? "zoom-in" : "default",
                          borderRadius: "50%",
                          flexShrink: 0,
                          ...(canPreview && {
                            "&:hover": {
                              outline: "3px solid rgba(255,255,255,0.6)",
                              outlineOffset: 2,
                            },
                          }),
                        }}
                      >
                        <SafeAvatar
                          src={sp?.profilePicture}
                          alt={sp?.playerName || "Player"}
                          fallbackText={sp?.playerName || "Player"}
                          sx={{
                            width: { xs: 60, md: 80 },
                            height: { xs: 60, md: 80 },
                            filter: sp?.privacyPolicyAccepted ? "none" : "blur(20px)",
                            pointerEvents: "none",
                          }}
                        />
                      </Box>
                    );
                  })()}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: isPadelEagle ? "#743444" : PE_BLUE,
                      }}
                    >
                      {
                        rankings.find((p) => p.playerId === selectedPlayerId)
                          ?.playerName
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentDivision}
                    </Typography>
                    <Chip
                      label={`Rank #${
                        rankings.findIndex(
                          (p) => p.playerId === selectedPlayerId,
                        ) + 1
                      }`}
                      color="primary"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>

                {/* Season Selector */}
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel
                      sx={{
                        color: isPadelEagle ? "#743444" : PE_BLUE,
                        fontWeight: 500,
                      }}
                    >
                      Season
                    </InputLabel>
                    <Select
                      value={playerProfileSeasonId || selectedSeason?.id || ""}
                      onChange={(e) =>
                        handlePlayerProfileSeasonChange(e.target.value)
                      }
                      label="Season"
                      sx={{
                        bgcolor: isPadelEagle
                          ? "rgba(116,52,68, 0.05)"
                          : "rgba(25, 118, 210, 0.05)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                          borderWidth: 2,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                          borderWidth: 2,
                        },
                        "& .MuiSelect-select": {
                          fontWeight: 500,
                          color: isPadelEagle ? "#743444" : PE_BLUE,
                        },
                      }}
                    >
                      {allSeasons
                        .filter((s) => s.status !== "draft")
                        .map((season) => (
                          <MenuItem key={season.id} value={season.id}>
                            {season.name} {season.isActive ? "(Active)" : ""}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Badges Section */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: isPadelEagle ? "#E49421" : PE_BLUE,
                    }}
                  >
                    🏆 Achievements
                  </Typography>
                  {(() => {
                    const allBadges = rankings.find((p) => p.playerId === selectedPlayerId)?.badges || [];
                    const currentSeasonId = playerProfileSeasonId || selectedSeason?.id;
                    const MILESTONE_TYPES = ["all_season_warrior", "division_champion", "sp_team"];
                    const REPEATABLE_TYPES = new Set(["perfect_day", "clean_sweep"]);
                    const filteredBadges = currentSeasonId
                      ? allBadges.filter((b: any) => b.seasonId === currentSeasonId || MILESTONE_TYPES.includes(b.type))
                      : allBadges;
                    const badgesWithPoints = filteredBadges.filter((b: any) => (b.pointsAwarded ?? 0) > 0);
                    const totalBadgePoints = badgesWithPoints.reduce((sum: number, b: any) => sum + (b.pointsAwarded ?? 0), 0);

                    const BADGE_NAMES: Record<string, { name: string; emoji: string }> = {
                      first_win: { name: "First Victory", emoji: "🎉" },
                      giant_killer: { name: "Giant Killer", emoji: "⚔️" },
                      win_streak_10: { name: "Unstoppable", emoji: "🔥" },
                      division_champion: { name: "Division Champion", emoji: "👑" },
                      close_call: { name: "Close Call", emoji: "💪" },
                      perfect_day: { name: "Perfect Day", emoji: "⭐" },
                      veteran: { name: "Veteran", emoji: "🎖️" },
                      partner_carry: { name: "Partner Carry", emoji: "🤝" },
                      clean_sweep: { name: "Clean Sweep", emoji: "🧹" },
                      mr_ms_consistent: { name: "Mr/Ms Consistent", emoji: "📊" },
                      century: { name: "Century", emoji: "💯" },
                      iron_man: { name: "Iron Man", emoji: "🦾" },
                      all_season_warrior: { name: "All-Season Warrior", emoji: "🛡️" },
                      division_hopper: { name: "Division Hopper", emoji: "🦘" },
                      unlucky: { name: "Unlucky", emoji: "😤" },
                      sp_team: { name: "SP Team", emoji: "⚡" },
                    };

                    const cfg = badgePointsConfig || {};
                    const BADGE_DETAILS: Record<string, { how: string; points: number }> = {
                      first_win:        { how: "Win your first match of the season.",                               points: cfg.first_win        ?? 3 },
                      giant_killer:     { how: "Beat a player ranked 5+ positions above you.",                     points: cfg.giant_killer     ?? 10 },
                      win_streak_10:    { how: "Win 10 matches in a row without losing a single one.",             points: cfg.win_streak_10    ?? 20 },
                      close_call:       { how: "Win a match by a narrow margin (4–3 or 5–4).",                     points: cfg.close_call       ?? 5 },
                      perfect_day:      { how: "Win all 4 matches in a single league day.",                        points: cfg.perfect_day      ?? 15 },
                      veteran:          { how: "Play in 50 or more league days.",                                  points: cfg.veteran          ?? 10 },
                      partner_carry:    { how: "Win a match with a partner ranked 10+ positions below you.",       points: cfg.partner_carry    ?? 5 },
                      clean_sweep:      { how: "Win a match without dropping a single game (4–0 or 5–0).",         points: cfg.clean_sweep      ?? 8 },
                      mr_ms_consistent: { how: "Win exactly 2 games in each of 5 consecutive matches.",           points: cfg.mr_ms_consistent ?? 5 },
                      century:          { how: "Win 100+ games in a season with a positive game difference.",      points: cfg.century          ?? 20 },
                      iron_man:         { how: "Play in 10 consecutive league days.",                              points: cfg.iron_man         ?? 10 },
                      division_hopper:  { how: "Play in 3 or more different divisions.",                           points: cfg.division_hopper  ?? 5 },
                      unlucky:          { how: "Lose 5 matches by a close margin (3–4 or 4–5). Tough luck!",      points: cfg.unlucky          ?? 3 },
                    };

                    // Group repeated badges (perfect_day, clean_sweep) so they show as one row
                    const groupedBadges: { type: string; count: number; totalPts: number; instances: any[] }[] = [];
                    const seenTypes = new Map<string, number>();
                    for (const b of badgesWithPoints) {
                      if (seenTypes.has(b.type)) {
                        const idx = seenTypes.get(b.type)!;
                        groupedBadges[idx].count++;
                        groupedBadges[idx].totalPts += b.pointsAwarded ?? 0;
                        groupedBadges[idx].instances.push(b);
                      } else {
                        seenTypes.set(b.type, groupedBadges.length);
                        groupedBadges.push({ type: b.type, count: 1, totalPts: b.pointsAwarded ?? 0, instances: [b] });
                      }
                    }

                    // Determine which seasonal badges the player has NOT yet earned this season
                    // Repeatable badges (perfect_day, clean_sweep) are always shown as available
                    const SEASONAL_TYPES = ["first_win", "giant_killer", "win_streak_10", "close_call", "perfect_day",
                      "veteran", "partner_carry", "clean_sweep", "mr_ms_consistent", "century",
                      "iron_man", "division_hopper", "unlucky"];
                    const earnedSeasonalTypes = new Set(
                      filteredBadges.filter((b: any) => SEASONAL_TYPES.includes(b.type)).map((b: any) => b.type)
                    );
                    const availableBadges = SEASONAL_TYPES.filter(t =>
                      !earnedSeasonalTypes.has(t) || REPEATABLE_TYPES.has(t)
                    );

                    return (
                      <>
                        <BadgeShowcase
                          earnedBadges={filteredBadges}
                          size="medium"
                          showOnlyName={true}
                        />
                        {badgesWithPoints.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 2,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: isPadelEagle ? "#E49421" : "warning.main",
                              bgcolor: isPadelEagle ? "rgba(228,148,33,0.06)" : "rgba(255,152,0,0.06)",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  🎯 Badge Bonus Points
                                </Typography>
                                <Chip
                                  label={`+${totalBadgePoints} pts total`}
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                            {groupedBadges.map((g, i) => {
                              const info = BADGE_NAMES[g.type] || { name: g.type, emoji: "🏅" };
                              const firstInstance = g.instances[0];
                              const meta = firstInstance?.metadata || {};
                              const contextParts: string[] = [];
                              if (meta.partnerName) contextParts.push(`with ${meta.partnerName}`);
                              if (meta.opponentName) contextParts.push(`vs ${meta.opponentName}`);
                              if (meta.defeatedPlayerName) contextParts.push(`beat ${meta.defeatedPlayerName}`);

                              // For repeatable badges show each instance's date + division; for single badges show one line
                              const allInstances = g.instances.map((inst: any) => ({
                                date: inst?.earnedAt
                                  ? new Date(inst.earnedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                                  : null,
                                division: inst?.division || null,
                              }));

                              return (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    py: 0.75,
                                    borderTop: i > 0 ? "1px solid" : "none",
                                    borderColor: "divider",
                                    gap: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2">
                                      {info.emoji} {info.name}{g.count > 1 ? ` ×${g.count}` : ""}
                                    </Typography>
                                    {g.count === 1 && allInstances[0]?.date && (
                                      <Typography variant="caption" color="text.secondary">
                                        {[allInstances[0].date, allInstances[0].division, ...contextParts].filter(Boolean).join(" · ")}
                                      </Typography>
                                    )}
                                    {g.count > 1 && (
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                        {allInstances.map((inst, di) => (
                                          <Typography key={di} variant="caption" color="text.secondary">
                                            {di + 1}. {[inst.date, inst.division].filter(Boolean).join(" · ")}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "bold", color: "warning.main", whiteSpace: "nowrap" }}
                                  >
                                    +{g.totalPts} pts
                                  </Typography>
                                </Box>
                              );
                            })}
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* Badges still available to earn this season */}
                        {availableBadges.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 1.5,
                              borderRadius: 1,
                              border: "1px dashed",
                              borderColor: "divider",
                              bgcolor: "action.hover",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}>
                              🎯 Still Available This Season
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {availableBadges.map(t => {
                                const info = BADGE_NAMES[t] || { name: t, emoji: "🏅" };
                                const details = BADGE_DETAILS[t];
                                return (
                                  <Tooltip
                                    key={t}
                                    arrow
                                    title={details ? (
                                      <Box sx={{ p: 0.5 }}>
                                        <Typography variant="caption" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>
                                          How to earn:
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
                                          {details.how}
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: "#ffd54f" }}>
                                          +{details.points} bonus points
                                        </Typography>
                                      </Box>
                                    ) : ""}
                                  >
                                    <Chip
                                      label={`${info.emoji} ${info.name}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ opacity: 0.7, fontSize: "0.7rem", cursor: "help" }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>

              {/* Hybrid Score Breakdown */}
              {!isTournamentOrg && (() => {
                const player = rankings.find(p => p.playerId === selectedPlayerId);
                if (!player || player.gamesPlayed === 0 || player.hybridScore == null) return null;

                const activePlayers = rankings.filter(p => p.gamesPlayed > 0);
                const sortedPoints = activePlayers.map(p => p.totalPoints).sort((a: number, b: number) => a - b);
                const maxPoints = sortedPoints[sortedPoints.length - 1] || 0;
                const maxWins = activePlayers.reduce((m: number, p: any) => Math.max(m, p.gamesWon), 0);
                const minPoints = maxWins >= 5 ? Math.max(sortedPoints[0] || 0, 5) : 0;
                const range = maxPoints - minPoints;
                const volumeScore = range > 0 ? Math.max(0, ((player.totalPoints - minPoints) / range) * 100) : 50;
                const qualityScore = player.bayesianWinRate || 0;

                return (
                  <Box sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
                    <Paper sx={{ p: 2, backgroundColor: "rgba(25, 118, 210, 0.03)", border: "2px solid rgba(25, 118, 210, 0.2)", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: PE_BLUE }}>
                        ⭐ Hybrid Score Breakdown
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: "rgba(25, 118, 210, 0.08)", borderRadius: 1, textAlign: "center" }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Accumulated Points
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: "bold", color: PE_BLUE }}>
                            {volumeScore.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.6rem", mt: 0.5, fontFamily: "monospace", opacity: 0.7 }}>
                            (your pts − div min) ÷ (div max − div min) × 100
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem", fontFamily: "monospace" }}>
                            ({player.totalPoints} − {minPoints}) ÷ ({maxPoints} − {minPoints}) × 100
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.6rem", mt: 1, fontStyle: "italic", textAlign: "left" }}>
                            Scales your total wins relative to the division — so the gap between players actually reflects the difference in effort and games played, not just a raw count.
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: "rgba(0, 150, 136, 0.08)", borderRadius: 1, textAlign: "center" }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Bayesian Win Rate
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#00796b" }}>
                            {qualityScore.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem", mt: 0.5 }}>
                            Raw WR: {(player.winPercentage ?? 0).toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.6rem", mt: 1, fontStyle: "italic", textAlign: "left" }}>
                            Prevents a player who played once on a lucky or unlucky day from unfairly shooting up or down the rankings. The more you play, the closer this gets to your raw WR.
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: "rgba(25, 118, 210, 0.08)", borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: "monospace", mb: 0.5 }}>
                          (AccumulatedPoints × 0.4) + (BayesianWinRate × 0.6) = Score
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          ({volumeScore.toFixed(1)} × 0.4) + ({qualityScore.toFixed(1)} × 0.6) ={" "}
                          <Box component="span" sx={{ fontWeight: 700, color: PE_BLUE }}>
                            {player.hybridScore.toFixed(1)}
                          </Box>
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                );
              })()}

              {/* Current Season Stats */}
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: isPadelEagle ? "#743444" : PE_BLUE,
                  }}
                >
                  📊 Current Season Stats
                </Typography>
                <Grid container spacing={2}>
                  {isTournamentOrg ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(116,52,68, 0.1)"
                              : "info.light",
                            border: isPadelEagle ? "2px solid #743444" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#743444" : PE_BLUE,
                            }}
                          >
                            {rankings.find(
                              (p) => p.playerId === selectedPlayerId,
                            )?.totalTournamentPoints || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tournament Points
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(228,148,33, 0.1)"
                              : "success.light",
                            border: isPadelEagle ? "2px solid #E49421" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#E49421" : "#4caf50",
                            }}
                          >
                            {rankings.find(
                              (p) => p.playerId === selectedPlayerId,
                            )?.tournamentsWon || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tournaments Won
                          </Typography>
                        </Paper>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={6} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(116,52,68, 0.1)"
                              : "info.light",
                            border: isPadelEagle ? "2px solid #743444" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#743444" : PE_BLUE,
                            }}
                          >
                            {playerHistory?.divisionStats?.[currentDivision]?.totalPoints || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Points in {currentDivision}
                          </Typography>
                          {playerHistory?.penalties && playerHistory.penalties.filter(p => p.division === currentDivision && p.points !== 0).length > 0 && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                              (Includes penalties/bonuses - see below)
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(228,148,33, 0.1)"
                              : "success.light",
                            border: isPadelEagle ? "2px solid #E49421" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#E49421" : "#4caf50",
                            }}
                          >
                            {playerHistory?.divisionStats?.[currentDivision]
                              ?.gamesWon || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Wins
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(116,52,68, 0.15)"
                              : "error.light",
                            border: isPadelEagle ? "2px solid #743444" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#743444" : "#f44336",
                            }}
                          >
                            {playerHistory?.divisionStats?.[currentDivision]
                              ?.gamesLost || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Losses
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: isPadelEagle
                              ? "rgba(116,52,68, 0.05)"
                              : "warning.light",
                            border: isPadelEagle ? "2px solid #743444" : "none",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: "bold",
                              color: isPadelEagle ? "#743444" : "#ff9800",
                            }}
                          >
                            {playerHistory?.divisionStats?.[currentDivision]
                              ?.gamesPlayed || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Played
                          </Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Penalties/Bonuses Card */}
                {playerHistory?.penalties && playerHistory.penalties.filter(p => p.division === currentDivision && p.points !== 0).length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: isPadelEagle
                          ? "rgba(116,52,68, 0.05)"
                          : "rgba(255, 152, 0, 0.1)",
                        border: isPadelEagle 
                          ? "2px solid #E49421" 
                          : "2px solid #ff9800",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          mb: 2,
                          color: isPadelEagle ? "#E49421" : "#ff9800",
                        }}
                      >
                        ⚖️ Penalties & Bonuses
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The total points shown above already include these adjustments. This breakdown is for transparency.
                      </Typography>
                      {playerHistory.penalties
                        .filter(p => p.division === currentDivision && p.points !== 0)
                        .map((penalty) => (
                          <Box
                            key={penalty.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              mb: 1,
                              backgroundColor: penalty.type === 'bonus' 
                                ? "rgba(76, 175, 80, 0.1)" 
                                : "rgba(244, 67, 54, 0.1)",
                              borderRadius: 1,
                              border: penalty.type === 'bonus'
                                ? "1px solid rgba(76, 175, 80, 0.3)"
                                : "1px solid rgba(244, 67, 54, 0.3)",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  color: penalty.type === 'bonus' ? "#4caf50" : "#f44336",
                                }}
                              >
                                {penalty.type === 'bonus' ? '✅ Bonus' : '⚠️ Penalty'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {penalty.reason || 'No reason provided'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(penalty.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                color: penalty.type === 'bonus' ? "#4caf50" : "#f44336",
                                ml: 2,
                              }}
                            >
                              {penalty.type === 'bonus' ? '+' : '-'}{penalty.points}
                            </Typography>
                          </Box>
                        ))}
                    </Paper>
                  </Box>
                )}

                {/* Other Divisions Stats - Only for league organizations */}
                {!isTournamentOrg &&
                  playerHistory?.divisionStats &&
                  Object.keys(playerHistory.divisionStats).filter(
                    (div) => div !== currentDivision && div !== 'unassigned',
                  ).length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          mb: 2,
                          color: isPadelEagle ? "#E49421" : "#9c27b0",
                        }}
                      >
                        🏆 Points in Other Divisions
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(playerHistory.divisionStats)
                          .filter(([division]) => division !== currentDivision && division !== 'unassigned')
                          .sort(([divA], [divB]) => divA.localeCompare(divB))
                          .map(([division, stats]) => (
                            <Grid item xs={6} sm={4} key={division}>
                              <Paper
                                sx={{
                                  p: 2,
                                  textAlign: "center",
                                  background: isPadelEagle
                                    ? "#FFF5F0"
                                    : "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: "bold",
                                    color: isPadelEagle ? "#743444" : "#9c27b0",
                                  }}
                                >
                                  {stats.totalPoints}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {division === 'unassigned' ? 'Trial Day' : division}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {division === 'unassigned'
                                    ? `${stats.totalPoints} pts`
                                    : `${stats.gamesPlayed} games`}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                      </Grid>
                    </Box>
                  )}

                {/* Trial Day Stats — always computed from match history for accuracy */}
                {!isTournamentOrg &&
                  playerHistory?.matches?.some((m: any) => m.isTrial) && (() => {
                    const trialPoints = playerHistory.matches
                      .filter((m: any) => m.isTrial)
                      .reduce((sum: number, m: any) => {
                        const inPair1 =
                          m.pair1?.player1Id === selectedPlayerId ||
                          m.pair1?.player2Id === selectedPlayerId;
                        return sum + (inPair1 ? (m.pair1Games ?? 0) : (m.pair2Games ?? 0));
                      }, 0);
                    return (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            color: isPadelEagle ? '#E49421' : '#9c27b0',
                          }}
                        >
                          🏆 Points in Other Divisions
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={4}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: 'center',
                                background: isPadelEagle
                                  ? '#FFF5F0'
                                  : 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                              }}
                            >
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 'bold',
                                  color: isPadelEagle ? '#743444' : '#9c27b0',
                                }}
                              >
                                {trialPoints}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Trial Day
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {trialPoints} pts
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })()}
              </Box>

              <Divider />

              {/* Best Partnership */}
              {(() => {
                const partnerships = calculatePartnerships();
                if (partnerships.length === 0) return null;

                // Find the maximum wins
                const maxWins = partnerships[0]?.gamesWon || 0;
                // Get all partners with the same max wins
                const bestPartners = partnerships.filter(
                  (p) => p.gamesWon === maxWins,
                );

                return (
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        color: isPadelEagle ? "#E49421" : "#4caf50",
                      }}
                    >
                      🤝 Best Partnership{bestPartners.length > 1 ? "s" : ""}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {bestPartners.map((partner) => (
                        <Paper
                          key={partner.partnerId}
                          sx={{
                            p: 2,
                            backgroundColor: "success.light",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "#2e7d32" }}
                          >
                            {partner.partnerName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {partner.gamesWon} wins in {partner.gamesPlayed}{" "}
                            games ({partner.winRate.toFixed(1)}% win rate)
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Toughest Opponent */}
              {(() => {
                // Don't show if player has no matches at all
                if (playerHistory && playerHistory.matches.length === 0) {
                  return null;
                }

                const opponents = calculateOpponents();
                if (opponents.length === 0) {
                  return (
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          mb: 2,
                          color: isPadelEagle ? "#743444" : "#f44336",
                        }}
                      >
                        💪 Toughest Opponent
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "success.light",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            color: "#2e7d32",
                            textAlign: "center",
                          }}
                        >
                          🏆 Unbeaten *YET* 🏆
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center" }}
                        >
                          No losses recorded this season!
                        </Typography>
                      </Paper>
                    </Box>
                  );
                }

                // Find the maximum losses
                const maxLosses = opponents[0]?.gamesLost || 0;
                // Get all opponents with the same max losses
                const toughestOpponents = opponents.filter(
                  (o) => o.gamesLost === maxLosses,
                );

                return (
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        color: isPadelEagle ? "#743444" : "#f44336",
                      }}
                    >
                      💪 Toughest Opponent
                      {toughestOpponents.length > 1 ? "s" : ""}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {toughestOpponents.map((opponent) => (
                        <Paper
                          key={opponent.opponentId}
                          sx={{
                            p: 2,
                            backgroundColor: "error.light",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "#c62828" }}
                          >
                            {opponent.opponentName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {opponent.gamesLost} losses in{" "}
                            {opponent.gamesPlayed} games (
                            {opponent.lossRate.toFixed(1)}% loss rate)
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              <Divider />

              {/* Match History - All Matches */}
              {playerHistory.matches.length > 0 &&
                (() => {
                  // Create a map of leagueDayId to date for easy lookup
                  const leagueDayDateMap = new Map<string, Date>();

                  // Try allDivisionLeagueDays first
                  playerHistory.allDivisionLeagueDays?.forEach((day) => {
                    if (day.id && day.date) {
                      leagueDayDateMap.set(day.id, new Date(day.date));
                    }
                  });

                  // Also add from leagueDayStats as fallback
                  playerHistory.leagueDayStats?.forEach((stat) => {
                    if (
                      stat.leagueDayId &&
                      stat.date &&
                      !leagueDayDateMap.has(stat.leagueDayId)
                    ) {
                      leagueDayDateMap.set(
                        stat.leagueDayId,
                        new Date(stat.date),
                      );
                    }
                  });

                  // Sort matches by date (newest first), then by round number descending
                  const sortedMatches = [...playerHistory.matches].sort(
                    (a, b) => {
                      // Get the actual league day dates for proper sorting
                      const dateA = leagueDayDateMap.get(a.leagueDayId);
                      const dateB = leagueDayDateMap.get(b.leagueDayId);

                      // If both have valid dates, compare them
                      if (dateA && dateB) {
                        const dateDiff = dateB.getTime() - dateA.getTime();

                        // If dates are equal, sort by round number descending (4, 3, 2, 1)
                        if (dateDiff === 0) {
                          return (b.roundNumber || 0) - (a.roundNumber || 0);
                        }

                        return dateDiff;
                      }

                      // Fallback to scoredAt/createdAt if league day date not available
                      const fallbackDateA =
                        a.scoredAt || a.createdAt || new Date(0);
                      const fallbackDateB =
                        b.scoredAt || b.createdAt || new Date(0);
                      const dateDiff =
                        new Date(fallbackDateB).getTime() -
                        new Date(fallbackDateA).getTime();

                      if (dateDiff === 0) {
                        return (b.roundNumber || 0) - (a.roundNumber || 0);
                      }

                      return dateDiff;
                    },
                  );

                  // Group matches by league day date
                  const matchesByDate = new Map<string, typeof sortedMatches>();
                  sortedMatches.forEach((match) => {
                    const leagueDayDate = leagueDayDateMap.get(
                      match.leagueDayId,
                    );
                    const dateKey =
                      leagueDayDate && !isNaN(leagueDayDate.getTime())
                        ? leagueDayDate.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "Date unknown";

                    if (!matchesByDate.has(dateKey)) {
                      matchesByDate.set(dateKey, []);
                    }
                    matchesByDate.get(dateKey)!.push(match);
                  });

                  return (
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          color: isPadelEagle ? "#E49421" : "#ff9800",
                          mb: 2,
                        }}
                      >
                        📈 Match History ({sortedMatches.length} matches)
                      </Typography>

                      {Array.from(matchesByDate.entries()).map(
                        ([dateKey, matches], index) => {
                          // Calculate wins for this date
                          const winsOnDate = matches.filter((match) => {
                            const isPair1 =
                              match.pair1.player1Id === selectedPlayerId ||
                              match.pair1.player2Id === selectedPlayerId;
                            return isPair1
                              ? match.score.winnerPair === 1
                              : match.score.winnerPair === 2;
                          }).length;

                          return (
                            <Accordion
                              key={dateKey}
                              defaultExpanded={index === 0}
                              sx={{ mb: 1 }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    📅 {dateKey}
                                  </Typography>
                                  <Chip
                                    label={`${matches.length} ${matches.length > 1 ? "matches" : "match"}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`${winsOnDate}W - ${matches.length - winsOnDate}L`}
                                    size="small"
                                    color={
                                      winsOnDate > matches.length - winsOnDate
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {matches.map((match) => {
                                    const isPair1 =
                                      match.pair1.player1Id ===
                                        selectedPlayerId ||
                                      match.pair1.player2Id ===
                                        selectedPlayerId;
                                    const partnerName = isPair1
                                      ? match.pair1.player1Id ===
                                        selectedPlayerId
                                        ? match.pair1Player2Name
                                        : match.pair1Player1Name
                                      : match.pair2.player1Id ===
                                          selectedPlayerId
                                        ? match.pair2Player2Name
                                        : match.pair2Player1Name;

                                    const opponent1Name = isPair1
                                      ? match.pair2Player1Name
                                      : match.pair1Player1Name;
                                    const opponent2Name = isPair1
                                      ? match.pair2Player2Name
                                      : match.pair1Player2Name;

                                    const didWin = isPair1
                                      ? match.score.winnerPair === 1
                                      : match.score.winnerPair === 2;

                                    const score = `${match.score.pair1Games}-${match.score.pair2Games}`;

                                    return (
                                      <Paper
                                        key={match.id}
                                        sx={{
                                          p: 2,
                                          backgroundColor: didWin
                                            ? "success.light"
                                            : "error.light",
                                          border: didWin
                                            ? "1px solid"
                                            : "1px solid",
                                          borderColor: didWin
                                            ? "success.main"
                                            : "error.main",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                          }}
                                        >
                                          <Chip
                                            label={didWin ? "Won" : "Lost"}
                                            color={didWin ? "success" : "error"}
                                            size="small"
                                            sx={{ fontWeight: "bold" }}
                                          />
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Round {match.roundNumber}, Court{" "}
                                            {match.courtNumber}
                                          </Typography>
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "bold", mb: 0.5 }}
                                        >
                                          With: {partnerName}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mb: 0.5 }}
                                        >
                                          vs {opponent1Name} & {opponent2Name}
                                        </Typography>
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: didWin
                                              ? "#2e7d32"
                                              : "#c62828",
                                          }}
                                        >
                                          {isPair1
                                            ? score
                                            : `${match.score.pair2Games}-${match.score.pair1Games}`}
                                        </Typography>
                                      </Paper>
                                    );
                                  })}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          );
                        },
                      )}
                    </Box>
                  );
                })()}

              <Divider />

              {/* Points Progress Chart */}
              {playerHistory.leagueDayStats.length > 0 && (
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: PE_BLUE }}
                    >
                      📊 Points Progress
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: PE_BLUE,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          Played
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: "#f44336",
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          Absent
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ position: "relative", height: 300, pt: 2 }}>
                    {/* Y-axis labels */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 60,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        pr: 1,
                      }}
                    >
                      {(() => {
                        const maxPoints = Math.max(
                          ...playerHistory.leagueDayStats.map((s) => s.points),
                          1,
                        );
                        const steps = 5;
                        const stepValue = Math.ceil(maxPoints / steps);
                        return Array.from({ length: steps + 1 }, (_, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {stepValue * (steps - i)}
                          </Typography>
                        ));
                      })()}
                    </Box>

                    {/* Chart area - Line Graph */}
                    <Box
                      sx={{
                        ml: 4,
                        height: "calc(100% - 60px)",
                        borderLeft: "2px solid #e0e0e0",
                        borderBottom: "2px solid #e0e0e0",
                        position: "relative",
                        px: 1,
                      }}
                    >
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 1000 200"
                        preserveAspectRatio="none"
                        style={{ overflow: "visible" }}
                      >
                        {(() => {
                          const sortedStats = [
                            ...playerHistory.leagueDayStats,
                          ].sort((a, b) => a.dayNumber - b.dayNumber);

                          const maxPoints = Math.max(
                            ...playerHistory.leagueDayStats
                              .filter((s) => s.participated !== false)
                              .map((s) => s.points),
                            1,
                          );

                          const chartWidth = 1000;
                          const chartHeight = 200;
                          const padding = 20;
                          const pointSpacing =
                            (chartWidth - 2 * padding) /
                            (sortedStats.length - 1 || 1);

                          // Generate points for the line
                          const points = sortedStats.map((stat, index) => {
                            const x =
                              sortedStats.length === 1
                                ? chartWidth / 2
                                : padding + index * pointSpacing;
                            const y =
                              chartHeight -
                              padding -
                              (stat.points / maxPoints) *
                                (chartHeight - 2 * padding);
                            return { x, y, stat };
                          });

                          // Create continuous path data connecting all points
                          const pathData = points
                            .map(
                              (point, index) =>
                                `${index === 0 ? "M" : "L"} ${point.x} ${
                                  point.y
                                }`,
                            )
                            .join(" ");

                          return (
                            <>
                              {/* Continuous line connecting all points */}
                              <path
                                d={pathData}
                                fill="none"
                                stroke={PE_BLUE}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                vectorEffect="non-scaling-stroke"
                              />

                              {/* Points */}
                              {points.map((point, index) => {
                                const isAbsent =
                                  point.stat.participated === false;
                                const color = isAbsent ? "#f44336" : PE_BLUE;

                                return (
                                  <g key={sortedStats[index].leagueDayId}>
                                    {/* Dot */}
                                    <circle
                                      cx={point.x}
                                      cy={point.y}
                                      r="4"
                                      fill={color}
                                      stroke="white"
                                      strokeWidth="2"
                                      vectorEffect="non-scaling-stroke"
                                      style={{ cursor: "pointer" }}
                                    />
                                    {/* Points label */}
                                    <text
                                      x={point.x}
                                      y={point.y - 15}
                                      textAnchor="middle"
                                      fontSize="12"
                                      fontWeight="bold"
                                      fill={color}
                                      transform={`translate(${point.x}, ${point.y - 15}) scale(1.5, 1) translate(${-point.x}, ${-(point.y - 15)})`}
                                    >
                                      {sortedStats[index].points}
                                    </text>
                                  </g>
                                );
                              })}
                            </>
                          );
                        })()}
                      </svg>
                    </Box>

                    {/* X-axis labels */}
                    <Box
                      sx={{
                        ml: 4,
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                        px: 1,
                      }}
                    >
                      {[...playerHistory.leagueDayStats]
                        .sort((a, b) => a.dayNumber - b.dayNumber)
                        .map((stat) => {
                          const date = new Date(stat.date);
                          const formattedDate = date.toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "2-digit",
                            },
                          );

                          return (
                            <Box
                              key={stat.leagueDayId}
                              sx={{
                                textAlign: "center",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.65rem",
                                  display: "block",
                                  fontWeight: "bold",
                                }}
                              >
                                Day {stat.dayNumber}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.6rem",
                                  display: "block",
                                }}
                              >
                                {formattedDate}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                </Box>
              )}

              <Divider />

              {/* League Day Participation */}
              {playerHistory.allDivisionLeagueDays &&
                playerHistory.allDivisionLeagueDays.length > 0 && (
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", mb: 2, color: PE_BLUE }}
                    >
                      📅 League Day Participation
                    </Typography>
                    <Grid container spacing={1}>
                      {playerHistory.allDivisionLeagueDays.map((day: any) => {
                        const date = new Date(day.date);
                        const formattedDate = date.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        });

                        {
                          // Determine status:
                          // - Completed & participated = Played
                          // - Completed & not participated = Absent
                          // - Not completed & on waitlist = On Waitlist
                          // - Not completed & participated = Registered
                          // - Not completed & registration not open = Registration Not Open
                          // - Not completed & registration open & not participated = Not Registered
                          const isIncomplete = !day.isCompleted || day.isDraft;
                          
                          let status: string;
                          if (!isIncomplete) {
                            // Completed league day
                            status = day.participated ? "played" : "absent";
                          } else {
                            // Incomplete league day
                            if (day.isOnWaitlist) {
                              status = "waitlist";
                            } else if (day.participated) {
                              status = "registered";
                            } else if (!day.registrationOpen) {
                              status = "registrationNotOpen";
                            } else {
                              status = "notRegistered";
                            }
                          }

                          return (
                            <Grid item xs={6} sm={4} md={3} key={day.id}>
                              <Paper
                                sx={{
                                  p: 1.5,
                                  textAlign: "center",
                                  backgroundColor:
                                    status === "played"
                                      ? "success.light"
                                      : status === "registered"
                                        ? "warning.light"
                                        : status === "waitlist"
                                          ? "#e1bee7"
                                          : status === "registrationNotOpen"
                                            ? "grey.100"
                                            : status === "notRegistered"
                                              ? "action.disabledBackground"
                                              : "error.light",
                                  border: "2px solid",
                                  borderColor:
                                    status === "played"
                                      ? "success.main"
                                      : status === "registered"
                                        ? "warning.main"
                                        : status === "waitlist"
                                          ? "#9c27b0"
                                          : status === "registrationNotOpen"
                                            ? "grey.400"
                                            : status === "notRegistered"
                                              ? "action.disabled"
                                              : "error.main",
                                  borderRadius: 2,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: "bold",
                                    color:
                                      status === "played"
                                        ? "#2e7d32"
                                        : status === "registered"
                                          ? "#e65100"
                                          : status === "waitlist"
                                            ? "#7b1fa2"
                                            : status === "registrationNotOpen"
                                              ? "#757575"
                                              : status === "notRegistered"
                                                ? "#616161"
                                                : "#c62828",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Day {day.dayNumber}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "text.secondary",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  {formattedDate}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: "bold",
                                    color:
                                      status === "played"
                                        ? "#4caf50"
                                        : status === "registered"
                                          ? "#ffa726"
                                          : status === "waitlist"
                                            ? "#ab47bc"
                                            : status === "registrationNotOpen"
                                              ? "#9e9e9e"
                                              : status === "notRegistered"
                                                ? "#9e9e9e"
                                                : "#f44336",
                                  }}
                                >
                                  {status === "played"
                                    ? "✓ Played"
                                    : status === "registered"
                                      ? "📝 Registered"
                                      : status === "waitlist"
                                        ? "⏳ On Waitlist"
                                        : status === "registrationNotOpen"
                                          ? "🔒 Registration Not Open"
                                          : status === "notRegistered"
                                            ? "○ Not Registered"
                                            : "✗ Absent"}
                                </Typography>
                              </Paper>
                            </Grid>
                          );
                        }
                      })}
                    </Grid>
                  </Box>
                )}
            </Box>
          ) : (
            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
              <Typography color="text.secondary">
                {playerHistoryError || "No data available"}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClosePlayerProfile} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar lightbox */}
      <Dialog
        open={!!avatarPreviewSrc}
        onClose={() => setAvatarPreviewSrc(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.88)" } },
        }}
        onClick={() => setAvatarPreviewSrc(null)}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={avatarPreviewSrc ?? ""}
            alt="Profile picture"
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: 3,
              boxShadow: isPadelEagle ? "none" : "0 24px 64px rgba(0,0,0,0.7)",
              display: "block",
              objectFit: "contain",
            }}
          />
          <IconButton
            onClick={() => setAvatarPreviewSrc(null)}
            sx={{
              position: "absolute",
              top: -16,
              right: -16,
              bgcolor: "rgba(255,255,255,0.15)",
              color: "white",
              backdropFilter: "blur(8px)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>

      {/* Registration Links - Footer */}
      {!isScorekeeper && !isAdmin && (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
            mt: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 3,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "0.75rem",
                "&:hover": {
                  color: "primary.main",
                },
              }}
              onClick={() => (window.location.href = "/player/register")}
            >
              Player Registration
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "0.75rem",
                "&:hover": {
                  color: "primary.main",
                },
              }}
              onClick={() => setScorekeeperDialogOpen(true)}
            >
              Scorekeeper Registration
            </Typography>
          </Box>
        </Box>
      )}

      {/* Head-to-Head Dialog */}
      {selectedPlayer1 && selectedPlayer2 && (
        <HeadToHeadDialog
          open={h2hOpen}
          onClose={handleH2hClose}
          player1Id={selectedPlayer1.id}
          player2Id={selectedPlayer2.id}
          player1Name={selectedPlayer1.name}
          player2Name={selectedPlayer2.name}
          seasonId={selectedSeason?.id}
        />
      )}
    </Container>
    </MuiThemeProvider>
  );
}

export default PublicHome;
